import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { WO_TYPE_COLORS, priorityColors, inputStyle, ROLE_LABELS, ROLE_COLORS } from '../utils/constants';
import { FiCheckSquare, FiSearch, FiRefreshCw, FiCheckCircle, FiAlertTriangle, FiClock, FiBarChart2, FiChevronDown, FiBell, FiCheck, FiX, FiMaximize2, FiArrowRight, FiArrowLeft, FiInfo, FiUsers, FiTrendingUp, FiDollarSign, FiCalendar, FiPlus, FiList, FiUserX, FiPackage, FiFileText, FiDownload, FiLock, FiEdit2, FiPrinter, FiGrid } from 'react-icons/fi';
import SignatureModal from '../components/SignatureModal';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine, PieChart, Pie, Cell } from 'recharts';
import { playSuccess, playClick, playDelete } from '../utils/sounds';
import TVDisplay from './TVDisplay';
import HelpTip from '../components/HelpTip';
import CostReport from '../components/CostReport';

const TAB_CATEGORIES = [
  { label: 'Workflow Approvals', tabs: [
    { key: 'notifications', label: 'Workflow Approvals', icon: FiBell },
  ]},
  { label: 'Work Order Management', tabs: [
    { key: 'overdue', label: 'Overdue Operations', icon: FiAlertTriangle },
    { key: 'unassigned', label: 'Unassigned Jobs', icon: FiUserX },
    { key: 'teco', label: 'Jobs Ready for TECO', icon: FiCheckSquare },
    { key: 'awaiting-parts', label: 'Awaiting Parts/Service', icon: FiPackage },
  ]},
  { label: 'Planning', tabs: [
    { key: 'pre-read', label: 'Weekly Pre-Read', icon: FiFileText },
    { key: 'capacity', label: 'Team Capacity', icon: FiBarChart2 },
    { key: 'myteam', label: 'My Team', icon: FiUsers },
  ]},
  { label: 'Insights', tabs: [
    { key: 'metrics', label: 'Metrics', icon: FiTrendingUp },
    { key: 'cost-report', label: 'Cost Report', icon: FiBarChart2 },
    { key: 'team-reports', label: 'Team Reports', icon: FiDollarSign },
    { key: 'variance', label: 'Labour Variance', icon: FiClock },
  ]},
];
const TABS = TAB_CATEGORIES.flatMap(c => c.tabs);

function titleCase(str) { return str.replace(/\b\w/g, c => c.toUpperCase()); }

// Operations a supervisor schedules when approving a notification. A PM02
// built from a multi-op task list runs in sequence: approval only creates
// the first op, and each later op arrives as its own notification once the
// previous one is TECO'd — so only the first op is shown/required here.
function getApprovalOps(n) {
  let ops = null;
  try { ops = n?.planned_operations ? (typeof n.planned_operations === 'string' ? JSON.parse(n.planned_operations) : n.planned_operations) : null; } catch (e) { /* ignore */ }
  if (!Array.isArray(ops) || ops.length === 0) return null;
  if (n.type === 'PM02' && n.task_list_template_id && ops.length > 1) return ops.slice(0, 1);
  return ops;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : parts[0][0].toUpperCase();
}

// Custom user dropdown component matching template style
function UserDropdown({ value, onChange, users, placeholder = 'Select team member...' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);
  const selected = users.find(u => String(u.id) === String(value));

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = users.filter(u => !search || u.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(!open)}
        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxSizing: 'border-box', minHeight: 40 }}>
        {selected ? (
          <>
            {selected.avatar ? (
              <img src={`/api/users/avatar/${selected.avatar}`} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: selected.avatar_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {getInitials(selected.name)}
              </div>
            )}
            <span style={{ fontWeight: 600 }}>{selected.name}</span>
            {selected.team_name && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>({selected.team_name})</span>}
          </>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>{placeholder}</span>
        )}
        <FiChevronDown size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 260, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." autoFocus
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {value && (
              <div onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                Clear selection
              </div>
            )}
            {filtered.length === 0 && <div style={{ padding: '12px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No users found</div>}
            {filtered.map(u => (
              <div key={u.id} onClick={() => { onChange(String(u.id)); setOpen(false); setSearch(''); }}
                style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.1s', background: String(u.id) === String(value) ? 'rgba(37,99,235,0.08)' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = String(u.id) === String(value) ? 'rgba(37,99,235,0.08)' : 'transparent'}>
                {u.avatar ? (
                  <img src={`/api/users/avatar/${u.avatar}`} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: u.avatar_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                    {getInitials(u.name)}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</div>
                  {u.team_name && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{u.team_name}</div>}
                </div>
                {String(u.id) === String(value) && <FiCheck size={14} color="#2563eb" />}
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

const fmtH = v => { const s = Math.round(parseFloat(v) * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };

export default function SupervisorPanel() {
  const { authFetch, user } = useAuth();
  const { isDark } = useTheme();
  const axisTickColor = isDark ? '#94a3b8' : '#64748b';
  const chartTooltipStyle = { background: isDark ? '#13213a' : '#ffffff', border: `1px solid ${isDark ? '#1f3050' : '#e2e8f0'}`, borderRadius: 8, fontSize: 12, color: isDark ? '#e8edf5' : '#1e293b', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'notifications';
  const initialUser = searchParams.get('user');
  const [tab, setTab] = useState(initialTab);
  const [showTV, setShowTV] = useState(false);
  const [tecoJobs, setTecoJobs] = useState([]);
  const [awaitingPartsJobs, setAwaitingPartsJobs] = useState([]);
  const [overdueJobs, setOverdueJobs] = useState([]);
  const [unconfirmedJobs, setUnconfirmedJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [capacityData, setCapacityData] = useState([]);
  const [capacityRange, setCapacityRange] = useState('week');
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedNotif, setExpandedNotif] = useState(null);
  const [pendingOps, setPendingOps] = useState([]);
  const [pendingOpsLoading, setPendingOpsLoading] = useState(false);
  const [expandedOp, setExpandedOp] = useState(null);
  const [opApprovalForm, setOpApprovalForm] = useState({ assigned_to: '', planned_date: '', est_hours: 0, est_minutes: 0 });
  const [teamUsers, setTeamUsers] = useState([]);
  const [opCapacity, setOpCapacity] = useState([]);
  const [dailyCapacity, setDailyCapacity] = useState([]);
  const [dailyCapLoading, setDailyCapLoading] = useState(false);
  const [tecoFilter, setTecoFilter] = useState('');
  const [tecoTypeFilter, setTecoTypeFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [approveSuccess, setApproveSuccess] = useState(null);
  const [tecoSuccess, setTecoSuccess] = useState(null);
  const [tecoSignatureTarget, setTecoSignatureTarget] = useState(null); // { issueId, woNumber, supervisorName }
  const [notifAssignedTo, setNotifAssignedTo] = useState('');
  const [notifPlannedDate, setNotifPlannedDate] = useState('');
  const [notifEstHours, setNotifEstHours] = useState(0);
  const [notifEstMinutes, setNotifEstMinutes] = useState(0);
  const [notifPush, setNotifPush] = useState(false);
  const [notifActiveOp, setNotifActiveOp] = useState(0); // index of currently editing operation
  const [notifOpSchedules, setNotifOpSchedules] = useState({}); // { idx: { date, hours, minutes } }
  const [userDailyCapacity, setUserDailyCapacity] = useState([]);
  const [userDailyCapLoading, setUserDailyCapLoading] = useState(false);
  const [infTarget, setInfTarget] = useState(null); // { type: 'operation'|'notification', id, issue_id?, title }
  const [infMessage, setInfMessage] = useState('');
  const [infSending, setInfSending] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // { type: 'operation'|'notification', id, issue_id?, title }
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSending, setRejectSending] = useState(false);
  const [capRange, setCapRange] = useState(4); // weeks to show in capacity calendar
  const [capStartFrom, setCapStartFrom] = useState(null); // optional start date for calendar centering
  // My Team tab state
  const [teamViewUser, setTeamViewUser] = useState(null);
  const [teamUserCapacity, setTeamUserCapacity] = useState([]);
  const [teamUserCapLoading, setTeamUserCapLoading] = useState(false);
  const [teamCapRange, setTeamCapRange] = useState(4);
  const [teamViewMode, setTeamViewMode] = useState('calendar'); // 'calendar' or 'chart'
  const [teamCapScrollDate, setTeamCapScrollDate] = useState(null); // date string to scroll to in calendar
  const [teamAllCapacity, setTeamAllCapacity] = useState([]); // all users capacity for overview
  const [teamAllCapLoading, setTeamAllCapLoading] = useState(false);
  // Block management state
  const [teamBlocks, setTeamBlocks] = useState([]);
  const [teamBlocksLoading, setTeamBlocksLoading] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  // Reschedule overdue operation state
  const [rescheduleOp, setRescheduleOp] = useState(null); // the overdue operation being rescheduled
  const [reschedAssignedTo, setReschedAssignedTo] = useState('');
  const [reschedDate, setReschedDate] = useState('');
  const [reschedEstHours, setReschedEstHours] = useState(0);
  const [reschedEstMinutes, setReschedEstMinutes] = useState(0);
  const [reschedReason, setReschedReason] = useState('');
  const [reschedComment, setReschedComment] = useState('');
  const [reschedPush, setReschedPush] = useState(false);
  const [reschedIsAssign, setReschedIsAssign] = useState(false); // true when used for initial assignment (no prior assignee)
  const [reschedCapacity, setReschedCapacity] = useState([]);
  const [reschedCapRange, setReschedCapRange] = useState(4);
  const RESCHEDULE_REASONS = ['Access Restricted','Equipment In Use','Higher Priority Work','Parts Not Available','Resource Unavailable','Safety Concern','Vendor Delay','Waiting For Approval','Weather Conditions','Other'];
  const [blockForm, setBlockForm] = useState({ user_id: '', name: '', start_date: '', end_date: '' });
  const [blockSaving, setBlockSaving] = useState(false);
  const [blockConflicts, setBlockConflicts] = useState({}); // keyed by block id
  const [expandedBlock, setExpandedBlock] = useState(null);
  // Metrics tab state
  const [metrics, setMetrics] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsView, setMetricsView] = useState('weekly');
  const [costMetrics, setCostMetrics] = useState(null);
  // Default to current month for Insights cost metrics
  const [costPeriod, setCostPeriod] = useState('month');

  // Helpers: compute current month start/end and previous week (Mon–Sun) in local time
  const fmtYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const currentMonthRange = () => {
    const n = new Date();
    const start = new Date(n.getFullYear(), n.getMonth(), 1);
    const end = new Date(n.getFullYear(), n.getMonth() + 1, 0);
    return { from: fmtYMD(start), to: fmtYMD(end) };
  };
  const previousWeekRange = () => {
    const n = new Date();
    const dow = n.getDay() || 7; // 1..7 (Mon..Sun)
    const lastSun = new Date(n); lastSun.setDate(n.getDate() - dow); lastSun.setHours(0, 0, 0, 0);
    const lastMon = new Date(lastSun); lastMon.setDate(lastSun.getDate() - 6);
    return { from: fmtYMD(lastMon), to: fmtYMD(lastSun) };
  };
  const [costLoading, setCostLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  // Reschedule tab state
  const [reschedules, setReschedules] = useState([]);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleForm, setRescheduleForm] = useState({});
  // Info-required tab state
  const [infoJobs, setInfoJobs] = useState([]);
  const [infoJobsLoading, setInfoJobsLoading] = useState(false);
  // Unassigned Jobs tab state
  const [unassignedJobs, setUnassignedJobs] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedSearch, setUnassignedSearch] = useState('');
  const [assignTarget, setAssignTarget] = useState(null);
  const [opForms, setOpForms] = useState({}); // { [opId]: { assigned_to, planned_date, est_hours, est_minutes } }
  const [activeOpId, setActiveOpId] = useState(null); // which op's capacity calendar is showing
  const [assignCapacity, setAssignCapacity] = useState([]);
  const [assignCapRange, setAssignCapRange] = useState(4);
  const [assignCapStartFrom, setAssignCapStartFrom] = useState(null);
  const [assignSaving, setAssignSaving] = useState(false);
  // Pre-read state
  const [preReadData, setPreReadData] = useState(null); // { preRead, operations, teamMembers, frozen }
  const [preReadLoading, setPreReadLoading] = useState(false);
  const [preReadNotes, setPreReadNotes] = useState('');
  const [preReadHistory, setPreReadHistory] = useState([]);
  const [preReadShowHistory, setPreReadShowHistory] = useState(false);
  const [preReadFilter, setPreReadFilter] = useState(''); // user id filter
  const [preReadDateEdit, setPreReadDateEdit] = useState(null); // { opId, currentDate, newDate }
  const [preReadSubmitting, setPreReadSubmitting] = useState(false);
  const [preReadSubmitConfirm, setPreReadSubmitConfirm] = useState(false);
  // Pending drag/date moves held in memory until Save as Draft is clicked.
  // Shape: { [opId]: 'YYYY-MM-DD' }. Empty when there are no unsaved changes.
  const [preReadPendingMoves, setPreReadPendingMoves] = useState({});
  // Confirmation modal for a drag drop: { opId, op, newDate, userName, overBy }
  const [preReadDragConfirm, setPreReadDragConfirm] = useState(null);
  const [preReadDraftSaving, setPreReadDraftSaving] = useState(false);
  // Labour variance state
  const [varianceData, setVarianceData] = useState(null);
  const [varianceLoading, setVarianceLoading] = useState(false);
  // Labour Variance defaults to the current month
  const [varianceFrom, setVarianceFrom] = useState(() => currentMonthRange().from);
  const [varianceTo, setVarianceTo] = useState(() => currentMonthRange().to);
  const [varianceSort, setVarianceSort] = useState('variance_pct_desc');

  // Team reports state
  const [teamReportData, setTeamReportData] = useState(null);
  const [teamReportLoading, setTeamReportLoading] = useState(false);
  const [teamReportType, setTeamReportType] = useState('summary');
  // Team Reports defaults to the previous completed week (Mon–Sun); custom dates remain available
  const [teamReportFrom, setTeamReportFrom] = useState(() => previousWeekRange().from);
  const [teamReportTo, setTeamReportTo] = useState(() => previousWeekRange().to);
  const [preReadBringForward, setPreReadBringForward] = useState(null); // { date, userId, userName } or null
  const [preReadAvailableJobs, setPreReadAvailableJobs] = useState([]);
  const [preReadAvailLoading, setPreReadAvailLoading] = useState(false);
  const [preReadTypeFilter, setPreReadTypeFilter] = useState('');
  const [preReadShowFrozenDetail, setPreReadShowFrozenDetail] = useState(false);
  const [preReadSplit, setPreReadSplit] = useState(null); // { parentId, issueId, woNumber, woName, totalHours, assignedTo, slices: [{ date, hours, minutes }] }
  const [preReadSplitSaving, setPreReadSplitSaving] = useState(false);
  const [preReadViewMode, setPreReadViewMode] = useState(() => {
    try { return localStorage.getItem('preReadViewMode') || 'graph'; } catch { return 'graph'; }
  });
  const [preReadTilePopover, setPreReadTilePopover] = useState(null); // { opId, anchor: DOMRect }
  const [preReadAvailSearch, setPreReadAvailSearch] = useState('');
  const [preReadAvailSort, setPreReadAvailSort] = useState('due_asc'); // due_asc, due_desc, wo_asc, wo_desc, hours_desc
  useEffect(() => { try { localStorage.setItem('preReadViewMode', preReadViewMode); } catch {} }, [preReadViewMode]);
  // Capacity day drill-down
  const [capDayModal, setCapDayModal] = useState(null); // { date, day, jobs, allocated, available, userId, userName }
  const [capDayReassigning, setCapDayReassigning] = useState(null); // job being reassigned { wo, opId, name }
  const [capDayNewDate, setCapDayNewDate] = useState('');
  const [capDaySaving, setCapDaySaving] = useState(false);
  const [capDayReason, setCapDayReason] = useState('');
  const [capDayComment, setCapDayComment] = useState('');
  const [capDayAssignedTo, setCapDayAssignedTo] = useState('');
  const [capDayCalData, setCapDayCalData] = useState([]);
  const [capDayHistory, setCapDayHistory] = useState(null); // { issueId, records }
  // Reassign operation state
  const [reassignOp, setReassignOp] = useState(null); // { id, issue_id, wo_number, operation_number, title }
  const [reassignUserId, setReassignUserId] = useState('');
  const [reassignSaving, setReassignSaving] = useState(false);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const loadCapDayCal = async (userId) => {
    try {
      const res = await authFetch(`/api/issues/user-capacity-daily?user_id=${userId}&weeks=52`);
      if (res.ok) setCapDayCalData(await res.json());
    } catch (e) {}
  };

  // Auto-capitalize first letter of each sentence
  const autoCapitalize = (text) => {
    return text.replace(/(^|[.!?]\s+)([a-z])/g, (match, prefix, letter) => prefix + letter.toUpperCase());
  };

  const handleInfSend = async () => {
    if (!infMessage.trim() || !infTarget) return;
    setInfSending(true);
    try {
      const msg = autoCapitalize(infMessage.trim());
      let res;
      if (infTarget.type === 'operation') {
        res = await authFetch(`/api/issues/${infTarget.issue_id}/operations/${infTarget.id}/info-required`, {
          method: 'PUT', body: JSON.stringify({ message: msg })
        });
      } else {
        res = await authFetch(`/api/notifs/${infTarget.id}/info-required`, {
          method: 'POST', body: JSON.stringify({ message: msg })
        });
      }
      if (res.ok) {
        showToast('Info request sent');
        setInfTarget(null);
        setInfMessage('');
        setExpandedOp(null);
        setExpandedNotif(null);
        loadNotifications();
        loadPendingOps();
      } else {
        showToast('Failed to send info request', 'error');
      }
    } catch { showToast('Failed to send', 'error'); }
    setInfSending(false);
  };

  // Calculate current and last week boundaries (local time)
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() + mondayOffset);
  thisMonday.setHours(0, 0, 0, 0);
  const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const thisMondayStr = toLocalDateStr(thisMonday);
  const thisSunday = new Date(thisMonday); thisSunday.setDate(thisMonday.getDate() + 6);
  const thisSundayStr = toLocalDateStr(thisSunday);
  const lastSunday = new Date(thisMonday); lastSunday.setDate(thisMonday.getDate() - 1);
  const lastSundayStr = lastSunday.toISOString().split('T')[0];

  const loadTeco = () => {
    authFetch('/api/issues/teco/pending').then(r => r.json()).then(data => { setTecoJobs(Array.isArray(data) ? data : []); setLoading(false); }).catch(() => setLoading(false));
  };

  const loadAwaitingParts = () => {
    authFetch('/api/issues/teco/awaiting-parts').then(r => r.json()).then(data => { setAwaitingPartsJobs(Array.isArray(data) ? data : []); }).catch(() => {});
  };

  const loadOverdue = () => {
    const todayLocal = toLocalDateStr(new Date());
    authFetch(`/api/issues/team-overdue?today=${todayLocal}`).then(r => r.json()).then(data => { setOverdueJobs(Array.isArray(data) ? data : []); setLoading(false); }).catch(() => setLoading(false));
  };

  const loadReschedules = () => {
    setRescheduleLoading(true);
    authFetch('/api/issues/reschedules/pending').then(r => r.json()).then(data => { setReschedules(Array.isArray(data) ? data : []); setRescheduleLoading(false); }).catch(() => setRescheduleLoading(false));
  };

  // Discard any in-memory moves whenever the active pre-read week changes.
  const preReadWeekKey = preReadData?.preRead?.week_start || null;
  useEffect(() => { setPreReadPendingMoves({}); }, [preReadWeekKey]);

  const loadPreRead = (weekStart, opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) {
      setPreReadLoading(true);
      setPreReadShowFrozenDetail(false);
    }
    const qs = weekStart ? `?week_start=${weekStart}` : '';
    authFetch(`/api/issues/pre-read${qs}`).then(r => {
      if (!r.ok) throw new Error('Failed');
      return r.json();
    }).then(data => {
      setPreReadData(data);
      // Only reset user-edited notes on a non-silent (view-switching) load
      if (!silent) setPreReadNotes(data.preRead?.notes || '');
      if (!silent) setPreReadLoading(false);
    }).catch(() => {
      if (!silent) {
        setPreReadData({ preRead: null, operations: [], teamMembers: [], frozen: false });
        setPreReadLoading(false);
      }
    });
  };

  const loadPreReadHistory = () => {
    authFetch('/api/issues/pre-read/history').then(r => r.ok ? r.json() : []).then(data => {
      setPreReadHistory(Array.isArray(data) ? data : []);
    }).catch(() => setPreReadHistory([]));
  };

  const loadVariance = (from, to) => {
    setVarianceLoading(true);
    const params = new URLSearchParams();
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    authFetch(`/api/issues/team-variance?${params.toString()}`).then(r => r.ok ? r.json() : null).then(data => {
      setVarianceData(data); setVarianceLoading(false);
    }).catch(() => setVarianceLoading(false));
  };

  const loadTeamReports = (type, from, to) => {
    setTeamReportLoading(true);
    const params = new URLSearchParams();
    if (type) params.set('report_type', type);
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    authFetch(`/api/issues/team-reports?${params.toString()}`).then(r => r.ok ? r.json() : null).then(data => {
      setTeamReportData(data); setTeamReportLoading(false);
    }).catch(() => setTeamReportLoading(false));
  };

  const loadInfoJobs = () => {
    setInfoJobsLoading(true);
    authFetch('/api/issues/pending-operations?status=info_required').then(r => r.json()).then(data => { setInfoJobs(Array.isArray(data) ? data : []); setInfoJobsLoading(false); }).catch(() => setInfoJobsLoading(false));
  };

  const loadUnconfirmed = () => {
    authFetch(`/api/issues/team-unconfirmed?today=${toLocalDateStr(new Date())}`).then(r => r.json()).then(data => { setUnconfirmedJobs(Array.isArray(data) ? data : []); setLoading(false); }).catch(() => setLoading(false));
  };


  const loadNotifications = (silent) => {
    if (!silent) setNotifLoading(true);
    authFetch('/api/notifs')
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setNotifications(prev => JSON.stringify(prev) === JSON.stringify(arr) ? prev : arr);
        setNotifLoading(false);
      })
      .catch(() => { if (!silent) { setNotifications([]); } setNotifLoading(false); });
  };

  const loadPendingOps = (silent) => {
    if (!silent) setPendingOpsLoading(true);
    authFetch('/api/issues/pending-operations')
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setPendingOps(prev => JSON.stringify(prev) === JSON.stringify(arr) ? prev : arr);
        setPendingOpsLoading(false);
      })
      .catch(() => { if (!silent) { setPendingOps([]); } setPendingOpsLoading(false); });
  };

  const loadTeamUsers = () => {
    authFetch('/api/users/my-team')
      .then(r => r.json())
      .then(data => {
        const active = Array.isArray(data) ? data.filter(u => !u.disabled) : [];
        // Filter out users with 0 working hours on all weekdays
        const withHours = active.filter(u => {
          const mon = parseFloat(u.working_hours_mon ?? 8);
          const tue = parseFloat(u.working_hours_tue ?? 8);
          const wed = parseFloat(u.working_hours_wed ?? 8);
          const thu = parseFloat(u.working_hours_thu ?? 8);
          const fri = parseFloat(u.working_hours_fri ?? 8);
          const sat = parseFloat(u.working_hours_sat ?? 0);
          const sun = parseFloat(u.working_hours_sun ?? 0);
          return (mon + tue + wed + thu + fri + sat + sun) > 0;
        });
        setTeamUsers(withHours);
      })
      .catch(() => setTeamUsers([]));
  };

  const loadTeamBlocks = () => {
    setTeamBlocksLoading(true);
    authFetch('/api/blocks?scope=team')
      .then(r => r.ok ? r.json() : [])
      .then(data => { setTeamBlocks(Array.isArray(data) ? data : []); setTeamBlocksLoading(false); })
      .catch(() => { setTeamBlocks([]); setTeamBlocksLoading(false); });
  };

  const loadBlockConflicts = async (block) => {
    try {
      const startStr = (block.start_date || '').slice(0, 10);
      const endStr = (block.end_date || '').slice(0, 10);
      const r = await authFetch(`/api/blocks/conflicts?user_id=${block.user_id}&start_date=${startStr}&end_date=${endStr}`);
      const jobs = r.ok ? await r.json() : [];
      setBlockConflicts(prev => ({ ...prev, [block.id]: jobs }));
    } catch {}
  };

  const loadUserDailyCapacity = (userId, weeks, startFrom) => {
    if (!userId) { setUserDailyCapacity([]); return; }
    const w = weeks || capRange;
    setUserDailyCapLoading(true);
    const params = `user_id=${userId}&weeks=${w}${startFrom ? `&start_from=${startFrom}` : ''}`;
    authFetch(`/api/issues/user-capacity-daily?${params}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setUserDailyCapacity(Array.isArray(data) ? data : []); setUserDailyCapLoading(false); })
      .catch(() => { setUserDailyCapacity([]); setUserDailyCapLoading(false); });
  };

  const loadReschedCapacity = (userId, weeks) => {
    if (!userId) { setReschedCapacity([]); return; }
    const w = weeks || reschedCapRange;
    authFetch(`/api/issues/user-capacity-daily?user_id=${userId}&weeks=${w}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setReschedCapacity(Array.isArray(data) ? data : []))
      .catch(() => setReschedCapacity([]));
  };

  const loadUnassigned = () => {
    setUnassignedLoading(true);
    authFetch('/api/issues/unassigned')
      .then(r => r.ok ? r.json() : [])
      .then(data => { setUnassignedJobs(Array.isArray(data) ? data : []); setUnassignedLoading(false); })
      .catch(() => { setUnassignedJobs([]); setUnassignedLoading(false); });
  };

  const loadAssignCapacity = (userId, weeks, startFrom) => {
    if (!userId) { setAssignCapacity([]); return; }
    const w = weeks || assignCapRange;
    authFetch(`/api/issues/user-capacity-daily?user_id=${userId}&weeks=${w}${startFrom ? `&start_from=${startFrom}` : ''}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setAssignCapacity(Array.isArray(data) ? data : []))
      .catch(() => setAssignCapacity([]));
  };

  const openAssignTarget = (wo) => {
    const defDate = wo.due_date ? wo.due_date.slice(0, 10) : '';
    const unassignedOps = (wo.operations || []).filter(op => !op.assigned_to_name && !op.has_assignees && op.status !== 'Complete' && op.status !== 'Confirmed');
    const forms = {};
    unassignedOps.forEach(op => {
      const estH = parseFloat(op.estimated_hours) || 0;
      const rawMins = Math.round((estH % 1) * 60);
      const snapMins = [0, 15, 30, 45].reduce((p, c) => Math.abs(c - rawMins) < Math.abs(p - rawMins) ? c : p);
      forms[op.id] = { assigned_to: '', planned_date: defDate, est_hours: Math.floor(estH), est_minutes: snapMins };
    });
    setOpForms(forms);
    setActiveOpId(null);
    setAssignCapacity([]);
    let w = 4; let sf = null;
    if (defDate) {
      const planned = new Date(defDate + 'T00:00:00');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const twoWeeksBefore = new Date(planned); twoWeeksBefore.setDate(planned.getDate() - 14);
      sf = twoWeeksBefore < today ? today.toISOString().split('T')[0] : twoWeeksBefore.toISOString().split('T')[0];
      w = Math.max(Math.ceil((planned - new Date(sf + 'T00:00:00')) / (7 * 86400000)) + 3, 4);
    }
    setAssignCapRange(w); setAssignCapStartFrom(sf);
    setAssignTarget(wo);
  };

  const handleAssignJob = async () => {
    if (!assignTarget) return;
    const toAssign = Object.entries(opForms).filter(([, f]) => f.assigned_to && f.planned_date);
    if (toAssign.length === 0) return;
    setAssignSaving(true);
    for (const [opIdStr, f] of toAssign) {
      const op = (assignTarget.operations || []).find(o => String(o.id) === opIdStr);
      if (!op) continue;
      const estH = f.est_hours + f.est_minutes / 60;
      await authFetch(`/api/issues/${assignTarget.id}/operations/${op.id}`, {
        method: 'PUT',
        body: JSON.stringify({ title: op.title, planned_date: f.planned_date, assigned_to: f.assigned_to, estimated_hours: estH || op.estimated_hours || undefined })
      });
    }
    setUnassignedJobs(prev => prev.filter(j => j.id !== assignTarget.id));
    setAssignTarget(null);
    setAssignSaving(false);
    showToast('Operations assigned');
    playSuccess();
  };

  const openReschedule = (op, isAssign = false) => {
    const estH = parseFloat(op.estimated_hours) || 0;
    setRescheduleOp(op);
    setReschedAssignedTo(op.assigned_to || '');
    setReschedDate('');
    setReschedEstHours(Math.floor(estH));
    const rawMins = Math.round((estH % 1) * 60);
    setReschedEstMinutes([0, 15, 30, 45].reduce((prev, curr) => Math.abs(curr - rawMins) < Math.abs(prev - rawMins) ? curr : prev));
    setReschedReason('');
    setReschedComment('');
    setReschedPush(op.priority === 'Push');
    setReschedIsAssign(isAssign);
    setReschedCapRange(4);
    if (op.assigned_to) loadReschedCapacity(op.assigned_to, 4);
    else setReschedCapacity([]);
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleOp || !reschedDate) return;
    if (!reschedIsAssign && (!reschedReason || !reschedComment.trim())) return;
    const estH = reschedEstHours + reschedEstMinutes / 60;
    const reason = reschedIsAssign ? 'Not Previously Assigned' : reschedReason;
    const comment = reschedIsAssign ? 'Assigned via supervisor panel' : reschedComment;
    const res = await authFetch(`/api/issues/operations/${rescheduleOp.id}/reschedule`, {
      method: 'PATCH',
      body: JSON.stringify({ planned_date: reschedDate, reason, comment, assigned_to: reschedAssignedTo || undefined, estimated_hours: estH, priority: reschedPush ? 'Push' : 'Normal' })
    });
    if (res.ok) {
      playSuccess();
      setOverdueJobs(prev => prev.filter(o => o.id !== rescheduleOp.id));
      setRescheduleOp(null);
    }
  };

  const handleReassignSubmit = async () => {
    if (!reassignOp || !reassignUserId) return;
    setReassignSaving(true);
    const res = await authFetch(`/api/issues/operations/${reassignOp.id}/reassign`, {
      method: 'PATCH',
      body: JSON.stringify({ assigned_to: reassignUserId })
    });
    if (res.ok) {
      const data = await res.json();
      setOverdueJobs(prev => prev.map(o => o.id === reassignOp.id ? { ...o, assigned_to: reassignUserId, assigned_to_name: data.assigned_to_name } : o));
      setReassignOp(null);
      setReassignUserId('');
      showToast(`Operation reassigned to ${data.assigned_to_name}`);
      playSuccess();
    }
    setReassignSaving(false);
  };

  // Auto-clear selected planned dates when capacity data shows they'd exceed capacity
  useEffect(() => {
    if (userDailyCapacity.length === 0) return;
    // Notification planned date
    if (notifPlannedDate) {
      const jobH = notifEstHours + notifEstMinutes / 60;
      const d = userDailyCapacity.find(d => d.date === notifPlannedDate);
      if (d && d.available > 0 && (d.allocated + jobH) > d.available) setNotifPlannedDate('');
      if (d && d.available > 0 && d.allocated >= d.available) setNotifPlannedDate('');
    }
    // Operation planned date
    if (opApprovalForm.planned_date) {
      const jobH = (opApprovalForm.est_hours || 0) + (opApprovalForm.est_minutes || 0) / 60;
      const d = userDailyCapacity.find(d => d.date === opApprovalForm.planned_date);
      if (d && d.available > 0 && (d.allocated + jobH) > d.available) setOpApprovalForm(f => ({ ...f, planned_date: '' }));
      if (d && d.available > 0 && d.allocated >= d.available) setOpApprovalForm(f => ({ ...f, planned_date: '' }));
    }
  }, [userDailyCapacity, notifEstHours, notifEstMinutes, opApprovalForm.est_hours, opApprovalForm.est_minutes]);

  const loadOpCapacity = (userId) => {
    if (!userId) { setOpCapacity([]); setUserDailyCapacity([]); return; }
    loadUserDailyCapacity(userId);
    const now = new Date();
    const dow = now.getDay();
    const monOff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(now); mon.setDate(now.getDate() + monOff); mon.setHours(0,0,0,0);
    const sun2 = new Date(mon); sun2.setDate(mon.getDate() + 13);
    const start = mon.toISOString().split('T')[0];
    const end = sun2.toISOString().split('T')[0];
    authFetch(`/api/issues/team-capacity?start=${start}&end=${end}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const userCap = (Array.isArray(data) ? data : []).find(u => u.id === parseInt(userId));
        setOpCapacity(userCap ? [userCap] : []);
      })
      .catch(() => setOpCapacity([]));
  };

  // My Team tab: load single user capacity for calendar view
  const loadTeamUserCapacity = (userId, weeks) => {
    if (!userId) { setTeamUserCapacity([]); return; }
    const w = weeks || teamCapRange;
    setTeamUserCapLoading(true);
    authFetch(`/api/issues/user-capacity-daily?user_id=${userId}&weeks=${w}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setTeamUserCapacity(Array.isArray(data) ? data : []); setTeamUserCapLoading(false); })
      .catch(() => { setTeamUserCapacity([]); setTeamUserCapLoading(false); });
  };

  // My Team tab: load all team users capacity overview (weeks matches calendar range)
  const loadTeamAllCapacity = (weeks) => {
    const w = weeks || teamCapRange;
    setTeamAllCapLoading(true);
    const now = new Date();
    const dow = now.getDay();
    const monOff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(now); mon.setDate(now.getDate() + monOff); mon.setHours(0,0,0,0);
    const endDate = new Date(mon); endDate.setDate(mon.getDate() + w * 7 - 1);
    const start = mon.toISOString().split('T')[0];
    const end = endDate.toISOString().split('T')[0];
    authFetch(`/api/issues/team-capacity?start=${start}&end=${end}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setTeamAllCapacity(Array.isArray(data) ? data : []); setTeamAllCapLoading(false); })
      .catch(() => { setTeamAllCapacity([]); setTeamAllCapLoading(false); });
  };

  // Metrics tab loaders
  const loadMetrics = (view) => {
    setMetricsLoading(true);
    let url;
    if (view === 'weekly') url = '/api/admin/metrics/weekly?weeks=8';
    else if (view === 'monthly') url = '/api/admin/metrics/monthly?months=12';
    else url = '/api/admin/metrics/yearly?years=5';
    authFetch(url)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const normalized = data.reverse().map((d, i) => ({
          ...d,
          period_start: d.period_start || d.week_start,
          period_end: d.period_end || d.week_end,
          period_label: d.period_label || null,
          week_num: i + 1,
        }));
        setMetrics(normalized);
        // Default the trend drill-down to the previous completed period so the
        // summary cards immediately show meaningful numbers. Most useful for
        // monthly view but fine for weekly/yearly too.
        if (normalized.length >= 2) {
          setSelectedPeriod(normalized[normalized.length - 2]);
        } else if (normalized.length === 1) {
          setSelectedPeriod(normalized[0]);
        } else {
          setSelectedPeriod(null);
        }
        setMetricsLoading(false);
      })
      .catch(() => { setMetrics([]); setMetricsLoading(false); });
  };

  const loadCostMetrics = (period) => {
    setCostLoading(true);
    authFetch(`/api/issues/team-cost-metrics?period=${period}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { setCostMetrics(data); setCostLoading(false); })
      .catch(() => { setCostMetrics(null); setCostLoading(false); });
  };

  const handleApproveOp = async (op) => {
    const estHours = (parseFloat(opApprovalForm.est_hours) || 0) + (parseFloat(opApprovalForm.est_minutes) || 0) / 60;
    const res = await authFetch(`/api/issues/${op.issue_id}/operations/${op.id}/approval`, {
      method: 'PUT',
      body: JSON.stringify({
        status: 'approved',
        assigned_to: opApprovalForm.assigned_to || undefined,
        planned_date: opApprovalForm.planned_date || undefined,
        estimated_hours: estHours || undefined
      })
    });
    if (res.ok) {
      playSuccess();
      showToast('Operation approved');
      setExpandedOp(null);
      loadPendingOps();
    } else { showToast('Failed to approve', 'error'); }
  };

  const handleRejectOp = async (op, reason) => {
    const res = await authFetch(`/api/issues/${op.issue_id}/operations/${op.id}/approval`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'rejected', reject_reason: reason })
    });
    if (res.ok) {
      playDelete();
      showToast('Operation rejected');
      setExpandedOp(null);
      loadPendingOps();
    } else { showToast('Failed to reject', 'error'); }
  };

  const handleApproveNotif = async (id, assignedTo, plannedDate, estHours, estMinutes, pushPriority) => {
    const body = {};
    if (assignedTo) body.assigned_to = assignedTo;
    if (plannedDate) body.planned_date = plannedDate;
    const totalHours = (parseFloat(estHours) || 0) + (parseFloat(estMinutes) || 0) / 60;
    if (totalHours > 0) body.estimated_hours = totalHours;
    if (pushPriority) body.priority = 'Push';
    // Include per-operation schedules if available
    if (Object.keys(notifOpSchedules).length > 0) {
      body.operation_schedules = Object.entries(notifOpSchedules).map(([idx, s]) => ({
        index: parseInt(idx),
        planned_date: s.date,
        estimated_hours: (s.hours || 0) + (s.minutes || 0) / 60
      }));
    }
    const res = await authFetch(`/api/notifs/${id}/approve`, { method: 'POST', body: JSON.stringify(body) });
    if (res.ok) {
      const data = await res.json();
      playSuccess();
      setApproveSuccess({ wo_number: data.wo_number, added_to_existing: data.added_to_existing });
      setNotifAssignedTo('');
      setNotifPlannedDate('');
      setNotifEstHours(0);
      setNotifEstMinutes(0);
      setNotifPush(false);
      setNotifActiveOp(0);
      setNotifOpSchedules({});
      setUserDailyCapacity([]);
      loadNotifications();
    } else {
      showToast('Failed to approve', 'error');
    }
  };

  const handleRejectNotif = async (id, reason) => {
    const res = await authFetch(`/api/notifs/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
    if (res.ok) {
      playDelete();
      showToast('Notification rejected');
      loadNotifications();
    } else {
      showToast('Failed to reject', 'error');
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim() || !rejectTarget) return;
    setRejectSending(true);
    const reason = autoCapitalize(rejectReason.trim());
    if (rejectTarget.type === 'operation') {
      await handleRejectOp({ id: rejectTarget.id, issue_id: rejectTarget.issue_id }, reason);
    } else {
      await handleRejectNotif(rejectTarget.id, reason);
    }
    setRejectTarget(null);
    setRejectReason('');
    setRejectSending(false);
  };

  const loadCapacity = (range) => {
    setCapacityLoading(true);
    const now = new Date();
    const dow = now.getDay();
    const monOff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(now); mon.setDate(now.getDate() + monOff); mon.setHours(0,0,0,0);
    let start, end;
    if (range === 'week') {
      start = mon.toISOString().split('T')[0];
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      end = sun.toISOString().split('T')[0];
    } else if (range === '2week') {
      start = mon.toISOString().split('T')[0];
      const sun2 = new Date(mon); sun2.setDate(mon.getDate() + 13);
      end = sun2.toISOString().split('T')[0];
    } else {
      // month: from this Monday to 4 weeks out
      start = mon.toISOString().split('T')[0];
      const end4 = new Date(mon); end4.setDate(mon.getDate() + 27);
      end = end4.toISOString().split('T')[0];
    }
    authFetch(`/api/issues/team-capacity?start=${start}&end=${end}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setCapacityData(Array.isArray(data) ? data : []); setCapacityLoading(false); })
      .catch(() => { setCapacityData([]); setCapacityLoading(false); });
  };

  const loadDailyCapacity = (range) => {
    setDailyCapLoading(true);
    const now = new Date();
    const dow = now.getDay();
    const monOff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(now); mon.setDate(now.getDate() + monOff); mon.setHours(0,0,0,0);
    let start, end;
    if (range === 'week') {
      start = mon.toISOString().split('T')[0];
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      end = sun.toISOString().split('T')[0];
    } else if (range === '2week') {
      start = mon.toISOString().split('T')[0];
      const sun2 = new Date(mon); sun2.setDate(mon.getDate() + 13);
      end = sun2.toISOString().split('T')[0];
    } else {
      start = mon.toISOString().split('T')[0];
      const end4 = new Date(mon); end4.setDate(mon.getDate() + 27);
      end = end4.toISOString().split('T')[0];
    }
    authFetch(`/api/issues/team-capacity-daily?start=${start}&end=${end}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setDailyCapacity(Array.isArray(data) ? data : []); setDailyCapLoading(false); })
      .catch(() => { setDailyCapacity([]); setDailyCapLoading(false); });
  };

  useEffect(() => {
    loadTeco();
    loadAwaitingParts();
    loadOverdue();
    loadNotifications();
    loadPendingOps();
    loadReschedules();

    loadTeamUsers();
    loadUnassigned();
  }, []);

  useEffect(() => { if (tab === 'unassigned') loadUnassigned(); }, [tab]);

  // Auto-expand user from URL params (e.g. returning from View WO)
  useEffect(() => {
    if (initialUser && teamUsers.length > 0 && !teamViewUser) {
      const uid = parseInt(initialUser);
      if (teamUsers.some(u => u.id === uid)) {
        setTeamViewUser(uid);
        loadTeamUserCapacity(uid, teamCapRange);
      }
    }
  }, [initialUser, teamUsers]);

  // Auto-close approve/teco success after 3s
  useEffect(() => {
    if (approveSuccess) {
      const timer = setTimeout(() => setApproveSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [approveSuccess]);
  useEffect(() => {
    if (tecoSuccess) {
      const timer = setTimeout(() => setTecoSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [tecoSuccess]);

  // Live polling every 30s for active tab data (silent to avoid flicker)
  useEffect(() => {
    const interval = setInterval(() => {
      if (tab === 'notifications') { loadNotifications(true); loadPendingOps(true); }
      else if (tab === 'teco') loadTeco();
      else if (tab === 'awaiting-parts') loadAwaitingParts();
      else if (tab === 'overdue') loadOverdue();
      else if (tab === 'reschedules') loadReschedules();
      else if (tab === 'inforequired') loadInfoJobs();
    }, 30000);
    return () => clearInterval(interval);
  }, [tab]);

  useEffect(() => {
    if (tab === 'notifications') { loadNotifications(); loadPendingOps(); }
    if (tab === 'teco') loadTeco();
    if (tab === 'awaiting-parts') loadAwaitingParts();
    if (tab === 'overdue') loadOverdue();
    if (tab === 'reschedules') loadReschedules();
    if (tab === 'capacity') { loadCapacity(capacityRange); loadDailyCapacity(capacityRange); }
    if (tab === 'myteam') { loadTeamAllCapacity(teamCapRange); loadTeamBlocks(); }
    if (tab === 'metrics') { loadMetrics(metricsView); loadCostMetrics(costPeriod); }
    if (tab === 'pre-read') { loadPreRead(undefined, { silent: !!preReadData }); loadPreReadHistory(); }
    if (tab === 'team-reports') { loadTeamReports(teamReportType, teamReportFrom, teamReportTo); }
    if (tab === 'variance') { loadVariance(varianceFrom, varianceTo); }
  }, [tab]);

  // Load pre-read status on mount so the tab badge reflects availability from any tab
  useEffect(() => { loadPreRead(); }, []);

  const handleTeco = (issueId, woNumber) => {
    // Show signature modal first — supervisor must sign before TECO
    setTecoSignatureTarget({ issueId, woNumber, supervisorName: user?.name });
  };

  const handleTecoSign = async (signatureDataUrl) => {
    const { issueId, woNumber } = tecoSignatureTarget;
    setTecoSignatureTarget(null);
    const res = await authFetch(`/api/issues/${issueId}/teco`, {
      method: 'POST',
      body: JSON.stringify({ supervisor_signature: signatureDataUrl })
    });
    if (res.ok) {
      playSuccess();
      setTecoSuccess(woNumber || 'Work Order');
      loadTeco();
    } else {
      const data = await res.json().catch(() => ({}));
      showToast(data.error || 'TECO failed', 'error');
    }
  };

  if (loading) return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="skeleton" style={{ width: 200, height: 28, marginBottom: 8 }} />
        <div className="skeleton" style={{ width: 180, height: 14 }} />
      </div>
      <div className="skeleton" style={{ height: 44, borderRadius: 10, marginBottom: 16 }} />
      {[...Array(4)].map((_, i) => (
        <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 16, marginBottom: 8, border: '1px solid var(--border)' }}>
          <div className="skeleton" style={{ width: '50%', height: 16, marginBottom: 8 }} />
          <div className="skeleton" style={{ width: '30%', height: 12 }} />
        </div>
      ))}
    </div>
  );

  const opTotalMins = (opApprovalForm.est_hours * 60) + opApprovalForm.est_minutes;
  const opApproveDisabled = !opApprovalForm.assigned_to || !opApprovalForm.planned_date || opTotalMins < 15;

  return (
    <div>
      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: 'fixed', top: 20, right: 20, zIndex: 999, padding: '12px 20px', borderRadius: 10,
              background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${toast.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
              color: toast.type === 'error' ? '#dc2626' : '#16a34a',
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <FiCheckCircle size={16} /> {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Capacity Day Drill-Down Modal */}
      <AnimatePresence>
        {capDayModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setCapDayModal(null); setCapDayReassigning(null); setCapDayNewDate(''); setCapDayReason(''); setCapDayComment(''); setCapDayAssignedTo(''); setCapDayCalData([]); setCapDayHistory(null); }}
            style={{ position: 'fixed', inset: 0, zIndex: 990, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', width: '100%', maxWidth: 780, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                    {capDayModal.userName} — {new Date(capDayModal.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={{ fontSize: 12, color: capDayModal.allocated > capDayModal.available ? '#dc2626' : capDayModal.available > 0 && capDayModal.allocated >= capDayModal.available ? '#f59e0b' : '#16a34a', fontWeight: 600, marginTop: 4 }}>
                    {fmtH(capDayModal.allocated)} booked / {fmtH(capDayModal.available)} capacity{capDayModal.allocated > capDayModal.available ? ' (Overbooked)' : capDayModal.available > 0 && capDayModal.allocated >= capDayModal.available ? ' (Full)' : ''}
                  </div>
                </div>
                <button onClick={() => { setCapDayModal(null); setCapDayReassigning(null); setCapDayNewDate(''); setCapDayReason(''); setCapDayComment(''); setCapDayAssignedTo(''); setCapDayCalData([]); setCapDayHistory(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '16px 24px' }}>
                {capDayModal.jobs.map((job, ji) => {
                  const isActive = capDayReassigning?.opId === job.opId;
                  const isShowingHistory = capDayHistory?.issueId === job.issueId;
                  return (
                  <div key={ji} style={{ padding: '12px 16px', borderRadius: 10, border: `1px solid ${isActive ? '#2563eb' : 'var(--border)'}`, background: isActive ? 'rgba(37,99,235,0.03)' : 'var(--bg-secondary)', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{job.wo}{job.opNum ? ` — ${job.opNum}` : ''}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: '#fff', background: job.priority === 'Push' ? '#8b5cf6' : '#6b7280' }}>{job.priority === 'Push' ? 'Push' : 'Normal'}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{job.name}{job.opTitle && job.opTitle !== job.name ? ` (${job.opTitle})` : ''}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{fmtH(job.hours)} planned</div>
                      </div>
                      {!capDayReassigning && !isShowingHistory && (
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <button onClick={() => { navigate(`/work-orders/${job.wo}?from=capacity&user=${capDayModal.userId}`); setCapDayModal(null); }}
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <FiMaximize2 size={10} /> View WO
                          </button>
                          <button onClick={async () => {
                            if (isShowingHistory) { setCapDayHistory(null); return; }
                            setCapDayHistory({ issueId: job.issueId, records: null });
                            try {
                              const r = await authFetch(`/api/issues/${job.issueId}/history`);
                              if (r.ok) { const data = await r.json(); setCapDayHistory({ issueId: job.issueId, records: data.filter(h => h.field === 'reschedule' || h.field === 'planned_date' || h.field === 'due_date' || h.field === 'assigned_to') }); }
                              else setCapDayHistory(null);
                            } catch { setCapDayHistory(null); }
                          }}
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <FiClock size={10} /> History
                          </button>
                          {job.opId && (
                            <button onClick={() => {
                              setCapDayReassigning(job); setCapDayNewDate(''); setCapDayReason(''); setCapDayComment(''); setCapDayAssignedTo(''); setCapDayHistory(null); loadCapDayCal(capDayModal.userId);
                            }}
                              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #2563eb', background: 'rgba(37,99,235,0.06)', color: '#2563eb', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <FiCalendar size={10} /> Reschedule
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {/* History panel */}
                    {isShowingHistory && (
                      <div style={{ marginTop: 12 }}>
                        {capDayHistory.records === null ? (
                          <div style={{ textAlign: 'center', padding: 12 }}>
                            <div style={{ width: 18, height: 18, border: '2px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                          </div>
                        ) : capDayHistory.records.length === 0 ? (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 10 }}>No reschedule history</div>
                        ) : (
                          <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {capDayHistory.records.map((h, hi) => (
                              <div key={hi} style={{ display: 'flex', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-input)', border: '1px solid var(--border)', fontSize: 11 }}>
                                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', minWidth: 70 }}>{new Date(h.created_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}</span>
                                <span style={{ fontWeight: 600, color: 'var(--text-primary)', minWidth: 80 }}>{h.user_name || 'System'}</span>
                                <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{h.new_value || h.old_value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                          <button onClick={() => setCapDayHistory(null)}
                            style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>Back</button>
                        </div>
                      </div>
                    )}
                    {isActive && (
                      <div style={{ marginTop: 12 }}>
                        {/* Reason */}
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Reason</label>
                          <select value={capDayReason} onChange={e => setCapDayReason(e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}>
                            <option value="">Select reason...</option>
                            {RESCHEDULE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </div>
                        {capDayReason && (
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Comment</label>
                            <input value={capDayComment} onChange={e => { let v = e.target.value; if (v.length > 0) v = v.charAt(0).toUpperCase() + v.slice(1); v = v.replace(/([.!?]\s+)([a-z])/g, (m, p, c) => p + c.toUpperCase()); setCapDayComment(v); }}
                              placeholder="Optional comment..."
                              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box' }} />
                          </div>
                        )}
                        {/* Resource (assignee) */}
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Assign To</label>
                          <select value={capDayAssignedTo} onChange={e => { setCapDayAssignedTo(e.target.value); if (e.target.value) loadCapDayCal(parseInt(e.target.value)); else loadCapDayCal(capDayModal.userId); }}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}>
                            <option value="">Keep current ({capDayModal.userName})</option>
                            {teamUsers.filter(u => u.id !== capDayModal.userId && !u.disabled).map(u => <option key={u.id} value={u.id}>{u.name}{u.employee_id ? ` (${u.employee_id})` : ''}</option>)}
                          </select>
                        </div>
                        {/* Calendar */}
                        <div style={{ marginBottom: 10 }}>
                          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>New Date *</label>
                          {capDayCalData.length > 0 ? (() => {
                            const calFmtH = v => { const snapped = Math.round(v * 4) / 4; const h = Math.floor(snapped); const m = Math.round((snapped - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };
                            const calToday = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                            return (
                              <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg-input)' }}>
                                {Array.from({ length: Math.ceil(capDayCalData.length / 7) }, (_, wi) => {
                                  const wd = capDayCalData.slice(wi * 7, (wi + 1) * 7);
                                  if (wd.length === 0) return null;
                                  return (
                                    <div key={wi} style={{ marginBottom: 6 }}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 3 }}>{new Date(wd[0].date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${wd.length}, 1fr)`, gap: 3 }}>
                                        {wd.map(day => {
                                          const isOff = day.offDuty;
                                          const isBlocked = day.blocked;
                                          const isFull = day.available > 0 && day.allocated >= day.available;
                                          const isOver = day.available > 0 && day.allocated > day.available;
                                          const isPastDay = day.date < calToday;
                                          const isSelected = day.date === capDayNewDate;
                                          const isPush = capDayReassigning?.priority === 'Push';
                                          const wouldExceed = !isPush && day.available > 0 && (day.allocated + parseFloat(capDayReassigning?.hours || 0)) > day.available;
                                          const disabled = isPastDay || isOff || isBlocked || wouldExceed;
                                          const bg = isSelected ? '#2563eb' : isBlocked ? 'rgba(139,92,246,0.1)' : isOver ? 'rgba(153,27,27,0.85)' : isFull ? 'rgba(239,68,68,0.08)' : isOff ? 'rgba(245,158,11,0.05)' : 'var(--bg-card)';
                                          const textColor = isSelected ? '#fff' : isOver ? '#fff' : 'var(--text-primary)';
                                          return (
                                            <div key={day.date}
                                              onClick={() => { if (!disabled) setCapDayNewDate(day.date); }}
                                              style={{ padding: '5px 2px', borderRadius: 6, textAlign: 'center', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? ((isOff || isBlocked) ? 0.65 : 0.4) : 1, border: isSelected ? '2px solid #2563eb' : '1px solid var(--border-subtle)', background: bg, transition: 'all 0.1s', minHeight: 54, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                                              <div style={{ fontSize: 11, fontWeight: 600, color: isSelected ? '#fff' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : 'var(--text-muted)' }}>{day.day}</div>
                                              <div style={{ fontSize: 15, fontWeight: 800, color: textColor, lineHeight: 1.1 }}>{new Date(day.date + 'T00:00:00').getDate()}</div>
                                              <div style={{ fontSize: 10, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.8)' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : isOver ? '#fecaca' : isFull ? '#ef4444' : '#16a34a' }}>{isBlocked ? (day.blockName || 'BLK') : isOff ? 'OFF' : `${calFmtH(day.allocated)}/${calFmtH(day.available)}`}</div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })() : (
                            <div style={{ textAlign: 'center', padding: 16 }}>
                              <div style={{ width: 20, height: 20, border: '2px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                            </div>
                          )}
                        </div>
                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 10, borderTop: '1px solid var(--border)', marginTop: 10 }}>
                          <button onClick={() => { setCapDayReassigning(null); setCapDayNewDate(''); setCapDayReason(''); setCapDayComment(''); setCapDayAssignedTo(''); setCapDayCalData([]); }}
                            style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                          <button disabled={!capDayNewDate || !capDayReason || capDaySaving}
                            onClick={async () => {
                              // Check capacity: don't allow if job would exceed day capacity (unless Push)
                              const isPush = capDayReassigning?.priority === 'Push';
                              if (!isPush && capDayNewDate) {
                                const targetDay = capDayCalData.find(d => d.date === capDayNewDate);
                                if (targetDay) {
                                  const jobHours = parseFloat(capDayReassigning?.hours || 0);
                                  if (targetDay.available > 0 && (targetDay.allocated + jobHours) > targetDay.available) {
                                    showToast(`Cannot reschedule — ${fmtH(targetDay.allocated + jobHours)} would exceed ${fmtH(targetDay.available)} capacity`, 'error');
                                    return;
                                  }
                                  if (targetDay.blocked) {
                                    showToast('Cannot reschedule to a blocked date', 'error');
                                    return;
                                  }
                                }
                              }
                              setCapDaySaving(true);
                              try {
                                const body = { planned_date: capDayNewDate };
                                if (capDayReason) body.reason = capDayReason;
                                if (capDayComment) body.comment = capDayComment;
                                if (capDayAssignedTo) body.assigned_to = parseInt(capDayAssignedTo);
                                const res = await authFetch(`/api/issues/operations/${job.opId}/reschedule`, { method: 'PATCH', body: JSON.stringify(body) });
                                if (res.ok) {
                                  showToast(`Rescheduled ${job.wo} to ${new Date(capDayNewDate + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}`);
                                  setCapDayReassigning(null); setCapDayNewDate(''); setCapDayReason(''); setCapDayComment(''); setCapDayAssignedTo(''); setCapDayCalData([]); setCapDayModal(null);
                                  loadTeamUserCapacity(teamViewUser, teamCapRange);
                                  loadCapacity(capacityRange);
                                } else {
                                  const d = await res.json(); showToast(d.error || 'Error', 'error');
                                }
                              } catch (e) { showToast('Network error', 'error'); }
                              setCapDaySaving(false);
                            }}
                            style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: capDayNewDate && capDayReason ? '#2563eb' : 'var(--bg-secondary)', color: capDayNewDate && capDayReason ? '#fff' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: capDayNewDate && capDayReason ? 'pointer' : 'not-allowed' }}>
                            {capDaySaving ? 'Saving...' : 'Reschedule'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
                {capDayModal.jobs.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>No jobs on this day</div>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TECO Supervisor Signature Modal */}
      {tecoSignatureTarget && (
        <SignatureModal
          title="Supervisor TECO Sign-Off"
          subtitle={`Sign to technically complete work order ${tecoSignatureTarget.woNumber}. This signature will appear on the shop paper.`}
          name={tecoSignatureTarget.supervisorName}
          onSign={handleTecoSign}
          onCancel={() => setTecoSignatureTarget(null)}
        />
      )}

      {/* Approve success dialog */}
      <AnimatePresence>
        {approveSuccess && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setApproveSuccess(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 20, padding: '40px 32px', width: '90%', maxWidth: 360, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.1 }}
                style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 32px rgba(16,185,129,0.4)' }}>
                <FiCheck size={36} color="#fff" strokeWidth={3} />
              </motion.div>
              <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
                {approveSuccess?.added_to_existing ? 'Operation Added' : 'Work Order Created'}
              </motion.h2>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#2563eb', fontFamily: 'monospace', letterSpacing: 1 }}>
                {approveSuccess?.wo_number || approveSuccess}
              </motion.p>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
                style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                {approveSuccess?.added_to_existing ? 'Operation added to existing work order' : 'Work order created successfully'}
              </motion.p>
            </motion.div>
          </motion.div>
        )}
        {tecoSuccess && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setTecoSuccess(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 20, padding: '40px 32px', width: '90%', maxWidth: 360, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 400, damping: 15, delay: 0.1 }}
                style={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 8px 32px rgba(16,185,129,0.4)' }}>
                <FiCheck size={36} color="#fff" strokeWidth={3} />
              </motion.div>
              <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
                TECO Complete
              </motion.h2>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
                style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#10b981', fontFamily: 'monospace', letterSpacing: 1 }}>
                {tecoSuccess}
              </motion.p>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
                style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                Work order technically completed
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Supervisor Panel</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>
            Manage your team and review completed work orders
          </p>
        </div>
        <button onClick={() => setShowTV(true)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
          borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          background: 'linear-gradient(135deg, #2563eb, #7c3aed)', color: '#fff',
          boxShadow: '0 4px 14px rgba(37,99,235,0.3)'
        }}>
          <FiMaximize2 size={15} /> Cast to TV
        </button>
      </div>

      {showTV && <TVDisplay onClose={() => setShowTV(false)} />}

      {/* Pre-Read availability banner */}
      {preReadData && !preReadData.frozen && (preReadData.operations?.length || 0) > 0 && tab !== 'pre-read' && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          onClick={() => { playClick(); setTab('pre-read'); }}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(139,92,246,0.08))', border: '1px solid rgba(37,99,235,0.25)', cursor: 'pointer', marginBottom: 16, boxShadow: '0 2px 8px rgba(37,99,235,0.08)' }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiFileText size={18} color="#2563eb" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>Weekly Pre-Read Available</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
              {preReadData?.operations?.length || 0} operation{(preReadData?.operations?.length || 0) !== 1 ? 's' : ''} for week of {(() => { const d = new Date((preReadData?.preRead?.week_start || '') + 'T00:00:00'); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }); })()} — click to review and submit
            </div>
          </div>
          <FiArrowRight size={16} color="#2563eb" />
        </motion.div>
      )}

      {/* Category buttons */}
      {(() => {
        const preReadOpsCount = preReadData?.operations?.length || 0;
        const preReadPending = preReadData && !preReadData.frozen && preReadOpsCount > 0;
        const getBadge = (key) => key === 'notifications' ? (notifications.length + pendingOps.length) || 0
          : key === 'teco' ? tecoJobs.length : key === 'overdue' ? overdueJobs.length
          : key === 'unassigned' ? unassignedJobs.length : key === 'awaiting-parts' ? awaitingPartsJobs.length
          : key === 'pre-read' ? (preReadPending ? preReadOpsCount : 0) : 0;
        const activeCat = TAB_CATEGORIES.find(c => c.tabs.some(t => t.key === tab));
        const catIcons = { 'Workflow Approvals': FiBell, 'Work Order Management': FiList, 'Planning': FiCalendar, 'Insights': FiTrendingUp };
        return (
          <div style={{ marginBottom: 20 }}>
            {/* Main category row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: activeCat ? 10 : 0 }}>
              {TAB_CATEGORIES.map(cat => {
                const catTotal = cat.tabs.reduce((sum, t) => sum + getBadge(t.key), 0);
                const isOpen = activeCat?.label === cat.label;
                const CatIcon = catIcons[cat.label] || FiList;
                return (
                  <button key={cat.label}
                    onClick={() => { playClick(); if (isOpen && cat.tabs.length > 1) return; setTab(cat.tabs[0].key); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                      borderRadius: 10, border: `2px solid ${isOpen ? '#2563eb' : 'var(--border)'}`,
                      background: isOpen ? 'rgba(37,99,235,0.06)' : 'var(--bg-card)',
                      cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left', width: '100%'
                    }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      background: isOpen ? '#2563eb' : 'var(--bg-input)', color: isOpen ? '#fff' : 'var(--text-muted)', transition: 'all 0.15s'
                    }}>
                      <CatIcon size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isOpen ? '#2563eb' : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cat.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{cat.tabs.length} {cat.tabs.length === 1 ? 'section' : 'sections'}</div>
                    </div>
                    {catTotal > 0 && (
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 700, flexShrink: 0,
                        background: isOpen ? '#2563eb' : '#ef4444', color: '#fff', minWidth: 24, textAlign: 'center'
                      }}>{catTotal}</span>
                    )}
                  </button>
                );
              })}
            </div>
            {/* Sub-tabs for active category */}
            {activeCat && activeCat.tabs.length > 1 && (
              <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', borderRadius: 10, padding: 4, border: '1px solid var(--border)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {activeCat.tabs.map(t => {
                  const isActive = tab === t.key;
                  const badge = getBadge(t.key);
                  const badgeColor = t.key === 'awaiting-parts' ? '#f59e0b' : '#ef4444';
                  return (
                    <button key={t.key} onClick={() => { playClick(); setTab(t.key); }}
                      style={{
                        flex: '1 1 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        padding: '9px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600, transition: 'all 0.15s', whiteSpace: 'nowrap',
                        background: isActive ? '#2563eb' : 'transparent',
                        color: isActive ? '#fff' : 'var(--text-secondary)',
                        minWidth: 0
                      }}>
                      <t.icon size={14} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.label}</span>
                      {badge > 0 && (
                        <span style={{
                          background: isActive ? 'rgba(255,255,255,0.3)' : badgeColor,
                          color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                          lineHeight: '16px', minWidth: 16, textAlign: 'center', flexShrink: 0
                        }}>{badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Workflow Approvals Tab */}
      {tab === 'notifications' && (
        <div>
          {(notifLoading || pendingOpsLoading) ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <div style={{ fontSize: 14, marginTop: 8 }}>Loading approvals...</div>
            </div>
          ) : expandedNotif ? (
            /* ===== INLINE NOTIFICATION DETAIL VIEW ===== */
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {/* Back button */}
              <button onClick={() => setExpandedNotif(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                <FiArrowRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back to Approvals
              </button>

              {/* Notification header card */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: '20px 24px', marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[expandedNotif.type] || '#6b7280' }}>{expandedNotif.type}</span>
                  <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{expandedNotif.notif_number}</span>
                  {expandedNotif.priority === 'Push' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#8b5cf6' }}>PUSH</span>}
                  {expandedNotif.status === 'Info Required' && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#f59e0b' }}>INFO REQUIRED</span>}
                  {expandedNotif.info_response && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#2563eb' }}>RESPONSE</span>}
                </div>
                <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{expandedNotif.name}</h2>
                {expandedNotif.description && (
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 16, padding: 14, background: 'var(--bg-input)', borderRadius: 10, border: '1px solid var(--border)', whiteSpace: 'pre-wrap' }}>
                    {expandedNotif.description}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 13 }}>
                  {expandedNotif.asset_name && (
                    <div><span style={{ color: 'var(--text-muted)' }}>Asset:</span> <strong>{expandedNotif.asset_tag}</strong> {expandedNotif.asset_name}</div>
                  )}
                  <div><span style={{ color: 'var(--text-muted)' }}>Priority:</span> <strong style={{ color: priorityColors[expandedNotif.priority] }}>{expandedNotif.priority}</strong></div>
                  {expandedNotif.due_date && <div><span style={{ color: 'var(--text-muted)' }}>Due:</span> {formatDate(expandedNotif.due_date)}</div>}
                  {expandedNotif.estimated_hours > 0 && (
                    <div><span style={{ color: 'var(--text-muted)' }}>Est. Time:</span> {Math.floor(expandedNotif.estimated_hours)}h {Math.round((expandedNotif.estimated_hours % 1) * 60)}m</div>
                  )}
                  {expandedNotif.frequency && <div><span style={{ color: 'var(--text-muted)' }}>Frequency:</span> {expandedNotif.frequency}</div>}
                  <div><span style={{ color: 'var(--text-muted)' }}>Created by:</span> {expandedNotif.created_by_name}</div>
                  <div><span style={{ color: 'var(--text-muted)' }}>Created:</span> {formatDate(expandedNotif.created_at)}</div>
                </div>
                {expandedNotif.info_response && (
                  <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.2)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><FiInfo size={12} /> User Response</div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{expandedNotif.info_response}</div>
                    {expandedNotif.info_required_message && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>Request: {expandedNotif.info_required_message}</div>}
                  </div>
                )}
                {expandedNotif.info_required_message && !expandedNotif.info_response && (
                  <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><FiInfo size={12} /> Info Requested</div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.4 }}>{expandedNotif.info_required_message}</div>
                  </div>
                )}
              </div>

              {/* Operations - each displayed with scheduling status */}
              {(() => {
                let ops = getApprovalOps(expandedNotif);
                if (!ops) {
                  ops = [{ operation_number: '0010', title: expandedNotif.name, estimated_hours: expandedNotif.estimated_hours || 0, planned_date: expandedNotif.due_date }];
                }
                const allScheduled = ops.every((_, idx) => notifOpSchedules[idx]?.date);
                return (
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FiList size={14} /> Operations ({ops.length})
                      {ops.length > 1 && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                          background: allScheduled ? 'rgba(22,163,74,0.1)' : 'rgba(245,158,11,0.1)',
                          color: allScheduled ? '#16a34a' : '#d97706'
                        }}>
                          {Object.keys(notifOpSchedules).filter(k => notifOpSchedules[k]?.date).length}/{ops.length} scheduled
                        </span>
                      )}
                    </h3>
                    {ops.length > 1 && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)' }}>
                        Click each operation to set its date. All operations must be scheduled before approving.
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {ops.map((op, idx) => {
                        const opNum = op.operation_number || String((idx + 1) * 10).padStart(4, '0');
                        const opEstH = parseFloat(op.estimated_hours) || 0;
                        const sched = notifOpSchedules[idx];
                        const isActive = notifActiveOp === idx;
                        const isScheduled = !!sched?.date;
                        return (
                          <div key={idx} onClick={() => {
                            setNotifActiveOp(idx);
                            // Set the calendar/hours to this operation's values
                            if (sched?.date) setNotifPlannedDate(sched.date);
                            else setNotifPlannedDate('');
                            const h = sched?.hours != null ? sched.hours : Math.floor(opEstH);
                            const m = sched?.minutes != null ? sched.minutes : Math.round((opEstH % 1) * 60);
                            setNotifEstHours(h);
                            setNotifEstMinutes([0, 15, 30, 45].reduce((prev, curr) => Math.abs(curr - m) < Math.abs(prev - m) ? curr : prev));
                          }}
                            style={{
                              background: isActive ? 'rgba(37,99,235,0.06)' : 'var(--bg-card)',
                              borderRadius: 10, border: `2px solid ${isActive ? '#2563eb' : isScheduled ? 'rgba(22,163,74,0.3)' : 'var(--border)'}`,
                              padding: '12px 16px', cursor: 'pointer', transition: 'all 0.15s'
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ width: 24, height: 24, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800,
                                background: isScheduled ? '#16a34a' : isActive ? '#2563eb' : 'var(--bg-input)',
                                color: (isScheduled || isActive) ? '#fff' : 'var(--text-muted)'
                              }}>{isScheduled ? '✓' : idx + 1}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{opNum}</span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{op.title || op.name || expandedNotif.name}</span>
                              {opEstH > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{Math.floor(opEstH)}h {Math.round((opEstH % 1) * 60)}m</span>}
                            </div>
                            {isScheduled && (
                              <div style={{ marginTop: 4, marginLeft: 32, fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                                Scheduled: {new Date(sched.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' })}
                                {sched.hours > 0 && ` — ${sched.hours}h${sched.minutes > 0 ? ` ${sched.minutes}m` : ''}`}
                              </div>
                            )}
                            {!isScheduled && isActive && (
                              <div style={{ marginTop: 4, marginLeft: 32, fontSize: 11, color: '#2563eb', fontWeight: 600 }}>
                                Select a date below
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Approval controls */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: '20px 24px', marginTop: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12 }}>Approval</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Assign To *</label>
                    <UserDropdown value={notifAssignedTo || expandedNotif.assigned_to || ''} users={teamUsers}
                      onChange={v => { setNotifAssignedTo(v); loadUserDailyCapacity(v, capRange, capStartFrom); }} />
                  </div>

                  {/* Daily capacity calendar */}
                  {(notifAssignedTo || expandedNotif.assigned_to) && userDailyCapacity.length > 0 && (
                    <div style={{ background: 'var(--bg-input)', borderRadius: 10, padding: 10, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Click a day to set planned date</div>
                        <select value={capRange} onChange={e => { const w = parseInt(e.target.value); setCapRange(w); loadUserDailyCapacity(notifAssignedTo || expandedNotif.assigned_to, w, capStartFrom); }}
                          style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 11 }}>
                          <option value={2}>2 weeks</option><option value={4}>1 month</option>
                          <option value={8}>2 months</option><option value={13}>3 months</option><option value={26}>6 months</option><option value={52}>1 year</option><option value={104}>2 years</option><option value={260}>5 years</option>
                        </select>
                      </div>
                      {(() => {
                        const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                        const fmtH = v => { const snapped = Math.round(v * 4) / 4; const h = Math.floor(snapped); const m = Math.round((snapped - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };
                        const jobHours = (notifEstHours || 0) + (notifEstMinutes || 0) / 60;
                        const selectedDate = notifPlannedDate;
                        const selectedWeekIdx = selectedDate ? userDailyCapacity.findIndex(d => d.date === selectedDate) : -1;
                        const scrollToWeek = selectedWeekIdx >= 0 ? Math.floor(selectedWeekIdx / 7) : -1;
                        return Array.from({ length: Math.ceil(userDailyCapacity.length / 7) }, (_, weekIdx) => {
                          const weekDays = userDailyCapacity.slice(weekIdx * 7, (weekIdx + 1) * 7);
                          if (weekDays.length === 0) return null;
                          const weekLabel = new Date(weekDays[0].date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                          return (
                            <div key={weekIdx} ref={weekIdx === scrollToWeek ? el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } : undefined} style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3 }}>{weekLabel}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weekDays.length}, 1fr)`, gap: 3 }}>
                                {weekDays.map(day => {
                                  const isToday = day.date === todayStr;
                                  const notifDueStr = (() => { if (expandedNotif?.due_date) return expandedNotif.due_date.slice(0, 10); try { const ops2 = typeof expandedNotif?.planned_operations === 'string' ? JSON.parse(expandedNotif.planned_operations) : expandedNotif?.planned_operations; if (Array.isArray(ops2) && ops2[0]?.planned_date) return ops2[0].planned_date.slice(0, 10); } catch {} return null; })();
                                  const isDueDate = notifDueStr && day.date === notifDueStr;
                                  const reqDate = notifDueStr;
                                  const isBeforeReq = reqDate && day.date < reqDate;
                                  const isOff = day.offDuty;
                                  const isBlocked = day.blocked;
                                  const pct = day.available > 0 ? Math.round((day.allocated / day.available) * 100) : (day.allocated > 0 ? 100 : 0);
                                  const isFull = day.available > 0 && day.allocated >= day.available;
                                  const isOver = day.available > 0 && day.allocated > day.available;
                                  const wouldExceed = day.available > 0 && (day.allocated + jobHours) > day.available;
                                  const isWarn = pct >= 75 && !isFull && day.available > 0;
                                  const hasWork = day.allocated >= 0.25 && !isFull && !isWarn;
                                  const isOverCap = isFull || wouldExceed;
                                  const isPush = notifPush;
                                  const offBlocked2 = isOff && !isPush;
                                  const dateBlocked = isBlocked && !isPush;
                                  const capBlocked = isOverCap && !isPush;
                                  const isPastDate = new Date(day.date + 'T00:00:00') < new Date(todayStr + 'T00:00:00');
                                  const isDisabled = isPastDate || capBlocked || dateBlocked || offBlocked2;
                                  const isSelected = notifPlannedDate === day.date;
                                  const selColor = isSelected && isOff ? '#f59e0b' : isSelected && isOverCap ? '#ef4444' : '#2563eb';
                                  const bg = isSelected ? selColor : isBlocked ? 'rgba(139,92,246,0.1)' : isOver ? 'rgba(153,27,27,0.85)' : isFull ? 'rgba(239,68,68,0.12)' : isDisabled ? (wouldExceed ? 'rgba(234,88,12,0.1)' : 'var(--bg-input)') : isDueDate ? 'rgba(16,185,129,0.1)' : isWarn ? 'rgba(234,88,12,0.08)' : hasWork ? 'rgba(14,165,233,0.1)' : isOff ? 'rgba(245,158,11,0.05)' : isToday ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)';
                                  const borderColor = isSelected ? selColor : isBlocked ? '#8b5cf6' : isOver ? '#991b1b' : isFull ? '#ef4444' : isDisabled ? (wouldExceed ? '#ea580c' : 'var(--border)') : isDueDate ? '#10b981' : isWarn ? '#ea580c' : hasWork ? '#0ea5e9' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--border)';
                                  const capText = `${fmtH(day.allocated)}/${fmtH(day.available)}`;
                                  return (
                                    <div key={day.date} onClick={() => {
                                        if (!isDisabled) {
                                          setNotifPlannedDate(day.date);
                                          // Save date + current hours into the active operation's schedule
                                          setNotifOpSchedules(prev => ({ ...prev, [notifActiveOp]: { date: day.date, hours: notifEstHours, minutes: notifEstMinutes } }));
                                        }
                                      }}
                                      title={isDisabled ? `${day.day} ${day.date}\n${dateBlocked ? `BLOCKED (${day.blockName || 'Block'}) - only Push work allowed` : isOver ? 'OVER CAPACITY' : isFull ? 'FULLY BOOKED' : wouldExceed ? `Would exceed capacity (${fmtH(day.allocated)}+${fmtH(jobHours)} > ${fmtH(day.available)})` : isBeforeReq ? 'Before requested date' : 'Past date'}` : `${day.day} ${day.date}${isDueDate ? '\nREQUESTED DATE' : ''}${isBlocked ? `\nBLOCKED (${day.blockName || 'Block'})` : isOff ? '\nOFF DUTY' : ''}\n${fmtH(day.allocated)} booked / ${fmtH(day.available)} capacity\n${day.jobs?.map(j => `${j.wo}: ${j.name} (${fmtH(j.hours)})`).join('\n') || 'No jobs'}`}
                                      style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'center', cursor: isDisabled ? 'not-allowed' : 'pointer', border: `${(isToday || isDueDate) && !isSelected ? '2px' : '1px'} solid ${borderColor}`, background: bg, opacity: isDisabled ? ((isOff || isBlocked) ? 0.65 : 0.35) : 1, transition: 'all 0.15s' }}>
                                      <div style={{ fontSize: 10, fontWeight: 600, color: isSelected ? '#fff' : isBlocked ? '#8b5cf6' : isDueDate ? '#10b981' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--text-muted)' }}>{isDueDate && !isSelected ? 'DUE' : day.day}</div>
                                      <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? '#fff' : isDueDate ? '#10b981' : isToday ? '#2563eb' : 'var(--text-primary)' }}>{new Date(day.date + 'T00:00:00').getDate()}</div>
                                      <div style={{ fontSize: 9, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.8)' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : isDisabled ? 'var(--text-muted)' : isOver ? '#fecaca' : isFull ? '#ef4444' : isWarn ? '#ea580c' : hasWork ? '#0ea5e9' : '#16a34a' }}>{isBlocked && !isSelected ? (day.blockName || 'BLK') : isOff && !isSelected ? 'OFF' : capText}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  {notifPlannedDate && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      Planned Date: <strong style={{ color: 'var(--text-primary)' }}>{new Date(notifPlannedDate + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Priority</label>
                    <div style={{ display: 'flex', gap: 2, background: 'var(--bg-input)', borderRadius: 6, padding: 2 }}>
                      {['Normal', 'Push'].map(p => (
                        <button key={p} type="button"
                          onClick={() => setNotifPush(p === 'Push')}
                          style={{ padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
                            background: (notifPush ? 'Push' : 'Normal') === p ? (p === 'Push' ? '#8b5cf6' : '#2563eb') : 'transparent',
                            color: (notifPush ? 'Push' : 'Normal') === p ? '#fff' : 'var(--text-muted)' }}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Estimated Time *</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select value={notifEstHours} onChange={e => { const h = parseInt(e.target.value); setNotifEstHours(h); setNotifOpSchedules(prev => ({ ...prev, [notifActiveOp]: { ...(prev[notifActiveOp] || {}), hours: h } })); if (notifPlannedDate && userDailyCapacity.length > 0) { const d = userDailyCapacity.find(d => d.date === notifPlannedDate); if (d && d.available > 0 && (d.allocated + h + notifEstMinutes / 60) > d.available && !notifPush) setNotifPlannedDate(''); } }}
                        style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 }}>
                        {[...Array(13)].map((_, i) => <option key={i} value={i}>{i} hour{i !== 1 ? 's' : ''}</option>)}
                      </select>
                      <select value={notifEstMinutes} onChange={e => { const m = parseInt(e.target.value); setNotifEstMinutes(m); setNotifOpSchedules(prev => ({ ...prev, [notifActiveOp]: { ...(prev[notifActiveOp] || {}), minutes: m } })); if (notifPlannedDate && userDailyCapacity.length > 0) { const d = userDailyCapacity.find(d => d.date === notifPlannedDate); if (d && d.available > 0 && (d.allocated + notifEstHours + m / 60) > d.available && !notifPush) setNotifPlannedDate(''); } }}
                        style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 }}>
                        {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m} minute{m !== 1 ? 's' : ''}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Capacity warning */}
                  {notifPlannedDate && userDailyCapacity.length > 0 && (() => {
                    const day = userDailyCapacity.find(d => d.date === notifPlannedDate);
                    const jobH = notifEstHours + notifEstMinutes / 60;
                    const freeH = day ? Math.max(0, day.available - day.allocated) : 0;
                    if (day && jobH > freeH && freeH > 0 && !notifPush) {
                      return (
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', fontSize: 12, color: '#d97706', lineHeight: 1.5 }}>
                          <strong>Capacity notice:</strong> This operation needs <strong>{jobH.toFixed(1)}h</strong> but only <strong>{freeH.toFixed(1)}h</strong> is free on {new Date(notifPlannedDate + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' })}.
                          Select a day with enough capacity, or use <strong>Push</strong> priority to override.
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Action buttons */}
                  {(() => {
                    const totalMins = (notifEstHours * 60) + notifEstMinutes;
                    // For multi-op: all ops must have dates. For single-op: use notifPlannedDate
                    const notifOps = getApprovalOps(expandedNotif);
                    const opCount = notifOps ? notifOps.length : 1;
                    const allOpsScheduled = opCount <= 1 ? !!notifPlannedDate : Object.keys(notifOpSchedules).filter(k => notifOpSchedules[k]?.date).length >= opCount;
                    const notifApproveDisabled = !(notifAssignedTo || expandedNotif.assigned_to) || !allOpsScheduled || totalMins < 15;
                    return (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                        <motion.button whileTap={{ scale: 0.97 }} onClick={() => { setRejectTarget({ type: 'notification', id: expandedNotif.id, title: expandedNotif.name }); setExpandedNotif(null); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 8, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                          <FiX size={14} /> {expandedNotif.type === 'PM02' ? 'Delete' : 'Reject'}
                        </motion.button>
                        {expandedNotif.type !== 'PM02' && expandedNotif.status !== 'Info Required' && (
                        <motion.button whileTap={{ scale: 0.97 }}
                          onClick={() => { setInfTarget({ type: 'notification', id: expandedNotif.id, title: expandedNotif.name }); setExpandedNotif(null); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 8, border: '1px solid #f59e0b', background: 'transparent', color: '#f59e0b', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                          <FiInfo size={14} /> INF Req
                        </motion.button>
                        )}
                        <div style={{ flex: 1 }} />
                        <motion.button whileTap={{ scale: 0.97 }}
                          onClick={() => { handleApproveNotif(expandedNotif.id, notifAssignedTo || expandedNotif.assigned_to, notifPlannedDate, notifEstHours, notifEstMinutes, notifPush); setExpandedNotif(null); }}
                          disabled={notifApproveDisabled}
                          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 8, border: 'none', background: notifApproveDisabled ? '#94a3b8' : '#16a34a', color: '#fff', cursor: notifApproveDisabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: notifApproveDisabled ? 0.6 : 1 }}>
                          <FiCheck size={14} /> Approve
                        </motion.button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          ) : (notifications.length === 0 && pendingOps.length === 0) ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <FiBell size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 15 }}>No pending approvals</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Notifications and operations awaiting approval will appear here</div>
            </div>
          ) : (
            <div>
              {/* New Notifications Section */}
              {notifications.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiBell size={14} /> New Notifications
                    <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{notifications.length}</span>
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {notifications.map(n => {
                      const notifOps = getApprovalOps(n);
                      const opCount = notifOps ? notifOps.length : 1;
                      return (
                      <motion.div key={`notif-${n.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        onClick={() => {
                          playClick();
                          setNotifAssignedTo('');
                          setNotifPush(n.priority === 'Push');
                          let estH = parseFloat(n.estimated_hours) || 0;
                          if (estH === 0 && n.planned_operations) {
                            try { const ops2 = typeof n.planned_operations === 'string' ? JSON.parse(n.planned_operations) : n.planned_operations; if (Array.isArray(ops2) && ops2.length > 0) estH = parseFloat(ops2[0].estimated_hours) || 0; } catch (e) {}
                          }
                          setNotifEstHours(Math.floor(estH));
                          const rawMins = Math.round((estH % 1) * 60);
                          setNotifEstMinutes([0, 15, 30, 45].reduce((prev, curr) => Math.abs(curr - rawMins) < Math.abs(prev - rawMins) ? curr : prev));
                          let defDate = n.due_date ? n.due_date.slice(0, 10) : '';
                          if (!defDate && n.planned_operations) { try { const ops2 = typeof n.planned_operations === 'string' ? JSON.parse(n.planned_operations) : n.planned_operations; if (Array.isArray(ops2) && ops2.length > 0 && ops2[0].planned_date) defDate = ops2[0].planned_date.slice(0, 10); } catch (e) {} }
                          setNotifPlannedDate(defDate);
                          let notifWeeks = 4; let notifSF = null;
                          if (defDate) {
                            const planned = new Date(defDate + 'T00:00:00');
                            const today = new Date(); today.setHours(0,0,0,0);
                            const twoWeeksBefore = new Date(planned); twoWeeksBefore.setDate(planned.getDate() - 14);
                            notifSF = twoWeeksBefore < today ? today.toISOString().split('T')[0] : twoWeeksBefore.toISOString().split('T')[0];
                            notifWeeks = Math.max(Math.ceil((planned - new Date(notifSF + 'T00:00:00')) / (7 * 86400000)) + 3, 4);
                          }
                          setCapRange(notifWeeks); setCapStartFrom(notifSF);
                          setExpandedNotif(n);
                          if (n.assigned_to) loadUserDailyCapacity(n.assigned_to, notifWeeks, notifSF);
                          else setUserDailyCapacity([]);
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-input)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; }}
                        style={{
                          background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
                          padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.2s',
                          cursor: 'pointer'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 90 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{n.notif_number}</span>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[n.type] || '#6b7280' }}>{n.type}</span>
                              {opCount > 1 && <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#6366f1' }}>{opCount} OPS</span>}
                              {n.status === 'Info Required' && <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#f59e0b' }}>INF</span>}
                              {n.info_response && <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#2563eb' }}>Response</span>}
                              {n.priority === 'Push' && <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: '#8b5cf6' }}>PUSH</span>}
                            </div>
                          </div>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{n.name}</div>
                            {n.description && (
                              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, lineHeight: 1.4,
                                maxHeight: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.description}</div>
                            )}
                            {/* Show operation numbers inline */}
                            {Array.isArray(notifOps) && notifOps.length > 0 && (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                                {notifOps.map((op, idx) => (
                                  <span key={idx} style={{ padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, background: 'var(--bg-input)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontFamily: 'monospace' }}>
                                    {op.operation_number || String((idx + 1) * 10).padStart(4, '0')} — {op.title || op.name || n.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
                              {n.asset_name && <span>Asset: <strong>{n.asset_tag || ''}</strong> {n.asset_name}</span>}
                              {n.priority && <span>Priority: <strong style={{ color: priorityColors[n.priority] || '#6b7280' }}>{n.priority}</strong></span>}
                              {n.due_date && <span>Due: {formatDate(n.due_date)}</span>}
                              <span>Created: {formatDate(n.created_at)}</span>
                              {n.created_by_name && <span>By: {n.created_by_name}</span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <FiArrowRight size={16} color="var(--text-muted)" />
                          </div>
                        </div>
                      </motion.div>
                    ); })}
                  </div>
                </div>
              )}

              {/* Pending Operation Approvals Section */}
              {pendingOps.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiClock size={14} /> Operation Approvals
                    <span style={{ background: '#8b5cf6', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{pendingOps.length}</span>
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {pendingOps.map(op => (
                      <motion.div key={`op-${op.id}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        onMouseEnter={e => { if (expandedOp?.id !== op.id) e.currentTarget.style.background = 'var(--bg-input)'; }}
                        onMouseLeave={e => { if (expandedOp?.id !== op.id) e.currentTarget.style.background = 'var(--bg-card)'; }}
                        style={{
                          background: expandedOp?.id === op.id ? 'rgba(139,92,246,0.06)' : 'var(--bg-card)', borderRadius: 12, border: `1px solid ${expandedOp?.id === op.id ? 'rgba(139,92,246,0.5)' : 'rgba(139,92,246,0.25)'}`,
                          borderLeft: '4px solid #8b5cf6', padding: '16px 20px', boxShadow: expandedOp?.id === op.id ? '0 0 0 2px rgba(139,92,246,0.12)' : '0 1px 3px rgba(0,0,0,0.04)', transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 90 }}>
                            <span onClick={() => { playClick(); navigate(`/work-orders/${op.wo_number}`); }} style={{ fontSize: 14, fontWeight: 700, color: '#2563eb', cursor: 'pointer', fontFamily: 'monospace', textDecoration: 'underline', textDecorationColor: 'transparent', transition: 'text-decoration-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.textDecorationColor = '#2563eb'} onMouseLeave={e => e.currentTarget.style.textDecorationColor = 'transparent'}>{op.wo_number} / {op.operation_number}</span>
                            <span style={{
                              padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                              color: '#fff', background: WO_TYPE_COLORS[op.wo_type] || '#6b7280', alignSelf: 'flex-start'
                            }}>{op.wo_type}</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{op.title}</div>
                            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>WO: {op.wo_name}</div>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
                              {op.asset_name && <span>Asset: <strong>{op.asset_tag || ''}</strong> {op.asset_name}</span>}
                              {op.estimated_hours > 0 && <span>Est: {Math.floor(op.estimated_hours)}h {Math.round((op.estimated_hours % 1) * 60)}m</span>}
                              {op.planned_date && <span>Planned: {formatDate(op.planned_date)}</span>}
                              <span>Created: {formatDate(op.created_at)}</span>
                              {op.created_by_name && <span>By: {op.created_by_name}</span>}
                            </div>
                            {op.approval_status === 'info_required' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 10px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>
                                <FiInfo size={12} /> Awaiting info from user
                              </div>
                            )}
                            {op.info_response && op.approval_status === 'pending' && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, padding: '6px 10px', borderRadius: 8, background: 'rgba(37,99,235,0.08)', border: '1px solid rgba(37,99,235,0.25)', fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                                <FiInfo size={12} /> User responded — ready for re-review
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                playClick();
                                setExpandedOp(op);
                                const hrs = Math.floor(op.estimated_hours || 0);
                                const mins = Math.round(((op.estimated_hours || 0) % 1) * 60);
                                const defDate = op.planned_date ? op.planned_date.slice(0, 10) : '';
                                setOpApprovalForm({ assigned_to: '', planned_date: defDate, est_hours: hrs, est_minutes: mins });
                                if (defDate) {
                                  const planned = new Date(defDate + 'T00:00:00');
                                  const today = new Date(); today.setHours(0,0,0,0);
                                  const twoWeeksBefore = new Date(planned);
                                  twoWeeksBefore.setDate(planned.getDate() - 14);
                                  const sf = twoWeeksBefore < today ? today.toISOString().split('T')[0] : twoWeeksBefore.toISOString().split('T')[0];
                                  const weeksFromStart = Math.ceil((planned - new Date(sf + 'T00:00:00')) / (7 * 86400000)) + 3;
                                  setCapRange(Math.max(weeksFromStart, 4));
                                  setCapStartFrom(sf);
                                } else {
                                  setCapRange(4);
                                  setCapStartFrom(null);
                                }
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                            ><FiMaximize2 size={14} /> Review</motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Old notification modal removed — now inline detail view */}

      {/* Operation Approval Modal */}
      <AnimatePresence>
        {expandedOp && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setExpandedOp(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, maxWidth: 600, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: '#8b5cf6' }}>Operation</span>
                    <span onClick={() => { playClick(); navigate(`/work-orders/${expandedOp.wo_number}`); setExpandedOp(null); }} style={{ fontSize: 16, fontWeight: 700, color: '#2563eb', fontFamily: 'monospace', cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'transparent', transition: 'text-decoration-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.textDecorationColor = '#2563eb'} onMouseLeave={e => e.currentTarget.style.textDecorationColor = 'transparent'}>{expandedOp.wo_number} / {expandedOp.operation_number}</span>
                  </div>
                </div>
                <button onClick={() => setExpandedOp(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
                <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{expandedOp.title}</h3>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Work Order: <strong>{expandedOp.wo_name}</strong></div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px', fontSize: 13, marginBottom: 20 }}>
                  {expandedOp.asset_name && (
                    <div><span style={{ color: 'var(--text-muted)' }}>Asset:</span> <strong>{expandedOp.asset_tag}</strong> {expandedOp.asset_name}</div>
                  )}
                  {expandedOp.created_by_name && <div><span style={{ color: 'var(--text-muted)' }}>Requested by:</span> {expandedOp.created_by_name}</div>}
                  <div><span style={{ color: 'var(--text-muted)' }}>Created:</span> {formatDate(expandedOp.created_at)}</div>
                </div>

                {expandedOp.info_response && (
                  <div style={{ padding: 14, borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.2)', marginBottom: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#2563eb', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiInfo size={12} /> User Response to Info Request
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{expandedOp.info_response}</div>
                    {expandedOp.info_required_message && (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                        <strong>Original request:</strong> {expandedOp.info_required_message}
                      </div>
                    )}
                  </div>
                )}

                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* Assign To */}
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Assign To</label>
                    <UserDropdown value={opApprovalForm.assigned_to} users={teamUsers} placeholder="Select team member..."
                      onChange={v => { setOpApprovalForm(f => ({ ...f, assigned_to: v })); loadUserDailyCapacity(v, capRange, capStartFrom); }} />
                  </div>

                  {/* Daily capacity calendar for selected user */}
                  {opApprovalForm.assigned_to && userDailyCapacity.length > 0 && (
                    <div style={{ background: 'var(--bg-input)', borderRadius: 10, padding: 10, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Click a day to set planned date</div>
                        <select value={capRange} onChange={e => { const w = parseInt(e.target.value); setCapRange(w); loadUserDailyCapacity(opApprovalForm.assigned_to, w, capStartFrom); }}
                          style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 11 }}>
                          <option value={2}>2 weeks</option><option value={4}>1 month</option>
                          <option value={8}>2 months</option><option value={13}>3 months</option><option value={26}>6 months</option><option value={52}>1 year</option><option value={104}>2 years</option><option value={260}>5 years</option>
                        </select>
                      </div>
                      {(() => {
                        const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                        const fmtH2 = v => { const snapped = Math.round(v * 4) / 4; const h = Math.floor(snapped); const m = Math.round((snapped - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };
                        const jobHours = (opApprovalForm.est_hours || 0) + (opApprovalForm.est_minutes || 0) / 60;
                        const selectedDate = opApprovalForm.planned_date;
                        const selectedWeekIdx = selectedDate ? userDailyCapacity.findIndex(d => d.date === selectedDate) : -1;
                        const scrollToWeek = selectedWeekIdx >= 0 ? Math.floor(selectedWeekIdx / 7) : -1;
                        return Array.from({ length: Math.ceil(userDailyCapacity.length / 7) }, (_, weekIdx) => {
                          const weekDays = userDailyCapacity.slice(weekIdx * 7, (weekIdx + 1) * 7);
                          if (weekDays.length === 0) return null;
                          const weekLabel = new Date(weekDays[0].date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                          return (
                            <div key={weekIdx} ref={weekIdx === scrollToWeek ? el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } : undefined} style={{ marginBottom: 6 }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3 }}>{weekLabel}</div>
                              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weekDays.length}, 1fr)`, gap: 3 }}>
                                {weekDays.map(day => {
                                  const isToday = day.date === todayStr;
                                  const isOff = day.offDuty;
                                  const isBlocked = day.blocked;
                                  const pct = day.available > 0 ? Math.round((day.allocated / day.available) * 100) : (day.allocated > 0 ? 100 : 0);
                                  const isFull = day.available > 0 && day.allocated >= day.available;
                                  const isOver = day.available > 0 && day.allocated > day.available;
                                  const wouldExceed = day.available > 0 && (day.allocated + jobHours) > day.available;
                                  const isWarn = pct >= 75 && !isFull && day.available > 0;
                                  const hasWork = day.allocated >= 0.25 && !isFull && !isWarn;
                                  const isOverCap = isFull || wouldExceed;
                                  const isCriticalOp = expandedOp?.priority === 'Push';
                                  const blockBlocked = isBlocked && !isCriticalOp;
                                  const isDisabled = day.date < todayStr || isOverCap || blockBlocked;
                                  const isSelected = opApprovalForm.planned_date === day.date;
                                  const selColor2 = isSelected && isOff ? '#f59e0b' : isSelected && isOverCap ? '#ef4444' : '#2563eb';
                                  const bg = isSelected ? selColor2 : isBlocked ? 'rgba(139,92,246,0.1)' : isOver ? 'rgba(153,27,27,0.85)' : isFull ? 'rgba(239,68,68,0.12)' : isDisabled ? (wouldExceed ? 'rgba(245,158,11,0.1)' : 'var(--bg-input)') : isWarn ? 'rgba(245,158,11,0.08)' : hasWork ? 'rgba(14,165,233,0.1)' : isOff ? 'rgba(245,158,11,0.05)' : isToday ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)';
                                  const borderColor = isSelected ? selColor2 : isBlocked ? '#8b5cf6' : isOver ? '#991b1b' : isFull ? '#ef4444' : isDisabled ? (wouldExceed ? '#f59e0b' : 'var(--border)') : isWarn ? '#f59e0b' : hasWork ? '#0ea5e9' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--border)';
                                  const capText = `${fmtH2(day.allocated)}/${fmtH2(day.available)}`;
                                  return (
                                    <div key={day.date} onClick={() => { if (!isDisabled) setOpApprovalForm(f => ({ ...f, planned_date: day.date })); }}
                                      title={isDisabled ? `${day.day} ${day.date}\n${blockBlocked ? `BLOCKED (${day.blockName || 'Block'}) - only Push work allowed` : isOver ? 'OVER CAPACITY' : isFull ? 'FULLY BOOKED' : wouldExceed ? `Would exceed capacity (${fmtH2(day.allocated)}+${fmtH2(jobHours)} > ${fmtH2(day.available)})` : 'Past date'}` : `${day.day} ${day.date}${isBlocked ? `\nBLOCKED (${day.blockName || 'Block'})` : isOff ? '\nOFF DUTY' : ''}\n${fmtH2(day.allocated)} booked / ${fmtH2(day.available)} capacity\n${day.jobs?.map(j => `${j.wo}: ${j.name} (${fmtH(j.hours)})`).join('\n') || 'No jobs'}`}
                                      style={{ padding: '2px 1px', borderRadius: 4, textAlign: 'center', cursor: isDisabled ? 'not-allowed' : 'pointer', border: `${isToday && !isSelected ? '2px' : '1px'} solid ${borderColor}`, background: bg, opacity: isDisabled ? ((isOff || isBlocked) ? 0.65 : 0.35) : 1, transition: 'all 0.15s' }}>
                                      <div style={{ fontSize: 8, fontWeight: 600, color: isSelected ? '#fff' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--text-muted)' }}>{day.day}</div>
                                      <div style={{ fontSize: 9, fontWeight: 700, color: isSelected ? '#fff' : isToday ? '#2563eb' : 'var(--text-primary)' }}>{new Date(day.date + 'T00:00:00').getDate()}</div>
                                      <div style={{ fontSize: 7, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.8)' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : isDisabled ? 'var(--text-muted)' : isOver ? '#fecaca' : isFull ? '#ef4444' : isWarn ? '#f59e0b' : hasWork ? '#0ea5e9' : '#16a34a' }}>{isBlocked && !isSelected ? (day.blockName || 'BLK') : isOff && !isSelected ? 'OFF' : capText}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  )}

                  {opApprovalForm.planned_date && (
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      Planned Date: <strong style={{ color: 'var(--text-primary)' }}>{new Date(opApprovalForm.planned_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                    </div>
                  )}

                  {/* Estimated Time */}
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Estimated Time</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <select value={opApprovalForm.est_hours}
                        onChange={e => { const h = parseInt(e.target.value); setOpApprovalForm(f => { if (f.planned_date && userDailyCapacity.length > 0) { const d = userDailyCapacity.find(d => d.date === f.planned_date); if (d && d.available > 0 && (d.allocated + h + f.est_minutes / 60) > d.available) return { ...f, est_hours: h, planned_date: '' }; } return { ...f, est_hours: h }; }); }}
                        style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
                        {[...Array(13)].map((_, i) => <option key={i} value={i}>{i} hour{i !== 1 ? 's' : ''}</option>)}
                      </select>
                      <select value={opApprovalForm.est_minutes}
                        onChange={e => { const m = parseInt(e.target.value); setOpApprovalForm(f => { if (f.planned_date && userDailyCapacity.length > 0) { const d = userDailyCapacity.find(d => d.date === f.planned_date); if (d && d.available > 0 && (d.allocated + f.est_hours + m / 60) > d.available) return { ...f, est_minutes: m, planned_date: '' }; } return { ...f, est_minutes: m }; }); }}
                        style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
                        {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m} minute{m !== 1 ? 's' : ''}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ padding: '12px 24px 16px', display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                <motion.button whileTap={{ scale: 0.97 }} onClick={() => { setRejectTarget({ type: 'operation', id: expandedOp.id, issue_id: expandedOp.issue_id, title: expandedOp.title }); setExpandedOp(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  <FiX size={14} /> Reject
                </motion.button>
                {expandedOp.approval_status !== 'info_required' && (
                <motion.button whileTap={{ scale: 0.97 }}
                  onClick={() => { setInfTarget({ type: 'operation', id: expandedOp.id, issue_id: expandedOp.issue_id, title: expandedOp.title }); setExpandedOp(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #f59e0b', background: 'transparent', color: '#f59e0b', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  <FiInfo size={14} /> INF Req
                </motion.button>
                )}
                <div style={{ flex: 1 }} />
                <motion.button whileTap={{ scale: 0.97 }} onClick={() => handleApproveOp(expandedOp)}
                  disabled={opApproveDisabled}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: opApproveDisabled ? '#94a3b8' : '#16a34a', color: '#fff', cursor: opApproveDisabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, opacity: opApproveDisabled ? 0.6 : 1 }}>
                  <FiCheck size={14} /> Approve
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INF (More Info Required) Modal */}
      <AnimatePresence>
        {infTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setInfTarget(null); setInfMessage(''); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, maxWidth: 500, width: '100%', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiInfo size={18} color="#f59e0b" />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>More Info Required</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{infTarget.title}</div>
                  </div>
                </div>
                <button onClick={() => { setInfTarget(null); setInfMessage(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Message to user — describe what information or changes are needed
                </label>
                <textarea
                  value={infMessage}
                  onChange={e => {
                    let val = e.target.value;
                    // Auto-capitalize first letter and after sentence endings
                    if (val.length === 1) val = val.toUpperCase();
                    else if (val.length > 1) {
                      const last2 = val.slice(-2);
                      if (/[.!?]\s$/.test(val.slice(-2)) || (val.length === 1)) {
                        // Don't modify - let the autoCapitalize handle on send
                      }
                      // Auto-capitalize after ". " or "! " or "? "
                      if (/[.!?]\s[a-z]$/.test(val.slice(-3))) {
                        val = val.slice(0, -1) + val.slice(-1).toUpperCase();
                      }
                    }
                    setInfMessage(val);
                  }}
                  placeholder="Please provide more details about..."
                  rows={4}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border)',
                    fontSize: 14, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)',
                    resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box', fontFamily: 'inherit'
                  }}
                />
              </div>
              <div style={{ padding: '12px 24px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
                <motion.button whileTap={{ scale: 0.97 }} onClick={() => { setInfTarget(null); setInfMessage(''); }}
                  style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                  Cancel
                </motion.button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={handleInfSend}
                  disabled={!infMessage.trim() || infSending}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8, border: 'none',
                    background: !infMessage.trim() ? '#94a3b8' : '#f59e0b', color: '#fff',
                    cursor: !infMessage.trim() || infSending ? 'not-allowed' : 'pointer',
                    fontSize: 14, fontWeight: 600, opacity: !infMessage.trim() ? 0.6 : 1
                  }}>
                  <FiInfo size={16} /> {infSending ? 'Sending...' : 'Send Info Request'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Reason Modal */}
      <AnimatePresence>
        {rejectTarget && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => { setRejectTarget(null); setRejectReason(''); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, maxWidth: 500, width: '100%', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FiX size={18} color="#ef4444" />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Reject {rejectTarget.type === 'operation' ? 'Operation' : 'Notification'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{rejectTarget.title}</div>
                  </div>
                </div>
                <button onClick={() => { setRejectTarget(null); setRejectReason(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Reason for rejection *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={e => {
                    let val = e.target.value;
                    if (val.length === 1) val = val.toUpperCase();
                    if (/[.!?]\s[a-z]$/.test(val.slice(-3))) {
                      val = val.slice(0, -1) + val.slice(-1).toUpperCase();
                    }
                    setRejectReason(val);
                  }}
                  placeholder="Enter reason for rejecting..."
                  rows={4}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border)',
                    fontSize: 14, outline: 'none', background: 'var(--bg-input)', color: 'var(--text-primary)',
                    resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box', fontFamily: 'inherit'
                  }}
                />
              </div>
              <div style={{ padding: '12px 24px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid var(--border)' }}>
                <motion.button whileTap={{ scale: 0.97 }} onClick={() => { setRejectTarget(null); setRejectReason(''); }}
                  style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                  Cancel
                </motion.button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={handleRejectConfirm}
                  disabled={!rejectReason.trim() || rejectSending}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8, border: 'none',
                    background: !rejectReason.trim() ? '#94a3b8' : '#ef4444', color: '#fff',
                    cursor: !rejectReason.trim() || rejectSending ? 'not-allowed' : 'pointer',
                    fontSize: 14, fontWeight: 600, opacity: !rejectReason.trim() ? 0.6 : 1
                  }}>
                  <FiX size={16} /> {rejectSending ? 'Rejecting...' : 'Reject'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unassigned Jobs Tab */}
      {tab === 'unassigned' && (
        <div>
          {assignTarget ? (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {/* Header row: back + view WO */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <button onClick={() => setAssignTarget(null)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  <FiArrowRight size={14} style={{ transform: 'rotate(180deg)' }} /> Back
                </button>
                <button onClick={() => navigate(`/work-orders/${assignTarget.wo_number}`)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  View WO <FiArrowRight size={14} />
                </button>
              </div>
              {/* WO Info */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: '20px 24px', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{assignTarget.wo_number}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[assignTarget.type] || '#6b7280' }}>{assignTarget.type}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, color: priorityColors[assignTarget.priority] || '#6b7280', border: `1px solid ${priorityColors[assignTarget.priority] || '#6b7280'}` }}>{assignTarget.priority}</span>
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{assignTarget.name}</div>
                {assignTarget.asset_name && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>Asset: <strong>{assignTarget.asset_tag}</strong> {assignTarget.asset_name}</div>}
                {assignTarget.description && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>{assignTarget.description}</div>}
                {assignTarget.due_date && <div style={{ fontSize: 12, color: new Date(assignTarget.due_date) < new Date() ? '#ef4444' : 'var(--text-muted)' }}>Due: {new Date(assignTarget.due_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</div>}
                {/* Operations list */}
                {assignTarget.operations?.length > 0 && (
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Operations</div>
                    {assignTarget.operations.map(op => (
                      <div key={op.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>{op.operation_number}</span>
                        <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)' }}>{op.title}</span>
                        {op.estimated_hours > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{op.estimated_hours}h</span>}
                        {op.assigned_to_name
                          ? <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>{op.assigned_to_name}</span>
                          : <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>Unassigned</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Assign form - per operation */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)', padding: '20px 24px' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 12 }}>Assign Operations</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {(assignTarget.operations || [])
                    .filter(op => !op.assigned_to_name && !op.has_assignees && op.status !== 'Complete' && op.status !== 'Confirmed')
                    .map(op => {
                      const form = opForms[op.id] || { assigned_to: '', planned_date: '', est_hours: 0, est_minutes: 0 };
                      const isActive = activeOpId === op.id;
                      return (
                        <div key={op.id} style={{ background: 'var(--bg-input)', borderRadius: 10, border: `1px solid ${isActive ? '#2563eb' : 'var(--border)'}`, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {/* Op header */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', minWidth: 28 }}>{op.operation_number}</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{op.title}</span>
                            {op.estimated_hours > 0 && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{op.estimated_hours}h est</span>}
                          </div>
                          {/* Assign to */}
                          <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>Assign To *</label>
                            <UserDropdown value={form.assigned_to} users={teamUsers} placeholder="Select team member..."
                              onChange={v => {
                                setOpForms(prev => ({ ...prev, [op.id]: { ...prev[op.id], assigned_to: v } }));
                                setActiveOpId(op.id);
                                loadAssignCapacity(v, assignCapRange, assignCapStartFrom);
                              }} />
                          </div>
                          {/* Capacity calendar for active op */}
                          {isActive && form.assigned_to && assignCapacity.length > 0 && (
                            <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 10, border: '1px solid var(--border)' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Click a day to set planned date</div>
                                <select value={assignCapRange} onChange={e => { const w = parseInt(e.target.value); setAssignCapRange(w); loadAssignCapacity(form.assigned_to, w, assignCapStartFrom); }}
                                  style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11 }}>
                                  <option value={2}>2 weeks</option><option value={4}>1 month</option>
                                  <option value={8}>2 months</option><option value={13}>3 months</option><option value={26}>6 months</option><option value={52}>1 year</option><option value={104}>2 years</option><option value={260}>5 years</option>
                                </select>
                              </div>
                              {(() => {
                                const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                                const fmtH = v => { const snapped = Math.round(v * 4) / 4; const h = Math.floor(snapped); const m = Math.round((snapped - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };
                                const jobHours = form.est_hours + form.est_minutes / 60;
                                const dueStr = assignTarget.due_date ? assignTarget.due_date.slice(0, 10) : null;
                                const selectedDate = form.planned_date;
                                const selectedWeekIdx = selectedDate ? assignCapacity.findIndex(d => d.date === selectedDate) : -1;
                                const scrollToWeek = selectedWeekIdx >= 0 ? Math.floor(selectedWeekIdx / 7) : -1;
                                return Array.from({ length: Math.ceil(assignCapacity.length / 7) }, (_, weekIdx) => {
                                  const weekDays = assignCapacity.slice(weekIdx * 7, (weekIdx + 1) * 7);
                                  if (weekDays.length === 0) return null;
                                  const weekLabel = new Date(weekDays[0].date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                                  return (
                                    <div key={weekIdx} ref={weekIdx === scrollToWeek ? el => { if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } : undefined} style={{ marginBottom: 6 }}>
                                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3 }}>{weekLabel}</div>
                                      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weekDays.length}, 1fr)`, gap: 3 }}>
                                        {weekDays.map(day => {
                                          const isToday = day.date === todayStr;
                                          const isDue = dueStr && day.date === dueStr;
                                          const isOff = day.offDuty;
                                          const isBlocked = day.blocked;
                                          const pct = day.available > 0 ? Math.round((day.allocated / day.available) * 100) : (day.allocated > 0 ? 100 : 0);
                                          const isFull = day.available > 0 && day.allocated >= day.available;
                                          const wouldExceed = day.available > 0 && (day.allocated + jobHours) > day.available;
                                          const isWarn = pct >= 75 && !isFull && day.available > 0;
                                          const hasWork = day.allocated >= 0.25 && !isFull && !isWarn;
                                          const isOverCap = isFull || wouldExceed;
                                          const isDisabled = day.date < todayStr || isOverCap || isBlocked;
                                          const isSelected = form.planned_date === day.date;
                                          const selColor = isSelected && isOff ? '#f59e0b' : isSelected && isOverCap ? '#ef4444' : '#2563eb';
                                          const bg = isSelected ? selColor : isBlocked ? 'rgba(139,92,246,0.1)' : isFull ? 'rgba(239,68,68,0.12)' : isDisabled ? (wouldExceed ? 'rgba(234,88,12,0.1)' : 'var(--bg-input)') : isDue ? 'rgba(16,185,129,0.1)' : isWarn ? 'rgba(234,88,12,0.08)' : hasWork ? 'rgba(14,165,233,0.1)' : isOff ? 'rgba(245,158,11,0.05)' : isToday ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)';
                                          const borderColor = isSelected ? selColor : isBlocked ? '#8b5cf6' : isFull ? '#ef4444' : isDisabled ? (wouldExceed ? '#ea580c' : 'var(--border)') : isDue ? '#10b981' : isWarn ? '#ea580c' : hasWork ? '#0ea5e9' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--border)';
                                          const capText = `${fmtH(day.allocated)}/${fmtH(day.available)}`;
                                          return (
                                            <div key={day.date} onClick={() => { if (!isDisabled) setOpForms(prev => ({ ...prev, [op.id]: { ...prev[op.id], planned_date: day.date } })); }}
                                              title={isDisabled ? `${day.day} ${day.date}\n${isBlocked ? `BLOCKED (${day.blockName||'Block'})` : isFull ? 'FULL' : wouldExceed ? `Would exceed capacity` : 'Past date'}` : `${day.day} ${day.date}${isDue ? '\nWO DUE DATE' : ''}${isBlocked ? `\nBLOCKED` : isOff ? '\nOFF DUTY' : ''}\n${fmtH(day.allocated)} booked / ${fmtH(day.available)} capacity\n${day.jobs?.map(j => `${j.wo}: ${j.name} (${fmtH(j.hours)})`).join('\n') || 'No jobs'}`}
                                              style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'center', cursor: isDisabled ? 'not-allowed' : 'pointer', border: `${(isToday || isDue) && !isSelected ? '2px' : '1px'} solid ${borderColor}`, background: bg, opacity: isDisabled ? ((isOff || isBlocked) ? 0.65 : 0.35) : 1, transition: 'all 0.15s' }}>
                                              <div style={{ fontSize: 10, fontWeight: 600, color: isSelected ? '#fff' : isBlocked ? '#8b5cf6' : isDue ? '#10b981' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--text-muted)' }}>{isDue && !isSelected ? 'DUE' : day.day}</div>
                                              <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? '#fff' : isDue ? '#10b981' : isToday ? '#2563eb' : 'var(--text-primary)' }}>{new Date(day.date + 'T00:00:00').getDate()}</div>
                                              <div style={{ fontSize: 9, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.8)' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : isDisabled ? 'var(--text-muted)' : isFull ? '#ef4444' : isWarn ? '#ea580c' : hasWork ? '#0ea5e9' : '#16a34a' }}>{isBlocked && !isSelected ? (day.blockName || 'BLK') : isOff && !isSelected ? 'OFF' : capText}</div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          )}
                          {/* Planned date display */}
                          {form.planned_date && (
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                              Planned: <strong style={{ color: 'var(--text-primary)' }}>{new Date(form.planned_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                            </div>
                          )}
                          {/* Estimated time */}
                          <div>
                            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>Estimated Time</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <select value={form.est_hours} onChange={e => setOpForms(prev => ({ ...prev, [op.id]: { ...prev[op.id], est_hours: parseInt(e.target.value) } }))}
                                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
                                {[...Array(13)].map((_, i) => <option key={i} value={i}>{i} hour{i !== 1 ? 's' : ''}</option>)}
                              </select>
                              <select value={form.est_minutes} onChange={e => setOpForms(prev => ({ ...prev, [op.id]: { ...prev[op.id], est_minutes: parseInt(e.target.value) } }))}
                                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
                                {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m} min</option>)}
                              </select>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  }
                  {/* Save button */}
                  {(() => {
                    const canSave = Object.values(opForms).some(f => f.assigned_to && f.planned_date);
                    return (
                      <div style={{ paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                        <motion.button whileTap={{ scale: 0.97 }} onClick={handleAssignJob}
                          disabled={!canSave || assignSaving}
                          style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 700, cursor: (!canSave || assignSaving) ? 'not-allowed' : 'pointer', background: (!canSave || assignSaving) ? '#94a3b8' : '#16a34a', color: '#fff', opacity: (!canSave || assignSaving) ? 0.6 : 1 }}>
                          {assignSaving ? 'Assigning...' : `Save Assignment${Object.values(opForms).filter(f => f.assigned_to && f.planned_date).length > 1 ? 's' : ''}`}
                        </motion.button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                  <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input value={unassignedSearch} onChange={e => setUnassignedSearch(e.target.value)} placeholder="Search work orders..."
                    style={{ width: '100%', padding: '9px 12px 9px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <button onClick={loadUnassigned} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                  <FiRefreshCw size={14} /> Refresh
                </button>
                <span style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{unassignedJobs.length} WO{unassignedJobs.length !== 1 ? 's' : ''}</span>
              </div>
              {unassignedLoading ? (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}><FiClock size={32} style={{ opacity: 0.3 }} /><div style={{ marginTop: 8 }}>Loading...</div></div>
              ) : unassignedJobs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
                  <FiUserX size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                  <div style={{ fontSize: 15 }}>No unassigned operations</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>All active operations have an assigned user</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {unassignedJobs.filter(wo => !unassignedSearch || wo.wo_number?.toLowerCase().includes(unassignedSearch.toLowerCase()) || wo.name?.toLowerCase().includes(unassignedSearch.toLowerCase()) || wo.asset_name?.toLowerCase().includes(unassignedSearch.toLowerCase()) || wo.asset_tag?.toLowerCase().includes(unassignedSearch.toLowerCase())).map(wo => {
                    const unassignedCount = (wo.operations || []).filter(op => !op.assigned_to_name && !op.has_assignees && op.status !== 'Complete' && op.status !== 'Confirmed').length;
                    return (
                    <motion.div key={wo.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                      onClick={() => { playClick(); openAssignTarget(wo); }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-input)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
                      style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', padding: '14px 18px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{wo.wo_number}</span>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[wo.type] || '#6b7280' }}>{wo.type}</span>
                            {wo.priority && <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: priorityColors[wo.priority] || '#6b7280', border: `1px solid ${priorityColors[wo.priority] || '#6b7280'}` }}>{wo.priority}</span>}
                          </div>
                        </div>
                        <div style={{ flex: 1, minWidth: 160 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 1 }}>{wo.name}</div>
                          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>
                            {wo.asset_name && <span><strong>{wo.asset_tag}</strong> {wo.asset_name}</span>}
                            {wo.due_date && <span style={{ color: new Date(wo.due_date) < new Date() ? '#ef4444' : 'var(--text-muted)' }}>Due: {new Date(wo.due_date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                          </div>
                          <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600 }}>{unassignedCount} unassigned op{unassignedCount !== 1 ? 's' : ''}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', flexShrink: 0 }}>
                          <FiArrowRight size={16} />
                        </div>
                      </div>
                    </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TECO Tab */}
      {tab === 'teco' && (() => {
        const filtered = tecoJobs
          .filter(j => {
            if (tecoTypeFilter !== 'all' && j.type !== tecoTypeFilter) return false;
            if (tecoFilter) {
              const q = tecoFilter.toLowerCase();
              return (j.wo_number || '').toLowerCase().includes(q) ||
                (j.name || '').toLowerCase().includes(q) ||
                (j.assigned_to_name || '').toLowerCase().includes(q) ||
                (j.asset_name || '').toLowerCase().includes(q);
            }
            return true;
          })
          .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
        const types = [...new Set(tecoJobs.map(j => j.type).filter(Boolean))];
        return (
        <div>
          {tecoJobs.length > 0 && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                <FiSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} size={14} />
                <input
                  value={tecoFilter} onChange={e => setTecoFilter(e.target.value)}
                  placeholder="Search by WO number, name, assigned to, asset..."
                  style={{ ...inputStyle, paddingLeft: 32, width: '100%' }}
                />
              </div>
              <select value={tecoTypeFilter} onChange={e => setTecoTypeFilter(e.target.value)}
                style={{ ...inputStyle, width: 'auto', minWidth: 100 }}>
                <option value="all">All Types</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} of {tecoJobs.length} jobs</span>
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <FiCheckSquare size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 15 }}>{tecoJobs.length === 0 ? 'No work orders pending TECO' : 'No matching jobs'}</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Jobs will appear here when all operations are final confirmed and materials are complete</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map((job, idx) => (
                <motion.div key={job.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px',
                    background: 'var(--bg-card)', borderRadius: 10,
                    border: '1px solid var(--border)', borderLeft: '4px solid #2563eb',
                    flexWrap: 'wrap'
                  }}>
                  <div style={{ flex: 1, minWidth: 180, cursor: 'pointer' }} onClick={() => { playClick(); navigate(`/work-orders/${job.wo_number}`); }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{job.wo_number}</span>
                      <span style={{
                        padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                        color: '#fff', background: WO_TYPE_COLORS[job.type] || '#6b7280'
                      }}>{job.type}</span>
                      <span style={{
                        padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        background: `${priorityColors[job.priority] || '#6b7280'}15`,
                        color: priorityColors[job.priority] || '#6b7280'
                      }}>{job.priority}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{job.name}</div>
                    {job.asset_name && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                        {job.asset_tag && <span style={{ fontWeight: 600 }}>{job.asset_tag}</span>}
                        {job.asset_tag && ' — '}{job.asset_name}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                      Assigned to: {job.assigned_to_name || 'Unassigned'}
                      {job.due_date && <> &middot; Due: {formatDate(job.due_date)}</>}
                      {job.created_at && <> &middot; Created: {formatDate(job.created_at)}</>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {job.was_reopened ? (
                      <span title="This work order was previously TECO'd and then reopened — TECO again to close it." style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        background: 'rgba(217,119,6,0.12)', color: '#d97706', border: '1px solid rgba(217,119,6,0.3)'
                      }}>REOPENED</span>
                    ) : (
                      <span style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        background: 'rgba(37,99,235,0.1)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.25)'
                      }}>PENDING TECO</span>
                    )}
                    <motion.button
                      whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                      onClick={() => handleTeco(job.id, job.wo_number)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                        borderRadius: 8, border: 'none', background: '#10b981', color: '#fff',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(16,185,129,0.3)'
                      }}>
                      <FiCheckSquare size={14} /> {job.was_reopened ? 'TECO again' : 'TECO'}
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
        );
      })()}

      {/* Awaiting Parts/Service Tab */}
      {tab === 'awaiting-parts' && (
        <div>
          {awaitingPartsJobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <FiPackage size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 15 }}>No jobs awaiting parts or service</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Jobs with incomplete parts, services, or labour will appear here</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                {awaitingPartsJobs.length} job{awaitingPartsJobs.length !== 1 ? 's' : ''} with pending materials — click to open work order
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {awaitingPartsJobs.map((job, idx) => {
                  const materials = Array.isArray(job.materials) ? job.materials : [];
                  const getMatChecks = (m) => {
                    if (m.material_type === 'labour') return [
                      { label: 'Contacted', done: !!m.contacted, date: m.contacted_date },
                      { label: 'Labour Complete', done: !!m.labour_complete, date: m.labour_complete_date },
                      { label: 'Paid', done: !!m.paid, date: m.paid_date },
                    ];
                    if (m.material_type === 'service') return [
                      { label: 'Invoice Received', done: !!m.ordered, date: m.ordered_date },
                      { label: 'Approved', done: !!m.delivered, date: m.delivered_date },
                      { label: 'Paid', done: !!m.installed, date: m.installed_date },
                    ];
                    return [
                      { label: 'Ordered', done: !!m.ordered, date: m.ordered_date },
                      { label: 'Delivered', done: !!m.delivered, date: m.delivered_date },
                      { label: 'Installed', done: !!m.installed, date: m.installed_date },
                    ];
                  };
                  return (
                    <motion.div key={job.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                      style={{
                        background: 'var(--bg-card)', borderRadius: 10,
                        border: '1px solid var(--border)', borderLeft: '4px solid #f59e0b',
                        overflow: 'hidden'
                      }}>
                      {/* Job header — clickable */}
                      <div onClick={() => { playClick(); navigate(`/work-orders/${job.wo_number}`); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', cursor: 'pointer', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{job.wo_number}</span>
                            <span style={{
                              padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                              color: '#fff', background: WO_TYPE_COLORS[job.type] || '#6b7280'
                            }}>{job.type}</span>
                            <span style={{
                              padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                              background: `${priorityColors[job.priority] || '#6b7280'}15`,
                              color: priorityColors[job.priority] || '#6b7280'
                            }}>{job.priority}</span>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{job.name}</div>
                          {job.asset_name && (
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                              {job.asset_tag && <span style={{ fontWeight: 600 }}>{job.asset_tag}</span>}
                              {job.asset_tag && ' — '}{job.asset_name}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                            Assigned to: {job.assigned_to_name || 'Unassigned'}
                            {job.next_operation_date && <> &middot; Next Op: <strong>{formatDate(job.next_operation_date)}</strong></>}
                            {!job.next_operation_date && job.due_date && <> &middot; Due: {formatDate(job.due_date)}</>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {job.pending_materials_count > 0 && (
                            <span style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              background: 'rgba(245,158,11,0.12)', color: '#d97706', border: '1px solid rgba(245,158,11,0.3)'
                            }}>
                              <FiPackage size={12} />
                              {job.pending_materials_count} outstanding
                            </span>
                          )}
                          {materials.length > 0 && materials.length - job.pending_materials_count > 0 && (
                            <span style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                              background: 'rgba(22,163,74,0.1)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.25)'
                            }}>
                              <FiCheck size={12} />
                              {materials.length - job.pending_materials_count} complete
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Materials breakdown */}
                      {materials.length > 0 && (
                        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 18px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {materials.map(m => {
                            const checks = getMatChecks(m);
                            const allDone = checks.every(c => c.done);
                            const typeLabel = m.material_type === 'labour' ? 'Labour' : m.material_type === 'service' ? 'Service' : 'Part';
                            const typeColor = m.material_type === 'labour' ? '#7c3aed' : m.material_type === 'service' ? '#0284c7' : '#374151';
                            return (
                              <div key={m.id} style={{
                                display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
                                padding: '8px 10px', borderRadius: 8,
                                background: allDone ? 'rgba(22,163,74,0.05)' : 'rgba(245,158,11,0.05)',
                                border: `1px solid ${allDone ? 'rgba(22,163,74,0.2)' : 'rgba(245,158,11,0.2)'}`
                              }}>
                                <div style={{ flex: '0 0 auto', minWidth: 140 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: `${typeColor}18`, color: typeColor }}>{typeLabel}</span>
                                    {allDone && <FiCheck size={12} color="#16a34a" />}
                                  </div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{m.part_name}</div>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 }}>
                                  {checks.map(c => (
                                    <span key={c.label} style={{
                                      display: 'flex', alignItems: 'center', gap: 4,
                                      padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                      background: c.done ? 'rgba(22,163,74,0.1)' : 'rgba(107,114,128,0.08)',
                                      color: c.done ? '#16a34a' : '#9ca3af',
                                      border: `1px solid ${c.done ? 'rgba(22,163,74,0.25)' : 'rgba(107,114,128,0.15)'}`
                                    }}>
                                      {c.done ? <FiCheck size={10} /> : <FiX size={10} />}
                                      {c.label}
                                      {c.done && c.date && (
                                        <span style={{ fontWeight: 500, opacity: 0.85 }}>
                                          {' '}{formatDate(c.date)}
                                        </span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reschedules Tab */}
      {/* Overdue Operations Tab */}
      {tab === 'overdue' && (
        <div>
          {overdueJobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <FiAlertTriangle size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 15 }}>No overdue operations</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Operations past their planned date without time confirmation will appear here</div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                {overdueJobs.length} operation{overdueJobs.length !== 1 ? 's' : ''} overdue — click to open work order
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {overdueJobs.map((op, idx) => {
                  const days = op.planned_date ? Math.ceil((new Date() - new Date(op.planned_date)) / 86400000) : 0;
                  return (
                    <motion.div key={op.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.02 }}
                      onClick={() => { playClick(); navigate(`/work-orders/${op.wo_number}`); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                        background: 'var(--bg-card)', borderRadius: 8, cursor: 'pointer',
                        border: '1px solid rgba(239,68,68,0.3)', borderLeft: '3px solid #ef4444',
                        flexWrap: 'wrap', transition: 'all 0.15s'
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 130, flexShrink: 0 }}>
                        <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[op.wo_type] || '#6b7280' }}>{op.wo_type}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{op.wo_number}/{op.operation_number}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.wo_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {op.title && <><span>{op.title}</span> · </>}
                          {op.asset_name && <>{op.asset_tag && <span style={{ fontWeight: 600 }}>{op.asset_tag}</span>}{op.asset_tag && ' — '}{op.asset_name} · </>}
                          {op.assigned_to_name || 'Unassigned'}
                          {op.planned_date && <> · {formatDate(op.planned_date)}</>}
                          {op.estimated_hours > 0 && <> · {fmtH(op.estimated_hours)}</>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                        <span style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${priorityColors[op.priority] || '#6b7280'}15`, color: priorityColors[op.priority] || '#6b7280' }}>{op.priority}</span>
                        <span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: 'rgba(220,38,38,0.1)', color: '#ef4444', border: '1px solid rgba(220,38,38,0.25)' }}>{days}d</span>
                        {op.assigned_to_name ? (
                          <button onClick={(e) => { e.stopPropagation(); setReassignOp(op); setReassignUserId(op.assigned_to || ''); }}
                            style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(99,102,241,0.3)', background: 'rgba(99,102,241,0.1)', color: '#6366f1', cursor: 'pointer', fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FiUsers size={10} /> Reassign
                          </button>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); openReschedule(op, true); }}
                            style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(22,163,74,0.3)', background: 'rgba(22,163,74,0.1)', color: '#16a34a', cursor: 'pointer', fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                            <FiUsers size={10} /> Assign
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); openReschedule(op); }}
                          style={{ padding: '3px 8px', borderRadius: 4, border: 'none', background: '#d97706', color: '#fff', cursor: 'pointer', fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <FiCalendar size={10} /> Reschedule
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reassign Operation Modal */}
      <AnimatePresence>
        {reassignOp && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setReassignOp(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, maxWidth: 420, width: '100%', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
              <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiUsers size={16} color="#6366f1" /> Reassign Operation
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{reassignOp.wo_number} / {reassignOp.operation_number} — {reassignOp.title}</div>
                </div>
                <button onClick={() => setReassignOp(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              <div style={{ padding: '20px 22px' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase' }}>Assign To</label>
                <UserDropdown value={reassignUserId} onChange={setReassignUserId} users={teamMembers} placeholder="Select team member..." />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                  <button onClick={() => setReassignOp(null)}
                    style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                    Cancel
                  </button>
                  <motion.button whileTap={{ scale: 0.97 }} onClick={handleReassignSubmit}
                    disabled={reassignSaving || !reassignUserId}
                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: reassignUserId ? '#6366f1' : 'var(--border)', color: reassignUserId ? '#fff' : 'var(--text-muted)', cursor: reassignUserId ? 'pointer' : 'default', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: reassignSaving ? 0.7 : 1 }}>
                    <FiUsers size={14} /> {reassignSaving ? 'Saving…' : 'Reassign'}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reschedule Overdue Operation Modal */}
      <AnimatePresence>
        {rescheduleOp && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              {/* Header */}
              <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {reschedIsAssign ? <FiUsers size={16} color="#16a34a" /> : <FiCalendar size={16} color="#d97706" />}
                    <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{reschedIsAssign ? 'Assign Operation' : 'Reschedule Operation'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {rescheduleOp.wo_number} / {rescheduleOp.operation_number} — {rescheduleOp.title}
                  </div>
                </div>
                <button onClick={() => setRescheduleOp(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
              </div>
              {/* Body */}
              <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Reason + Comment — only required for reschedule, not initial assign */}
                {!reschedIsAssign && (
                  <>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Reason *</label>
                      <select value={reschedReason} onChange={e => setReschedReason(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }}>
                        <option value="">Select reason...</option>
                        {RESCHEDULE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Additional Information *</label>
                      <input value={reschedComment} onChange={e => setReschedComment(e.target.value.replace(/(^|[.!?]\s+)(\w)/g, (m, p, c) => p + c.toUpperCase()))}
                        placeholder="Provide details..." style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
                    </div>
                  </>
                )}
                {/* Assign To */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Assign To{reschedIsAssign ? ' *' : ''}</label>
                  <UserDropdown value={reschedAssignedTo} users={teamUsers}
                    onChange={v => { setReschedAssignedTo(v); setReschedDate(''); loadReschedCapacity(v, reschedCapRange); }} />
                </div>
                {/* Estimated Time */}
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Estimated Time</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select value={reschedEstHours} onChange={e => setReschedEstHours(parseInt(e.target.value))}
                      style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 }}>
                      {[...Array(13)].map((_, i) => <option key={i} value={i}>{i} hour{i !== 1 ? 's' : ''}</option>)}
                    </select>
                    <select value={reschedEstMinutes} onChange={e => setReschedEstMinutes(parseInt(e.target.value))}
                      style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 }}>
                      {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m} minute{m !== 1 ? 's' : ''}</option>)}
                    </select>
                  </div>
                </div>
                {/* Priority */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Priority</label>
                  <div style={{ display: 'flex', gap: 2, background: 'var(--bg-input)', borderRadius: 6, padding: 2 }}>
                    {['Normal', 'Push'].map(p => (
                      <button key={p} type="button"
                        onClick={() => setReschedPush(p === 'Push')}
                        style={{ padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, transition: 'all 0.15s',
                          background: (reschedPush ? 'Push' : 'Normal') === p ? (p === 'Push' ? '#8b5cf6' : '#2563eb') : 'transparent',
                          color: (reschedPush ? 'Push' : 'Normal') === p ? '#fff' : 'var(--text-muted)' }}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Capacity Calendar */}
                {reschedAssignedTo && reschedCapacity.length > 0 && (
                  <div style={{ background: 'var(--bg-input)', borderRadius: 10, padding: 10, border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Click a day to set new date (next week onwards)</div>
                      <select value={reschedCapRange} onChange={e => { const w = parseInt(e.target.value); setReschedCapRange(w); loadReschedCapacity(reschedAssignedTo, w); }}
                        style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 11 }}>
                        <option value={4}>1 month</option><option value={8}>2 months</option><option value={13}>3 months</option><option value={26}>6 months</option>
                      </select>
                    </div>
                    {(() => {
                      const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                      const fmtHCal = v => { const s = Math.round(v * 4) / 4; const h = Math.floor(s); const m = Math.round((s - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };
                      const jobHours = reschedEstHours + reschedEstMinutes / 60;
                      return Array.from({ length: Math.ceil(reschedCapacity.length / 7) }, (_, weekIdx) => {
                        const weekDays = reschedCapacity.slice(weekIdx * 7, (weekIdx + 1) * 7);
                        if (weekDays.length === 0) return null;
                        const weekLabel = new Date(weekDays[0].date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                        return (
                          <div key={weekIdx} style={{ marginBottom: 6 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3 }}>{weekLabel}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weekDays.length}, 1fr)`, gap: 3 }}>
                              {weekDays.map(day => {
                                const isOff = day.offDuty;
                                const isBlocked = day.blocked;
                                const isFull = day.available > 0 && day.allocated >= day.available;
                                const wouldExceed = day.available > 0 && (day.allocated + jobHours) > day.available;
                                const isOverCap = isFull || wouldExceed;
                                const isTooSoon = day.date < todayStr;
                                const isDisabled = isTooSoon || (!reschedPush && (isOff || isBlocked || isOverCap));
                                const isSelected = reschedDate === day.date;
                                const bg = isSelected ? '#2563eb' : isTooSoon ? 'var(--bg-input)' : isFull ? 'rgba(239,68,68,0.12)' : wouldExceed ? 'rgba(234,88,12,0.1)' : isOff ? 'rgba(245,158,11,0.05)' : isBlocked ? 'rgba(139,92,246,0.1)' : 'var(--bg-card)';
                                const borderColor = isSelected ? '#2563eb' : isTooSoon ? 'var(--border)' : isFull ? '#ef4444' : wouldExceed ? '#ea580c' : isOff ? '#f59e0b' : isBlocked ? '#8b5cf6' : 'var(--border)';
                                return (
                                  <div key={day.date} onClick={() => { if (!isDisabled) setReschedDate(day.date); }}
                                    title={isDisabled ? (isTooSoon ? 'Cannot select a past date' : isOff ? 'Off duty' : isBlocked ? 'Blocked' : 'Over capacity') : `${day.day} ${day.date}\n${fmtHCal(day.allocated)} / ${fmtHCal(day.available)}`}
                                    style={{ padding: '4px 2px', borderRadius: 6, textAlign: 'center', cursor: isDisabled ? 'not-allowed' : 'pointer', border: `1px solid ${borderColor}`, background: bg, opacity: isDisabled ? ((isOff || isBlocked) ? 0.65 : 0.35) : 1, transition: 'all 0.15s' }}>
                                    <div style={{ fontSize: 10, fontWeight: 600, color: isSelected ? '#fff' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : 'var(--text-muted)' }}>{day.day}</div>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? '#fff' : 'var(--text-primary)' }}>{new Date(day.date + 'T00:00:00').getDate()}</div>
                                    <div style={{ fontSize: 9, fontWeight: 600, color: isSelected ? 'rgba(255,255,255,0.8)' : isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : isFull ? '#ef4444' : '#16a34a' }}>{isBlocked ? (day.blockName || 'BLK') : isOff ? 'OFF' : `${fmtHCal(day.allocated)}/${fmtHCal(day.available)}`}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
                {reschedDate && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {reschedIsAssign ? 'Planned Date' : 'New Date'}: <strong style={{ color: 'var(--text-primary)' }}>{new Date(reschedDate + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</strong>
                  </div>
                )}
              </div>
              {/* Footer */}
              {(() => {
                const canSubmit = reschedIsAssign
                  ? (reschedDate && reschedAssignedTo)
                  : (reschedDate && reschedReason && reschedComment.trim());
                const btnColor = reschedIsAssign ? '#16a34a' : '#d97706';
                return (
                  <div style={{ padding: '12px 24px 16px', display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                    <motion.button whileTap={{ scale: 0.97 }} onClick={() => setRescheduleOp(null)}
                      style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      Cancel
                    </motion.button>
                    <div style={{ flex: 1 }} />
                    <motion.button whileTap={canSubmit ? { scale: 0.97 } : {}} disabled={!canSubmit} onClick={handleRescheduleSubmit}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: canSubmit ? btnColor : '#94a3b8', color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, opacity: canSubmit ? 1 : 0.6 }}>
                      {reschedIsAssign ? <><FiUsers size={14} /> Assign</> : <><FiCalendar size={14} /> Reschedule</>}
                    </motion.button>
                  </div>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Capacity Tab */}
      {tab === 'capacity' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Team workload and availability overview
            </div>
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
              {[{ key: 'week', label: 'This Week' }, { key: '2week', label: '2 Weeks' }, { key: 'month', label: '4 Weeks' }].map(r => (
                <button key={r.key} onClick={() => { setCapacityRange(r.key); loadCapacity(r.key); loadDailyCapacity(r.key); }}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                    background: capacityRange === r.key ? '#2563eb' : 'transparent',
                    color: capacityRange === r.key ? '#fff' : 'var(--text-secondary)'
                  }}>{r.label}</button>
              ))}
            </div>
          </div>

          {capacityLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading capacity data...</div>
            </div>
          ) : capacityData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
              <FiBarChart2 size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <div style={{ fontSize: 15 }}>No team members found</div>
            </div>
          ) : (
            <div>
              {/* Summary Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                {(() => {
                  const totalAvail = capacityData.reduce((s, u) => s + u.available_hours, 0);
                  const totalAlloc = capacityData.reduce((s, u) => s + u.allocated_hours, 0);
                  const totalJobs = capacityData.reduce((s, u) => s + u.job_count, 0);
                  const avgUtil = totalAvail > 0 ? Math.round((totalAlloc / totalAvail) * 100) : 0;
                  const overloaded = capacityData.filter(u => u.utilization > 100).length;
                  return [
                    { label: 'Total Available', value: `${totalAvail}h`, color: '#2563eb' },
                    { label: 'Total Allocated', value: `${totalAlloc}h`, color: '#059669' },
                    { label: 'Avg Utilization', value: `${avgUtil}%`, color: avgUtil > 90 ? '#dc2626' : avgUtil > 70 ? '#f59e0b' : '#059669' },
                    { label: 'Active Jobs', value: totalJobs, color: '#6b7280' },
                    { label: 'Overloaded', value: overloaded, color: overloaded > 0 ? '#dc2626' : '#059669' },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--border)', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ));
                })()}
              </div>

              {/* Daily Capacity Chart */}
              {dailyCapacity.length > 0 && (
                <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 16 }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Daily Hours - Available vs Allocated</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={dailyCapacity}>
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => {
                        const dt = new Date(d);
                        const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                        return `${days[dt.getDay()]} ${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}`;
                      }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}h`} />
                      <Tooltip
                        labelFormatter={d => {
                          const dt = new Date(d);
                          return dt.toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'short' });
                        }}
                        formatter={(v, name) => [`${v}h`, name === 'available' ? 'Available' : 'Allocated']}
                        contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }}
                      />
                      <Legend />
                      <Bar dataKey="available" name="Available" fill="rgba(37,99,235,0.25)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="allocated" name="Allocated" fill="rgba(16,163,74,0.7)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Per-User Capacity Chart */}
              {capacityData.length > 0 && (
                <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 16 }}>
                  <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Hours Per Team Member</h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, capacityData.length * 45)}>
                    <BarChart data={capacityData} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `${v}h`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip
                        formatter={(v, name) => [`${v}h`, name === 'available_hours' ? 'Available' : 'Allocated']}
                        contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }}
                      />
                      <Legend />
                      <Bar dataKey="available_hours" name="Available" fill="rgba(37,99,235,0.3)" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="allocated_hours" name="Allocated" fill="rgba(16,163,74,0.7)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Detailed User List */}
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 20, border: '1px solid var(--border)', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Team Capacity Overview</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {capacityData.map((u, idx) => {
                    const maxHrs = Math.max(...capacityData.map(x => Math.max(x.available_hours, x.allocated_hours)), 1);
                    const availPct = (u.available_hours / maxHrs) * 100;
                    const allocPct = (u.allocated_hours / maxHrs) * 100;
                    const isOver = u.utilization > 100;
                    const utilColor = isOver ? '#dc2626' : u.utilization > 80 ? '#f59e0b' : '#059669';
                    const isExpanded = expandedUser === u.id;

                    return (
                      <motion.div key={u.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.03 }}>
                        <div
                          onClick={() => { playClick(); setExpandedUser(isExpanded ? null : u.id); }}
                          style={{ cursor: 'pointer', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            {u.avatar ? (
                              <img src={`/api/users/avatar/${u.avatar}`} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: 28, height: 28, borderRadius: '50%', background: u.avatar_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                {getInitials(u.name)}
                              </div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</span>
                                {u.employee_id && <span style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{u.employee_id}</span>}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: utilColor }}>{u.utilization}%</span>
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtH(u.allocated_hours)} / {fmtH(u.available_hours)}</span>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)', padding: '2px 6px', borderRadius: 4, background: 'var(--bg-input)' }}>{u.job_count} job{u.job_count !== 1 ? 's' : ''}</span>
                              <FiChevronDown size={14} color="var(--text-muted)" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </div>
                          </div>
                          {/* Dual bar */}
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <div style={{ flex: 1, position: 'relative', height: 20, borderRadius: 6, background: 'var(--bg-input)', overflow: 'hidden' }}>
                              {/* Available (background) */}
                              <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${availPct}%`, background: 'rgba(37,99,235,0.12)', borderRadius: 6, transition: 'width 0.4s ease' }} />
                              {/* Allocated (foreground) */}
                              <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${allocPct}%`, background: isOver ? 'rgba(220,38,38,0.7)' : u.utilization > 80 ? 'rgba(245,158,11,0.7)' : 'rgba(5,150,105,0.7)', borderRadius: 6, transition: 'width 0.4s ease' }} />
                            </div>
                          </div>
                        </div>

                        {/* Expanded job list */}
                        <AnimatePresence>
                          {isExpanded && u.jobs.length > 0 && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                              style={{ overflow: 'hidden', paddingLeft: 38 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 8, paddingBottom: 4 }}>
                                {u.jobs.map((j, ji) => {
                                  const now = new Date(); now.setHours(0,0,0,0);
                                  const jobDate = j.due_date ? new Date(j.due_date + (j.due_date.length === 10 ? 'T00:00:00' : '')) : null;
                                  if (jobDate) jobDate.setHours(0,0,0,0);
                                  const isPastOrToday = jobDate && jobDate <= now;
                                  const btnLabel = isPastOrToday ? 'Reschedule' : 'Edit Dates';
                                  return (
                                  <div key={`${j.id}-${j.op_id || ji}`} onClick={(e) => { e.stopPropagation(); navigate(`/work-orders/${j.wo_number}`); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-input)', cursor: 'pointer', transition: 'background 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'var(--border)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-input)'}>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                      {j.wo_number}{j.operation_number ? ` / ${j.operation_number}` : ''}
                                    </span>
                                    <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[j.type] || '#6b7280' }}>{j.type}</span>
                                    <span style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.op_title || j.name}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{j.planned_hours > 0 ? fmtH(j.planned_hours) : '-'}</span>
                                    <span onClick={(e) => { e.stopPropagation(); if (j.due_date) { const dateStr = new Date(j.due_date).toISOString().split('T')[0]; if (teamViewUser !== u.id) { setTeamViewUser(u.id); loadTeamUserCapacity(u.id, teamCapRange); } setTeamCapScrollDate(dateStr); } }}
                                      style={{ fontSize: 10, color: '#2563eb', whiteSpace: 'nowrap', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                      title="Click to find in calendar">{formatDate(j.due_date)}</span>
                                    <button onClick={(e) => { e.stopPropagation(); const mapped = { ...j, opId: j.op_id, wo: j.wo_number, hours: j.planned_hours, opNum: j.operation_number, opTitle: j.op_title }; setCapDayModal({ date: j.due_date ? new Date(j.due_date).toISOString().split('T')[0] : '', jobs: [mapped], allocated: j.planned_hours, available: 0, userId: u.id, userName: u.name, rescheduleJob: mapped }); }}
                                      style={{ padding: '2px 8px', borderRadius: 4, border: `1px solid ${isPastOrToday ? 'rgba(220,38,38,0.3)' : 'rgba(37,99,235,0.3)'}`, background: isPastOrToday ? 'rgba(220,38,38,0.06)' : 'rgba(37,99,235,0.06)', color: isPastOrToday ? '#dc2626' : '#2563eb', fontSize: 9, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                      {btnLabel}
                                    </button>
                                  </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                          {isExpanded && u.jobs.length === 0 && (
                            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                              style={{ overflow: 'hidden', paddingLeft: 38 }}>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>No jobs assigned in this period</div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 11, color: 'var(--text-muted)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(37,99,235,0.25)' }} />
                    Available Hours
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(5,150,105,0.7)' }} />
                    Allocated (&le;80%)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(245,158,11,0.7)' }} />
                    High (80-100%)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: 'rgba(220,38,38,0.7)' }} />
                    Overloaded (&gt;100%)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* My Team Tab */}
      {tab === 'myteam' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              View team member calendars, jobs, and capacity
            </div>
          </div>

          {teamAllCapLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading team data...</div>
            </div>
          ) : (
            <div>
              {/* Team member cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginBottom: 20 }}>
                {teamUsers.map(u => {
                  const cap = teamAllCapacity.find(c => c.id === u.id);
                  const utilColor = cap ? (cap.utilization > 100 ? '#dc2626' : cap.utilization > 80 ? '#f59e0b' : '#059669') : 'var(--text-muted)';
                  const isSelected = teamViewUser === u.id;
                  return (
                    <motion.div key={u.id} whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (isSelected) { setTeamViewUser(null); setTeamUserCapacity([]); }
                        else { setTeamViewUser(u.id); loadTeamUserCapacity(u.id, teamCapRange); }
                      }}
                      style={{ background: isSelected ? 'rgba(37,99,235,0.06)' : 'var(--bg-card)', borderRadius: 12, padding: 16, border: `1px solid ${isSelected ? '#2563eb' : 'var(--border)'}`, cursor: 'pointer', transition: 'all 0.15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        {u.avatar ? (
                          <img src={`/api/users/avatar/${u.avatar}`} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: u.avatar_color || '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff' }}>
                            {getInitials(u.name)}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{u.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.team_name || u.employee_id || ''}</div>
                          <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d, i) => {
                              const key = `working_hours_${d.toLowerCase()}`;
                              const hrs = parseFloat(u[key] ?? (i < 5 ? 8 : 0));
                              return hrs > 0 ? <span key={d} style={{ marginRight: 4 }}>{d}:{hrs}h</span> : null;
                            })}
                          </div>
                        </div>
                        {cap && (
                          <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 60 }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: utilColor }}>{cap.utilization}%</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{cap.job_count} {cap.job_count !== 1 ? 'jobs' : 'job'}</div>
                          </div>
                        )}
                      </div>
                      {cap && (
                        <div style={{ position: 'relative', height: 6, borderRadius: 3, background: 'var(--bg-input)', overflow: 'hidden' }}>
                          <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${Math.min(cap.utilization, 100)}%`, background: utilColor, borderRadius: 3, transition: 'width 0.3s' }} />
                        </div>
                      )}
                      {/* Over-capacity days indicator */}
                      {cap && cap.jobs && (() => {
                        const overDays = new Set();
                        const dayMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                        // Check each job's due date for over-capacity
                        const jobsByDate = {};
                        cap.jobs.forEach(j => {
                          if (j.due_date) {
                            const d = new Date(j.due_date).toISOString().split('T')[0];
                            if (!jobsByDate[d]) jobsByDate[d] = 0;
                            jobsByDate[d] += j.planned_hours || 0;
                          }
                        });
                        Object.entries(jobsByDate).forEach(([date, hours]) => {
                          const d = new Date(date);
                          const dayName = dayMap[d.getDay()];
                          const dailyHrs = parseFloat(u[`working_hours_${dayName}`] ?? (dayName === 'sat' || dayName === 'sun' ? 0 : 8));
                          if (hours > dailyHrs && dailyHrs > 0) overDays.add(date);
                        });
                        if (overDays.size === 0) return null;
                        return (
                          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {[...overDays].sort().slice(0, 5).map(d => (
                              <span key={d} style={{ padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, background: 'rgba(220,38,38,0.1)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.2)' }}>
                                {new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })} OVER
                              </span>
                            ))}
                            {overDays.size > 5 && <span style={{ fontSize: 9, color: '#dc2626', fontWeight: 600 }}>+{overDays.size - 5} more</span>}
                          </div>
                        );
                      })()}
                    </motion.div>
                  );
                })}
              </div>

              {/* Selected user calendar + bar chart */}
              <AnimatePresence>
                {teamViewUser && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden' }}>
                    <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 16 }}>
                      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FiCalendar size={14} color="#2563eb" />
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{teamUsers.find(u => u.id === teamViewUser)?.name} — Capacity</span>
                        </div>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', gap: 2, background: 'var(--bg-input)', borderRadius: 6, padding: 2 }}>
                            <button onClick={() => setTeamViewMode('calendar')} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: teamViewMode === 'calendar' ? '#2563eb' : 'transparent', color: teamViewMode === 'calendar' ? '#fff' : 'var(--text-muted)' }}>Calendar</button>
                            <button onClick={() => setTeamViewMode('chart')} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: teamViewMode === 'chart' ? '#2563eb' : 'transparent', color: teamViewMode === 'chart' ? '#fff' : 'var(--text-muted)' }}>Bar Chart</button>
                          </div>
                          <select value={teamCapRange} onChange={e => { const v = parseInt(e.target.value); setTeamCapRange(v); loadTeamUserCapacity(teamViewUser, v); loadTeamAllCapacity(v); }}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', appearance: 'auto' }}>
                            {[{v:1,l:'1 Week'},{v:4,l:'1 Month'},{v:8,l:'2 Months'},{v:13,l:'3 Months'},{v:17,l:'4 Months'},{v:22,l:'5 Months'},{v:26,l:'6 Months'},{v:30,l:'7 Months'},{v:35,l:'8 Months'},{v:39,l:'9 Months'},{v:43,l:'10 Months'},{v:48,l:'11 Months'},{v:52,l:'12 Months'}].map(r => (
                              <option key={r.v} value={r.v}>{r.l}</option>
                            ))}
                          </select>
                          <select onChange={e => {
                            if (!e.target.value) return;
                            const jumpDate = e.target.value;
                            const diff = Math.ceil((new Date(jumpDate) - new Date()) / (7 * 86400000));
                            const neededWeeks = Math.max(diff + 4, teamCapRange);
                            if (neededWeeks > teamCapRange) { setTeamCapRange(neededWeeks); loadTeamUserCapacity(teamViewUser, neededWeeks); loadTeamAllCapacity(neededWeeks); }
                            setTimeout(() => setTeamCapScrollDate(jumpDate), neededWeeks > teamCapRange ? 800 : 100);
                            e.target.value = '';
                          }}
                            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', appearance: 'auto' }}>
                            <option value="">Jump to...</option>
                            {Array.from({ length: 12 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + i); const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; return <option key={i} value={dateStr}>{d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}</option>; })}
                          </select>
                          <button onClick={() => { setTeamViewUser(null); setTeamUserCapacity([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={16} /></button>
                        </div>
                      </div>
                      <div style={{ padding: 16 }}>
                        {teamUserCapLoading ? (
                          <div style={{ textAlign: 'center', padding: 30 }}>
                            <div style={{ width: 24, height: 24, border: '2px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                          </div>
                        ) : teamViewMode === 'calendar' ? (
                          /* Calendar View - same as assignment calendar but read-only */
                          <div>
                            {(() => {
                              const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
                              const fmtH = v => { const snapped = Math.round(v * 4) / 4; const h = Math.floor(snapped); const m = Math.round((snapped - h) * 60); return m > 0 ? `${h}h${m}m` : `${h}h`; };
                              return Array.from({ length: Math.ceil(teamUserCapacity.length / 7) }, (_, weekIdx) => {
                                const weekDays = teamUserCapacity.slice(weekIdx * 7, (weekIdx + 1) * 7);
                                if (weekDays.length === 0) return null;
                                const weekLabel = new Date(weekDays[0].date + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
                                return (
                                  <div key={weekIdx} style={{ marginBottom: 8 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>{weekLabel}</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weekDays.length}, 1fr)`, gap: 4 }}>
                                      {weekDays.map(day => {
                                        const isScrollTarget = teamCapScrollDate && day.date === teamCapScrollDate;
                                        const isToday = day.date === todayStr;
                                        const isOff = day.offDuty;
                                        const isBlocked = day.blocked;
                                        const pct = day.available > 0 ? Math.round((day.allocated / day.available) * 100) : (day.allocated > 0 ? 100 : 0);
                                        const isFull = day.available > 0 && day.allocated >= day.available;
                                        const isOver = day.available > 0 && day.allocated > day.available;
                                        const isWarn = pct >= 75 && !isFull && day.available > 0;
                                        const bg = isBlocked ? 'rgba(139,92,246,0.08)' : isOver ? 'rgba(153,27,27,0.85)' : isFull ? 'rgba(239,68,68,0.08)' : isWarn ? 'rgba(245,158,11,0.08)' : isOff ? 'rgba(245,158,11,0.05)' : isToday ? 'rgba(37,99,235,0.08)' : 'var(--bg-input)';
                                        const borderColor = isBlocked ? '#8b5cf6' : isOver ? '#dc2626' : isFull ? '#ef4444' : isWarn ? '#f59e0b' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--border)';
                                        const capText = `${fmtH(day.allocated)}/${fmtH(day.available)}`;
                                        const clickable = day.jobs && day.jobs.length > 0;
                                        return (
                                          <div key={day.date}
                                            ref={isScrollTarget ? el => { if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => setTeamCapScrollDate(null), 600); } } : undefined}
                                            onClick={() => { if (clickable) setCapDayModal({ date: day.date, day: day.day, jobs: day.jobs, allocated: day.allocated, available: day.available, userId: teamViewUser, userName: teamUsers.find(u => u.id === teamViewUser)?.name }); }}
                                            title={`${day.day} ${day.date}${isBlocked ? `\nBLOCKED (${day.blockName || 'Block'})` : isOff ? '\nOFF DUTY' : ''}\n${fmtH(day.allocated)} booked / ${fmtH(day.available)} capacity\n${day.jobs?.map(j => `${j.wo}: ${j.name} (${fmtH(j.hours)})`).join('\n') || 'No jobs'}${clickable ? '\nClick to view jobs' : ''}`}
                                            style={{ padding: '6px 4px', borderRadius: 8, textAlign: 'center', border: `${isScrollTarget ? '2px' : (isToday || isOver) ? '2px' : '1px'} solid ${isScrollTarget ? '#2563eb' : borderColor}`, background: isScrollTarget ? 'rgba(37,99,235,0.12)' : bg, transition: 'all 0.15s', minHeight: 68, cursor: clickable ? 'pointer' : 'default', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: isBlocked ? '#8b5cf6' : isOver ? '#fca5a5' : isOff ? '#f59e0b' : isToday ? '#2563eb' : 'var(--text-muted)', letterSpacing: '0.5px' }}>{day.day}</div>
                                            <div style={{ fontSize: 18, fontWeight: 800, color: isOver ? '#fff' : isToday ? '#2563eb' : 'var(--text-primary)', lineHeight: 1.1 }}>{new Date(day.date + 'T00:00:00').getDate()}</div>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: isBlocked ? '#8b5cf6' : isOff ? '#f59e0b' : isOver ? '#fecaca' : isFull ? '#ef4444' : isWarn ? '#f59e0b' : '#16a34a' }}>{isBlocked ? (day.blockName || 'BLK') : isOff ? 'OFF' : capText}</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        ) : (
                          /* Bar Chart View */
                          <div>
                            {(() => {
                              // Group by week for chart data
                              const weeks = [];
                              for (let i = 0; i < teamUserCapacity.length; i += 7) {
                                const weekDays = teamUserCapacity.slice(i, i + 7);
                                const ws = weekDays[0]?.date;
                                const we = weekDays[weekDays.length - 1]?.date;
                                const available = weekDays.reduce((s, d) => s + d.available, 0);
                                const allocated = weekDays.reduce((s, d) => s + d.allocated, 0);
                                const jobs = weekDays.reduce((s, d) => s + (d.jobs?.length || 0), 0);
                                const wStart = new Date(ws + 'T00:00:00');
                                const wEnd = new Date(we + 'T00:00:00');
                                const weekNum = Math.ceil((wStart - new Date(wStart.getFullYear(), 0, 1)) / 604800000);
                                weeks.push({
                                  week: `Wk ${weekNum} (${wStart.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit' })} - ${wEnd.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit' })})`,
                                  available: Math.round(available * 10) / 10,
                                  allocated: Math.round(allocated * 10) / 10,
                                  jobs,
                                });
                              }
                              return (
                                <ResponsiveContainer width="100%" height={280}>
                                  <BarChart data={weeks}>
                                    <XAxis dataKey="week" tick={{ fontSize: 9 }} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${v}h`} />
                                    <Tooltip formatter={(v, name) => [`${v}h`, name === 'available' ? 'Available' : 'Allocated']}
                                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                                    <Legend />
                                    <Bar dataKey="allocated" name="Allocated" fill="rgba(16,163,74,0.7)" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="available" name="Available" fill="rgba(37,99,235,0.25)" radius={[4, 4, 0, 0]} />
                                  </BarChart>
                                </ResponsiveContainer>
                              );
                            })()}
                          </div>
                        )}

                        {/* Per-user blocks */}
                        {(() => {
                          const userBlocks = teamBlocks.filter(b => b.user_id === teamViewUser && new Date(b.end_date) >= new Date(new Date().toISOString().split('T')[0]));
                          return (
                            <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <FiCalendar size={13} /> Scheduling Blocks
                                  {userBlocks.filter(b => b.status === 'pending').length > 0 && (
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
                                      {userBlocks.filter(b => b.status === 'pending').length} pending
                                    </span>
                                  )}
                                </span>
                                <button onClick={() => { setShowBlockModal(true); setBlockForm({ user_id: String(teamViewUser), name: '', start_date: '', end_date: '' }); }}
                                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                  <FiPlus size={12} /> Add Block
                                </button>
                              </div>
                              {userBlocks.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {userBlocks.map(block => {
                                    const statusColor = block.status === 'active' ? '#059669' : '#f59e0b';
                                    return (
                                      <div key={block.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'var(--bg-secondary)', border: `1px solid ${block.status === 'pending' ? 'rgba(245,158,11,0.3)' : 'var(--border)'}` }}>
                                        <div style={{ flex: 1 }}>
                                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{block.name}</span>
                                          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                                            {new Date(block.start_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })} – {new Date(block.end_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                                          </span>
                                        </div>
                                        <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase', color: statusColor, background: `${statusColor}15` }}>{block.status}</span>
                                        {block.status === 'pending' && (
                                          <button onClick={async () => {
                                            try {
                                              const r = await authFetch(`/api/blocks/${block.id}/activate`, { method: 'PUT' });
                                              if (r.ok) { showToast('Block activated'); loadTeamBlocks(); }
                                              else { const d = await r.json(); showToast(d.error || 'Cannot activate', 'error'); }
                                            } catch { showToast('Failed', 'error'); }
                                          }}
                                            style={{ padding: '3px 10px', borderRadius: 5, border: 'none', background: '#059669', color: '#fff', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>
                                            Activate
                                          </button>
                                        )}
                                        <button onClick={async () => {
                                          if (!confirm(`Remove block "${block.name}"?`)) return;
                                          try {
                                            const r = await authFetch(`/api/blocks/${block.id}`, { method: 'DELETE' });
                                            if (r.ok) { showToast('Block removed'); loadTeamBlocks(); loadTeamUserCapacity(teamViewUser, teamCapRange); }
                                          } catch { showToast('Failed', 'error'); }
                                        }}
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                                          <FiX size={13} />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 10 }}>No blocks set</div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

            </div>
          )}
        </div>
      )}

      {/* Add Block Modal */}
      <AnimatePresence>
        {showBlockModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 420, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', padding: 24 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Add Scheduling Block</h3>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>Block a date range for a team member. No non-Push work can be planned during this period.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Team Member *</label>
                  <select value={blockForm.user_id} onChange={e => setBlockForm(f => ({ ...f, user_id: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13 }}>
                    <option value="">Select...</option>
                    {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Block Name *</label>
                  <input value={blockForm.name} onChange={e => setBlockForm(f => ({ ...f, name: titleCase(e.target.value) }))} placeholder="e.g. Birthday, Training, Holiday"
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Start Date *</label>
                    <input type="date" value={blockForm.start_date} onChange={e => setBlockForm(f => ({ ...f, start_date: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>End Date *</label>
                    <input type="date" value={blockForm.end_date} onChange={e => setBlockForm(f => ({ ...f, end_date: e.target.value }))}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button onClick={() => setShowBlockModal(false)}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  disabled={blockSaving || !blockForm.user_id || !blockForm.name.trim() || !blockForm.start_date || !blockForm.end_date}
                  onClick={async () => {
                    setBlockSaving(true);
                    try {
                      const r = await authFetch('/api/blocks', { method: 'POST', body: JSON.stringify(blockForm) });
                      if (r.ok) {
                        const d = await r.json();
                        if (d.conflicts > 0) showToast(`Block created as pending — ${d.conflicts} job(s) need to be moved first`);
                        else showToast('Block activated');
                        setShowBlockModal(false);
                        loadTeamBlocks();
                        if (teamViewUser) loadTeamUserCapacity(teamViewUser, teamCapRange);
                      } else { const d = await r.json(); showToast(d.error || 'Failed', 'error'); }
                    } catch { showToast('Failed', 'error'); }
                    setBlockSaving(false);
                  }}
                  style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (blockSaving || !blockForm.user_id || !blockForm.name.trim() || !blockForm.start_date || !blockForm.end_date) ? 0.5 : 1 }}>
                  {blockSaving ? 'Saving...' : 'Add Block'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Metrics Tab */}
      {tab === 'metrics' && (
        <div>
          {/* Adherence Metrics Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiTrendingUp size={16} /> Work Order Metrics
              </h3>
              <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
                {[{ key: 'weekly', label: 'Weekly' }, { key: 'monthly', label: 'Monthly' }, { key: 'yearly', label: 'Yearly' }].map(r => (
                  <button key={r.key} onClick={() => { setMetricsView(r.key); loadMetrics(r.key); }}
                    style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s', background: metricsView === r.key ? '#2563eb' : 'transparent', color: metricsView === r.key ? '#fff' : 'var(--text-secondary)' }}>{r.label}</button>
                ))}
              </div>
            </div>

            {metricsLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading metrics...</div>
              </div>
            ) : metrics.length > 0 && (
              <div>
                {/* Metric summary cards — reflect the currently-selected bar.
                    Drops back to averages/totals when no bar is selected. */}
                {(() => {
                  const latest = metrics[metrics.length - 1] || {};
                  const avgAdherence = metrics.length > 0 ? Math.round(metrics.reduce((s, m) => s + (m.adherence_pct || 0), 0) / metrics.length) : 0;
                  const totalCompleted = metrics.reduce((s, m) => s + (m.completed || 0), 0);
                  const totalHours = Math.round(metrics.reduce((s, m) => s + (m.hours_logged || 0), 0) * 10) / 10;
                  const sel = selectedPeriod;
                  const selLabel = sel ? (sel.period_label || (() => {
                    if (!sel.period_start) return null;
                    const s = new Date(sel.period_start);
                    return s.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
                  })()) : null;
                  const cards = sel ? [
                    { label: `${selLabel || 'Selected'} adherence`, value: `${sel.adherence_pct || 0}%`, color: (sel.adherence_pct || 0) >= 80 ? '#059669' : (sel.adherence_pct || 0) >= 60 ? '#f59e0b' : '#dc2626' },
                    { label: 'Completed', value: sel.completed || 0, color: '#2563eb' },
                    { label: 'Not Completed', value: sel.not_completed || 0, color: '#dc2626' },
                    { label: 'Hours', value: fmtH(sel.hours_logged || 0), color: '#6b7280' },
                  ] : [
                    { label: 'Avg Adherence', value: `${avgAdherence}%`, color: avgAdherence >= 80 ? '#059669' : avgAdherence >= 60 ? '#f59e0b' : '#dc2626' },
                    { label: 'Latest Period', value: `${latest.adherence_pct || 0}%`, color: (latest.adherence_pct || 0) >= 80 ? '#059669' : '#f59e0b' },
                    { label: 'Total Completed', value: totalCompleted, color: '#2563eb' },
                    { label: 'Total Hours', value: fmtH(totalHours), color: '#6b7280' },
                  ];
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
                      {cards.map((s, i) => (
                        <div key={i} style={{ background: sel ? 'rgba(37,99,235,0.05)' : 'var(--bg-card)', borderRadius: 10, padding: '12px 14px', border: sel ? '1px solid rgba(37,99,235,0.3)' : '1px solid var(--border)', textAlign: 'center' }}>
                          <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Adherence chart */}
                <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '16px', border: '1px solid var(--border)', marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Adherence Trend — click a bar for details</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={metrics} onClick={(data) => { if (data?.activePayload?.[0]) setSelectedPeriod(data.activePayload[0].payload); }} style={{ cursor: 'pointer' }}>
                      <XAxis dataKey={metricsView === 'weekly' ? 'period_start' : 'period_label'} tick={{ fontSize: 9, fill: axisTickColor }} stroke={axisTickColor}
                        tickFormatter={d => {
                          if (metricsView !== 'weekly') return d;
                          const s = new Date(d);
                          const e = new Date(s); e.setDate(s.getDate() + 6);
                          return `${s.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${e.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                        }} />
                      <YAxis tick={{ fontSize: 11, fill: axisTickColor }} stroke={axisTickColor} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                      <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: isDark ? '#e8edf5' : '#1e293b' }} itemStyle={{ color: isDark ? '#e8edf5' : '#1e293b' }}
                        labelFormatter={d => {
                          if (metricsView !== 'weekly') return d;
                          const s = new Date(d);
                          const e = new Date(s); e.setDate(s.getDate() + 6);
                          return `${s.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} – ${e.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                        }}
                        formatter={(v, name) => [name === 'adherence_pct' ? `${v}%` : v, name === 'adherence_pct' ? 'Adherence' : name]} />
                      <ReferenceLine y={80} stroke="#059669" strokeDasharray="3 3" label={{ value: 'Target 80%', fontSize: 10, fill: '#059669' }} />
                      <Bar dataKey="adherence_pct" name="Adherence" radius={[4, 4, 0, 0]}>
                        {metrics.map((entry, i) => {
                          const isSel = selectedPeriod && ((entry.period_label && entry.period_label === selectedPeriod.period_label) || (entry.period_start && entry.period_start === selectedPeriod.period_start));
                          const base = entry.adherence_pct >= 80 ? 'rgba(5,150,105,0.7)' : entry.adherence_pct >= 60 ? 'rgba(245,158,11,0.7)' : 'rgba(220,38,38,0.7)';
                          return <Cell key={i} fill={isSel ? base.replace('0.7', '1') : base} stroke={isSel ? '#2563eb' : 'none'} strokeWidth={isSel ? 2 : 0} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Completed vs Not Completed chart */}
                <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '16px', border: '1px solid var(--border)', marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Completed vs Not Completed</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={metrics}>
                      <XAxis dataKey={metricsView === 'weekly' ? 'period_start' : 'period_label'} tick={{ fontSize: 9, fill: axisTickColor }} stroke={axisTickColor}
                        tickFormatter={d => {
                          if (metricsView !== 'weekly') return d;
                          const s = new Date(d);
                          const e = new Date(s); e.setDate(s.getDate() + 6);
                          return `${s.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${e.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                        }} />
                      <YAxis tick={{ fontSize: 11, fill: axisTickColor }} stroke={axisTickColor} />
                      <Tooltip contentStyle={chartTooltipStyle} labelStyle={{ color: isDark ? '#e8edf5' : '#1e293b' }} itemStyle={{ color: isDark ? '#e8edf5' : '#1e293b' }} />
                      <Legend wrapperStyle={{ color: axisTickColor }} />
                      <Bar dataKey="completed" name="Completed" stackId="a" fill="rgba(5,150,105,0.7)" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="not_completed" name="Not Completed" stackId="a" fill="rgba(220,38,38,0.5)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Hours chart */}
                <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: '16px', border: '1px solid var(--border)', marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Hours Logged</h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={metrics}>
                      <XAxis dataKey={metricsView === 'weekly' ? 'period_start' : 'period_label'} tick={{ fontSize: 9, fill: axisTickColor }} stroke={axisTickColor}
                        tickFormatter={d => {
                          if (metricsView !== 'weekly') return d;
                          const s = new Date(d);
                          const e = new Date(s); e.setDate(s.getDate() + 6);
                          return `${s.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${e.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                        }} />
                      <YAxis tick={{ fontSize: 11, fill: axisTickColor }} stroke={axisTickColor} tickFormatter={v => `${v}h`} />
                      <Tooltip formatter={(v) => [`${v}h`, 'Hours']} contentStyle={chartTooltipStyle} labelStyle={{ color: isDark ? '#e8edf5' : '#1e293b' }} itemStyle={{ color: isDark ? '#e8edf5' : '#1e293b' }} />
                      <Bar dataKey="hours_logged" name="Hours" fill="rgba(37,99,235,0.6)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Selected period detail */}
                {selectedPeriod && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid #2563eb', marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#2563eb' }}>
                        {selectedPeriod.period_label || (() => {
                          const s = new Date(selectedPeriod.period_start);
                          const e = new Date(selectedPeriod.period_end);
                          return `Week ${Math.ceil((s - new Date(s.getFullYear(), 0, 1)) / 604800000)} (${s.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${e.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })})`;
                        })()}
                      </h4>
                      <button onClick={() => setSelectedPeriod(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><FiX size={14} /></button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8, fontSize: 12 }}>
                      <div><span style={{ color: 'var(--text-muted)' }}>Adherence:</span> <strong style={{ color: selectedPeriod.adherence_pct >= 80 ? '#059669' : '#f59e0b' }}>{selectedPeriod.adherence_pct}%</strong></div>
                      <div><span style={{ color: 'var(--text-muted)' }}>Completed:</span> <strong>{selectedPeriod.completed}</strong></div>
                      <div><span style={{ color: 'var(--text-muted)' }}>Not Done:</span> <strong style={{ color: '#dc2626' }}>{selectedPeriod.not_completed}</strong></div>
                      <div><span style={{ color: 'var(--text-muted)' }}>Total Due:</span> <strong>{selectedPeriod.total_due}</strong></div>
                      <div><span style={{ color: 'var(--text-muted)' }}>Hours:</span> <strong>{fmtH(selectedPeriod.hours_logged)}</strong></div>
                    </div>
                    {selectedPeriod.by_type && Object.keys(selectedPeriod.by_type).length > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {Object.entries(selectedPeriod.by_type).map(([type, data]) => (
                          <span key={type} style={{ padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: `${WO_TYPE_COLORS[type] || '#6b7280'}18`, color: WO_TYPE_COLORS[type] || '#6b7280', border: `1px solid ${WO_TYPE_COLORS[type] || '#6b7280'}30` }}>
                            {type}: {data.done}/{data.total}
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Cost Metrics Section */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiDollarSign size={16} /> Cost Breakdown
              </h3>
              <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
                {[{ key: 'week', label: 'This Week' }, { key: 'month', label: 'This Month' }, { key: 'quarter', label: 'Quarter' }, { key: 'year', label: 'Year' }].map(r => (
                  <button key={r.key} onClick={() => { setCostPeriod(r.key); loadCostMetrics(r.key); }}
                    style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s', background: costPeriod === r.key ? '#2563eb' : 'transparent', color: costPeriod === r.key ? '#fff' : 'var(--text-secondary)' }}>{r.label}</button>
                ))}
              </div>
            </div>

            {costLoading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ width: 32, height: 32, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              </div>
            ) : costMetrics && (
              <div>
                {costMetrics.total_cost === 0 && costMetrics.labour_hours === 0 && costMetrics.workers === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--text-muted)', marginBottom: 12 }}>
                    <FiDollarSign size={32} style={{ opacity: 0.2, marginBottom: 8 }} />
                    <div style={{ fontSize: 13 }}>No cost data for this period</div>
                    <div style={{ fontSize: 11, marginTop: 4 }}>Cost tracking requires hourly rates to be set on user profiles and material costs on work orders</div>
                  </div>
                )}
                {/* Cost summary cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: 'Total Cost', value: `$${costMetrics.total_cost.toLocaleString()}`, color: '#2563eb' },
                    { label: 'Labour', value: `$${costMetrics.labour_cost.toLocaleString()}`, color: '#059669' },
                    { label: 'Materials', value: `$${costMetrics.material_cost.toLocaleString()}`, color: '#f59e0b' },
                    { label: 'Ext. Labour', value: `$${costMetrics.ext_labour_cost.toLocaleString()}`, color: '#8b5cf6' },
                    { label: 'Labour Hours', value: fmtH(costMetrics.labour_hours), color: '#6b7280' },
                    { label: 'Workers', value: costMetrics.workers, color: '#6b7280' },
                  ].map((s, i) => (
                    <div key={i} style={{ background: 'var(--bg-card)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--border)', textAlign: 'center' }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Cost breakdown pie + by type bar */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 16 }}>
                  {/* Pie chart */}
                  {costMetrics.total_cost > 0 && (
                    <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Cost Split</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={[
                            { name: 'Labour', value: costMetrics.labour_cost },
                            { name: 'Materials', value: costMetrics.material_cost },
                            { name: 'Ext. Labour', value: costMetrics.ext_labour_cost },
                          ].filter(d => d.value > 0)} cx="50%" cy="50%" outerRadius={70} innerRadius={40} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            <Cell fill="rgba(5,150,105,0.7)" />
                            <Cell fill="rgba(245,158,11,0.7)" />
                            <Cell fill="rgba(139,92,246,0.7)" />
                          </Pie>
                          <Tooltip formatter={v => `$${v.toLocaleString()}`} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Cost by WO type */}
                  {costMetrics.by_type && costMetrics.by_type.length > 0 && (
                    <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Labour Cost by Type</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={costMetrics.by_type} layout="vertical">
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${v}`} />
                          <YAxis type="category" dataKey="type" tick={{ fontSize: 11 }} width={50} />
                          <Tooltip formatter={v => `$${v.toLocaleString()}`} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                          <Bar dataKey="labour_cost" name="Labour Cost" radius={[0, 4, 4, 0]}>
                            {costMetrics.by_type.map((entry, i) => (
                              <Cell key={i} fill={WO_TYPE_COLORS[entry.type] || 'rgba(37,99,235,0.6)'} fillOpacity={0.7} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* Top costliest work orders */}
                {costMetrics.top_work_orders && costMetrics.top_work_orders.length > 0 && (
                  <div style={{ background: 'var(--bg-card)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Top Costliest Work Orders</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {costMetrics.top_work_orders.map((wo, i) => (
                        <div key={i} onClick={() => { playClick(); navigate(`/work-orders/${wo.wo_number}`); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.15s', flexWrap: 'wrap' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--border-subtle)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-input)'}>
                          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: WO_TYPE_COLORS[wo.type] || '#2563eb', flexShrink: 0 }}>{wo.wo_number}</span>
                          <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[wo.type] || '#6b7280' }}>{wo.type}</span>
                          <span style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 100 }}>{wo.name}</span>
                          <div style={{ display: 'flex', gap: 8, fontSize: 11, flexShrink: 0, flexWrap: 'wrap' }}>
                            <span style={{ color: '#059669', fontWeight: 600 }}>Labour: ${wo.labour_cost.toLocaleString()}</span>
                            <span style={{ color: '#f59e0b', fontWeight: 600 }}>Parts: ${wo.material_cost.toLocaleString()}</span>
                            <span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>Total: ${wo.total.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cost Report Tab */}
      {tab === 'cost-report' && <CostReport />}

      {/* Team Reports Tab */}
      {tab === 'team-reports' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiDollarSign size={16} /> Team Reports <HelpTip text="View cost summaries, time entries, and materials for your team. Filter by date range and export to PDF or Excel. Jobs with $0 cost are hidden from the summary view." />
            </h3>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="date" value={teamReportFrom} onChange={e => setTeamReportFrom(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }} />
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>to</span>
              <input type="date" value={teamReportTo} onChange={e => setTeamReportTo(e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }} />
              <button onClick={() => loadTeamReports(teamReportType, teamReportFrom, teamReportTo)}
                style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                Apply
              </button>
            </div>
          </div>

          {/* Report type selector */}
          <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', borderRadius: 8, padding: 3, border: '1px solid var(--border)', marginBottom: 16 }}>
            {[{ key: 'summary', label: 'Cost Summary' }, { key: 'time', label: 'Time Entries' }, { key: 'materials', label: 'Materials' }].map(r => (
              <button key={r.key} onClick={() => { setTeamReportType(r.key); loadTeamReports(r.key, teamReportFrom, teamReportTo); }}
                style={{ flex: 1, padding: '8px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: teamReportType === r.key ? '#2563eb' : 'transparent', color: teamReportType === r.key ? '#fff' : 'var(--text-secondary)' }}>
                {r.label}
              </button>
            ))}
          </div>

          {/* Export buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button onClick={() => { const p = new URLSearchParams({ format: 'pdf' }); if (teamReportFrom) p.set('date_from', teamReportFrom); if (teamReportTo) p.set('date_to', teamReportTo); window.open(`/api/issues/team-reports/export?${p.toString()}`, '_blank'); }}
              style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiDownload size={12} /> PDF
            </button>
            <button onClick={() => { const p = new URLSearchParams({ format: 'xlsx' }); if (teamReportFrom) p.set('date_from', teamReportFrom); if (teamReportTo) p.set('date_to', teamReportTo); window.open(`/api/issues/team-reports/export?${p.toString()}`, '_blank'); }}
              style={{ padding: '7px 14px', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FiDownload size={12} /> Excel
            </button>
            {teamReportData?.period && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'center', marginLeft: 8 }}>
                {teamReportData.total} records &middot; {teamReportData.period.start} to {teamReportData.period.end}
              </span>
            )}
          </div>

          {/* Loading */}
          {teamReportLoading && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
            </div>
          )}

          {/* Data table */}
          {!teamReportLoading && teamReportData?.data && (
            <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto', maxHeight: 500 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-input)', position: 'sticky', top: 0, zIndex: 1 }}>
                      {teamReportType === 'summary' && ['WO #', 'Name', 'Type', 'Status', 'Assigned', 'Hours', 'Labour $', 'Material $', 'Total $'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '2px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                      ))}
                      {teamReportType === 'time' && ['Employee', 'WO #', 'Work Order', 'Op #', 'Hours', 'Rate', 'Cost', 'Date'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '2px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                      ))}
                      {teamReportType === 'materials' && ['Part', 'Type', 'Supplier', 'Cost', 'Qty', 'WO #', 'Work Order', 'Ordered', 'Delivered', 'Installed'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '2px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {teamReportData.data.length === 0 && (
                      <tr><td colSpan={99} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No data for the selected period</td></tr>
                    )}
                    {teamReportType === 'summary' && teamReportData.data.filter(r => r.total_cost > 0).map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{r.wo_number}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-primary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.wo_name}</td>
                        <td style={{ padding: '8px 12px' }}><span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[r.wo_type] || '#6b7280' }}>{r.wo_type}</span></td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{r.status}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{r.assigned_to_name || '—'}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#8b5cf6' }}>{r.total_hours}h</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#059669' }}>${r.labour_cost}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#f59e0b' }}>${r.material_cost}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#2563eb' }}>${r.total_cost}</td>
                      </tr>
                    ))}
                    {teamReportType === 'time' && teamReportData.data.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--text-primary)' }}>{r.user_name}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>{r.wo_number}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.wo_name}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{r.operation_number || '—'}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#8b5cf6' }}>{(parseFloat(r.total_hours) || 0).toFixed(1)}h</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>${(parseFloat(r.effective_rate) || 0).toFixed(2)}/h</td>
                        <td style={{ padding: '8px 12px', fontWeight: 700, color: '#059669' }}>${(parseFloat(r.cost) || 0).toFixed(2)}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{r.work_date ? formatDate(r.work_date) : formatDate(r.created_at)}</td>
                      </tr>
                    ))}
                    {teamReportType === 'materials' && teamReportData.data.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--text-primary)' }}>{r.part_name}</td>
                        <td style={{ padding: '8px 12px' }}><span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: r.material_type === 'labour' ? 'rgba(124,58,237,0.1)' : r.material_type === 'service' ? 'rgba(2,132,199,0.1)' : 'rgba(55,65,81,0.1)', color: r.material_type === 'labour' ? '#7c3aed' : r.material_type === 'service' ? '#0284c7' : '#374151' }}>{titleCase(r.material_type || 'part')}</span></td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{r.supplier_name || '—'}</td>
                        <td style={{ padding: '8px 12px', fontWeight: 600, color: '#f59e0b' }}>${(parseFloat(r.cost) || 0).toFixed(2)}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{r.quantity || 1}</td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>{r.wo_number}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.wo_name}</td>
                        <td style={{ padding: '8px 12px' }}>{r.ordered ? <span style={{ color: '#16a34a' }}>✓ {r.ordered_date ? formatDate(r.ordered_date) : ''}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td style={{ padding: '8px 12px' }}>{r.delivered ? <span style={{ color: '#16a34a' }}>✓ {r.delivered_date ? formatDate(r.delivered_date) : ''}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td style={{ padding: '8px 12px' }}>{r.installed ? <span style={{ color: '#16a34a' }}>✓ {r.installed_date ? formatDate(r.installed_date) : ''}</span> : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Summary footer */}
              {teamReportType === 'summary' && teamReportData.data.length > 0 && (
                <div style={{ padding: '12px 16px', borderTop: '2px solid var(--border)', background: 'var(--bg-input)', display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, fontWeight: 700 }}>
                  <span style={{ color: '#8b5cf6' }}>Total Hours: {teamReportData.data.reduce((s, r) => s + (r.total_hours || 0), 0).toFixed(1)}h</span>
                  <span style={{ color: '#059669' }}>Labour: ${teamReportData.data.reduce((s, r) => s + (r.labour_cost || 0), 0).toFixed(2)}</span>
                  <span style={{ color: '#f59e0b' }}>Materials: ${teamReportData.data.reduce((s, r) => s + (r.material_cost || 0), 0).toFixed(2)}</span>
                  <span style={{ color: '#2563eb' }}>Total: ${teamReportData.data.reduce((s, r) => s + (r.total_cost || 0), 0).toFixed(2)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Labour Variance Tab */}
      {tab === 'variance' && (() => {
        const vd = varianceData;
        const rows = vd?.data || [];
        const summary = vd?.summary || { total_estimated: 0, total_actual: 0, variance_hours: 0, avg_variance_pct: 0, op_count: 0, confirmed_count: 0 };
        const sorted = [...rows].sort((a, b) => {
          switch (varianceSort) {
            case 'variance_pct_desc': return b.variance_pct - a.variance_pct;
            case 'variance_pct_asc': return a.variance_pct - b.variance_pct;
            case 'actual_desc': return b.actual_hours - a.actual_hours;
            case 'estimated_desc': return b.estimated_hours - a.estimated_hours;
            case 'date_desc': return (b.planned_date || '').localeCompare(a.planned_date || '');
            default: return 0;
          }
        });
        const varianceColor = (pct) => pct > 20 ? '#ef4444' : pct < -20 ? '#16a34a' : pct >= -20 && pct <= 20 ? '#f59e0b' : '#64748b';

        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiClock size={16} /> Labour Variance <HelpTip text="Compare estimated hours against actual time confirmed for each operation. Red = over by more than 20%, amber = within 20%, green = under budget. Helps improve estimation accuracy." />
              </h3>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="date" value={varianceFrom} onChange={e => setVarianceFrom(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }} />
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>to</span>
                <input type="date" value={varianceTo} onChange={e => setVarianceTo(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }} />
                <button onClick={() => loadVariance(varianceFrom, varianceTo)}
                  style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                  Apply
                </button>
              </div>
            </div>

            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
              <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#2563eb' }}>{summary.op_count}</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Operations</div>
              </div>
              <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#8b5cf6' }}>{summary.total_estimated}h</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Estimated</div>
              </div>
              <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981' }}>{summary.total_actual}h</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Actual</div>
              </div>
              <div style={{ padding: '14px 12px', borderRadius: 10, background: `${varianceColor(summary.avg_variance_pct)}10`, border: `1px solid ${varianceColor(summary.avg_variance_pct)}25`, textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: varianceColor(summary.avg_variance_pct) }}>
                  {summary.avg_variance_pct > 0 ? '+' : ''}{summary.avg_variance_pct}%
                </div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Avg Variance</div>
              </div>
              <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>{summary.confirmed_count}</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>With Actuals</div>
              </div>
            </div>

            {/* Sort selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>Sort by:</span>
              <select value={varianceSort} onChange={e => setVarianceSort(e.target.value)}
                style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}>
                <option value="variance_pct_desc">Highest over (%)</option>
                <option value="variance_pct_asc">Most under (%)</option>
                <option value="actual_desc">Most actual hours</option>
                <option value="estimated_desc">Most estimated hours</option>
                <option value="date_desc">Newest planned date</option>
              </select>
            </div>

            {/* Loading */}
            {varianceLoading && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
              </div>
            )}

            {/* Data table */}
            {!varianceLoading && (
              <div style={{ background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', maxHeight: 560 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-input)', position: 'sticky', top: 0, zIndex: 1 }}>
                        {['WO #', 'Name', 'Op', 'Assigned', 'Planned', 'Est', 'Actual', 'Variance', 'Status'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '2px solid var(--border)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.length === 0 && (
                        <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No operations with estimated or actual hours in this period</td></tr>
                      )}
                      {sorted.map((r, i) => {
                        const vColor = varianceColor(r.variance_pct);
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                            onClick={() => { playClick(); navigate(`/work-orders/${r.wo_number}`); }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,99,235,0.03)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>{r.wo_number}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-primary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.wo_name}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-muted)' }}>{r.op_number}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>{r.assigned_to_name || '—'}</td>
                            <td style={{ padding: '8px 12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{r.planned_date ? new Date(typeof r.planned_date === 'string' ? r.planned_date.split('T')[0] + 'T00:00:00' : r.planned_date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }) : '—'}</td>
                            <td style={{ padding: '8px 12px', fontWeight: 600, color: '#8b5cf6' }}>{r.estimated_hours}h</td>
                            <td style={{ padding: '8px 12px', fontWeight: 600, color: '#10b981' }}>{r.actual_hours}h</td>
                            <td style={{ padding: '8px 12px' }}>
                              {r.actual_hours > 0 ? (
                                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: `${vColor}15`, color: vColor, border: `1px solid ${vColor}30`, whiteSpace: 'nowrap' }}>
                                  {r.variance_pct > 0 ? '+' : ''}{r.variance_pct}% ({r.variance_hours > 0 ? '+' : ''}{r.variance_hours}h)
                                </span>
                              ) : (
                                <span style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic' }}>No actuals yet</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-input)', color: 'var(--text-muted)' }}>{r.op_status || '—'}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Weekly Pre-Read Tab */}
      {tab === 'pre-read' && (() => {
        try {
        const data = preReadData;
        const ops = data?.operations || [];
        const members = data?.teamMembers || [];
        const frozen = data?.frozen || false;
        const pr = data?.preRead;

        // Group ops by day
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        // Format a duration in hours into something humans actually read.
        // Anything under an hour shows in minutes ("48m") so 0.8h doesn't
        // look like 80 minutes; whole hours show "1h"; everything else
        // shows the decimal hour ("1.5h").
        const fmtDuration = (h) => {
          if (h == null || isNaN(h)) return '0m';
          const num = parseFloat(h);
          if (!num) return '0m';
          if (num < 1) return `${Math.round(num * 60)}m`;
          if (Math.abs(num - Math.round(num)) < 0.01) return `${Math.round(num)}h`;
          return `${num.toFixed(1)}h`;
        };
        const safeDate = (v) => {
          if (!v) return null;
          try {
            // Plain YYYY-MM-DD with no time component — already a local date.
            if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
            // Anything else (ISO string, Date object) — convert to LOCAL date
            // components. The DB stores DATE values which mysql2 returns as
            // Date objects at midnight server-local time; serialised to JSON
            // they become UTC strings that are a day earlier in non-UTC
            // timezones if you naively split on 'T'.
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          } catch { return null; }
        };
        // Build all 7 days of the week (Mon-Sun) so every day shows, even empty ones.
        const weekStartStr = (pr?.week_start ? safeDate(pr.week_start) : null) || (() => {
          const n = new Date(); n.setHours(12, 0, 0, 0);
          const d = n.getDay(); const diff = d === 0 ? 1 : 8 - d;
          const m = new Date(n); m.setDate(n.getDate() + diff);
          return `${m.getFullYear()}-${String(m.getMonth()+1).padStart(2,'0')}-${String(m.getDate()).padStart(2,'0')}`;
        })();
        const allWeekDays = (() => {
          const out = [];
          const ws = new Date(weekStartStr + 'T12:00:00');
          if (isNaN(ws.getTime())) return out;
          for (let i = 0; i < 7; i++) {
            const d = new Date(ws); d.setDate(ws.getDate() + i);
            out.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
          }
          return out;
        })();

        const dayGroups = {};
        const dayOrder = [];
        allWeekDays.forEach(dk => { dayGroups[dk] = []; dayOrder.push(dk); });
        ops.forEach(op => {
          const id = op.operation_id || op.id;
          const dk = preReadPendingMoves[id] || safeDate(op.planned_date) || 'unplanned';
          if (!dayGroups[dk]) { dayGroups[dk] = []; dayOrder.push(dk); }
          dayGroups[dk].push(op);
        });
        dayOrder.sort((a, b) => a === 'unplanned' ? 1 : b === 'unplanned' ? -1 : a.localeCompare(b));

        // Filter by team member
        const filtered = preReadFilter ? ops.filter(op => String(op.assigned_to || op.user_id) === preReadFilter) : ops;
        const filteredGroups = {};
        const filteredOrder = [];
        allWeekDays.forEach(dk => { filteredGroups[dk] = []; filteredOrder.push(dk); });
        filtered.forEach(op => {
          const id = op.operation_id || op.id;
          const dk = preReadPendingMoves[id] || safeDate(op.planned_date) || 'unplanned';
          if (!filteredGroups[dk]) { filteredGroups[dk] = []; filteredOrder.push(dk); }
          filteredGroups[dk].push(op);
        });
        filteredOrder.sort((a, b) => a === 'unplanned' ? 1 : b === 'unplanned' ? -1 : a.localeCompare(b));

        // Build slice map: parentId -> [all slices of that parent op, sorted by date].
        // Any op with split_parent_id is a slice; the parent itself is also a slice (the first).
        const sliceMap = {};
        ops.forEach(o => {
          const key = o.split_parent_id || o.operation_id || o.id;
          if (!sliceMap[key]) sliceMap[key] = [];
          sliceMap[key].push(o);
        });
        Object.values(sliceMap).forEach(group => group.sort((a, b) => {
          const ad = (a.split_index || 1) - (b.split_index || 1);
          if (ad !== 0) return ad;
          return String(a.planned_date || '').localeCompare(String(b.planned_date || ''));
        }));
        const getSliceGroup = (op) => sliceMap[op.split_parent_id || op.operation_id || op.id] || [op];

        // Warnings: off-day conflicts, unassigned
        const unassigned = ops.filter(op => !op.assigned_to_name && !op.assigned_to && !op.user_id);
        const totalHours = ops.reduce((s, o) => s + (parseFloat(o.estimated_hours) || 0), 0);

        // Queue a date change in-memory. Nothing is sent to the server
        // until "Save as Draft" is clicked.
        const handleReschedule = (opId, newDate) => {
          setPreReadPendingMoves(prev => {
            const orig = (ops.find(o => (o.operation_id || o.id) === opId)?.planned_date) || null;
            const origDate = orig ? (typeof orig === 'string' ? orig.split('T')[0] : new Date(orig).toISOString().split('T')[0]) : null;
            const next = { ...prev };
            if (newDate === origDate) { delete next[opId]; }
            else { next[opId] = newDate; }
            return next;
          });
          setPreReadDateEdit(null);
          playClick();
        };

        // Persist every queued move to the server in one batch, then refetch.
        // On any failure, the failed moves stay queued so the user can retry —
        // only successful ones get dropped from the pending map.
        const handleSaveDraft = async () => {
          const entries = Object.entries(preReadPendingMoves);
          console.log('[pre-read] Save Draft clicked', { entries, pr });
          if (entries.length === 0) { showToast('No pending changes to save'); return; }
          // week_start from the server can come back as a Date object; normalise
          // to YYYY-MM-DD or the endpoint's WHERE clause won't match.
          const rawWs = pr?.week_start || weekStartStr;
          const weekStart = rawWs ? (typeof rawWs === 'string' ? rawWs.split('T')[0] : new Date(rawWs).toISOString().split('T')[0]) : weekStartStr;
          setPreReadDraftSaving(true);
          const stillPending = {};
          const failures = [];
          let saved = 0;
          try {
            for (const [opId, newDate] of entries) {
              try {
                const res = await authFetch('/api/issues/pre-read/reschedule', {
                  method: 'PUT', body: JSON.stringify({ operation_id: parseInt(opId), new_date: newDate, week_start: weekStart })
                });
                const body = await res.json().catch(() => ({}));
                console.log('[pre-read] reschedule response', { opId, newDate, status: res.status, body });
                if (!res.ok) {
                  failures.push(`Op ${opId}: ${body.error || res.status}`);
                  stillPending[opId] = newDate;
                } else {
                  saved++;
                }
              } catch (err) {
                console.error('[pre-read] reschedule fetch failed', err);
                failures.push(`Op ${opId}: ${err.message}`);
                stillPending[opId] = newDate;
              }
            }
            setPreReadPendingMoves(stillPending);
            if (saved > 0) {
              playSuccess();
              showToast(`Saved ${saved} change${saved === 1 ? '' : 's'}${failures.length ? ` — ${failures.length} failed` : ''}`);
            } else if (failures.length > 0) {
              showToast(`Save failed: ${failures[0]}`);
            }
            if (saved > 0) loadPreRead(weekStart, { silent: true });
          } finally {
            setPreReadDraftSaving(false);
          }
        };

        // Effective planned_date for an op, accounting for in-memory moves.
        const effectiveDate = (op) => {
          const id = op.operation_id || op.id;
          return preReadPendingMoves[id] || safeDate(op.planned_date);
        };

        const handleSaveNotes = async () => {
          const weekStart = pr?.week_start || (() => { const n = new Date(); const d = n.getDay(); const diff = d === 0 ? 1 : 8 - d; const m = new Date(n); m.setDate(n.getDate() + diff); return m.toISOString().split('T')[0]; })();
          await authFetch('/api/issues/pre-read/save', { method: 'POST', body: JSON.stringify({ week_start: weekStart, notes: preReadNotes }) });
          playClick();
        };

        const doSubmit = async () => {
          setPreReadSubmitConfirm(false);
          setPreReadSubmitting(true);
          try {
            const weekStart = pr?.week_start || (() => { const n = new Date(); const d = n.getDay(); const diff = d === 0 ? 1 : 8 - d; const m = new Date(n); m.setDate(n.getDate() + diff); return m.toISOString().split('T')[0]; })();
            const res = await authFetch('/api/issues/pre-read/submit', {
              method: 'POST', body: JSON.stringify({ week_start: weekStart, notes: preReadNotes })
            });
            if (res.ok) {
              playSuccess();
              showToast(`Pre-read submitted for ${fmtDFull(weekStart)}`);
              loadPreRead(weekStart, { silent: true });
              loadPreReadHistory();
            } else {
              const err = await res.json().catch(() => ({}));
              showToast(err.error || 'Failed to submit pre-read', 'error');
            }
          } catch (e) {
            showToast('Failed to submit pre-read', 'error');
          }
          setPreReadSubmitting(false);
        };
        const handleSubmit = () => setPreReadSubmitConfirm(true);

        // Calculate capacity per user per day
        const dayOfWeekMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        const getUserDayCapacity = (userId, dateStr) => {
          if (!dateStr || dateStr === 'unplanned') return 0;
          const m = members.find(u => u.id === parseInt(userId));
          if (!m) return 0;
          // Prefer server-computed daily_available (honours roster + blocks); fall back to raw working_hours_*.
          if (m.daily_available && Object.prototype.hasOwnProperty.call(m.daily_available, dateStr)) {
            const v = parseFloat(m.daily_available[dateStr]);
            return Number.isFinite(v) ? v : 0;
          }
          try {
            const d = new Date(dateStr + 'T12:00:00');
            if (isNaN(d.getTime())) return 0;
            const col = `working_hours_${dayOfWeekMap[d.getDay()]}`;
            return parseFloat(m[col]) || 0;
          } catch { return 0; }
        };

        // Per-day capacity summary: { date -> { totalAvailable, totalAllocated, users: { userId -> { available, allocated, name } } } }
        const dayCapacity = {};
        if (!frozen) {
          filteredOrder.forEach(dk => {
            if (dk === 'unplanned') return;
            const dayOps = filteredGroups[dk] || [];
            const usersInDay = {};
            dayOps.forEach(op => {
              const uid = String(op.assigned_to || op.user_id || 0);
              if (!usersInDay[uid]) {
                usersInDay[uid] = { name: op.assigned_to_name || 'Unassigned', allocated: 0, available: getUserDayCapacity(uid, dk) };
              }
              usersInDay[uid].allocated += parseFloat(op.estimated_hours) || 0;
            });
            // Also add team members with no ops on this day
            if (!preReadFilter) {
              members.forEach(m => {
                const uid = String(m.id);
                if (!usersInDay[uid]) {
                  const avail = getUserDayCapacity(uid, dk);
                  if (avail > 0) usersInDay[uid] = { name: m.name, allocated: 0, available: avail };
                }
              });
            }
            const totalAvail = Object.values(usersInDay).reduce((s, u) => s + u.available, 0);
            const totalAlloc = Object.values(usersInDay).reduce((s, u) => s + u.allocated, 0);
            dayCapacity[dk] = { totalAvailable: totalAvail, totalAllocated: totalAlloc, users: usersInDay };
          });
        }

        const openSplitFor = (op) => {
          const group = getSliceGroup(op);
          const parent = group.find(o => !o.split_parent_id) || group[0] || op;
          const total = group.reduce((s, o) => s + (parseFloat(o.estimated_hours) || 0), 0);
          const slicesByDate = {};
          group.forEach(o => {
            const ds = safeDate(o.planned_date);
            if (!ds) return;
            const h = parseFloat(o.estimated_hours) || 0;
            slicesByDate[ds] = { h: Math.floor(h), m: Math.round((h - Math.floor(h)) * 60) };
          });
          setPreReadSplit({
            parentId: parent.operation_id || parent.id,
            issueId: parent.issue_id,
            woNumber: parent.wo_number,
            woName: parent.wo_name,
            opNumber: parent.operation_number,
            opTitle: parent.operation_title || parent.wo_name,
            assignedTo: parent.assigned_to,
            assignedToName: parent.assigned_to_name,
            totalHours: Math.round(total * 100) / 100,
            slicesByDate
          });
        };

        const handleSplitSave = async () => {
          if (!preReadSplit) return;
          const allocations = allWeekDays.map(date => {
            const s = preReadSplit.slicesByDate[date];
            if (!s) return null;
            const hours = (parseFloat(s.h) || 0) + (parseFloat(s.m) || 0) / 60;
            return hours > 0 ? { planned_date: date, hours: Math.round(hours * 100) / 100 } : null;
          }).filter(Boolean);
          if (allocations.length === 0) { showToast('Allocate at least one day', 'error'); return; }
          const sum = allocations.reduce((s, a) => s + a.hours, 0);
          if (Math.abs(sum - preReadSplit.totalHours) > 0.02) {
            showToast(`Allocated ${sum.toFixed(2)}h must match total ${preReadSplit.totalHours.toFixed(2)}h`, 'error');
            return;
          }
          setPreReadSplitSaving(true);
          try {
            const res = await authFetch('/api/issues/pre-read/split', {
              method: 'POST',
              body: JSON.stringify({
                operation_id: preReadSplit.parentId,
                allocations,
                week_start: pr?.week_start || weekStartStr
              })
            });
            if (res.ok) {
              playSuccess();
              showToast(`Split into ${allocations.length} day${allocations.length !== 1 ? 's' : ''}`);
              setPreReadSplit(null);
              loadPreRead(pr?.week_start || weekStartStr, { silent: true });
            } else {
              const err = await res.json().catch(() => ({}));
              showToast(err.error || 'Failed to split', 'error');
            }
          } catch {
            showToast('Failed to split', 'error');
          }
          setPreReadSplitSaving(false);
        };

        const loadAvailableJobs = (typeFilter) => {
          const weekEnd = pr?.week_end || (() => { const n = new Date(); const d = n.getDay(); const diff = d === 0 ? 1 : 8 - d; const m = new Date(n); m.setDate(n.getDate() + diff + 6); return m.toISOString().split('T')[0]; })();
          // Always send week_start too so the server can include jobs whose
          // planned_date sits *before* the pre-read week (overdue carryovers)
          // alongside future and unscheduled ones.
          const weekStartParam = pr?.week_start || weekStartStr;
          const ws = weekStartParam ? (typeof weekStartParam === 'string' ? weekStartParam.split('T')[0] : new Date(weekStartParam).toISOString().split('T')[0]) : '';
          setPreReadAvailLoading(true);
          const qs = typeFilter ? `&type_filter=${typeFilter}` : '';
          authFetch(`/api/issues/pre-read/available-jobs?week_end=${weekEnd}&week_start=${ws}${qs}`).then(r => r.json()).then(data => {
            setPreReadAvailableJobs(Array.isArray(data) ? data : []);
            setPreReadAvailLoading(false);
          }).catch(() => setPreReadAvailLoading(false));
        };

        const handleBringForwardAndSplit = async (job) => {
          const targetDate = preReadBringForward?.date;
          const targetUserId = preReadBringForward?.userId;
          const weekStart = pr?.week_start || weekStartStr;
          if (!targetDate || !targetUserId) return;
          const res = await authFetch('/api/issues/pre-read/reschedule', {
            method: 'PUT',
            body: JSON.stringify({ operation_id: job.operation_id, new_date: targetDate, week_start: weekStart, assigned_to: targetUserId })
          });
          if (!res.ok) { showToast('Failed to move job', 'error'); return; }
          playSuccess();
          setPreReadBringForward(null);
          setPreReadAvailableJobs([]);
          loadPreRead(weekStart, { silent: true });
          const totalHours = parseFloat(job.estimated_hours) || 0;
          setPreReadSplit({
            parentId: job.operation_id,
            issueId: job.issue_id,
            woNumber: job.wo_number,
            woName: job.wo_name,
            opNumber: job.operation_number,
            opTitle: job.operation_title || job.wo_name,
            assignedTo: targetUserId,
            assignedToName: preReadBringForward.userName,
            totalHours: Math.round(totalHours * 100) / 100,
            slicesByDate: { [targetDate]: { h: Math.floor(totalHours), m: Math.round((totalHours % 1) * 60) } }
          });
        };

        const handleBringForward = async (opId, targetDate, targetUserId) => {
          const weekStart = pr?.week_start || (() => { const n = new Date(); const d = n.getDay(); const diff = d === 0 ? 1 : 8 - d; const m = new Date(n); m.setDate(n.getDate() + diff); return m.toISOString().split('T')[0]; })();
          const body = { operation_id: opId, new_date: targetDate, week_start: weekStart };
          if (targetUserId) body.assigned_to = targetUserId;
          const res = await authFetch('/api/issues/pre-read/reschedule', {
            method: 'PUT', body: JSON.stringify(body)
          });
          if (res.ok) { playSuccess(); loadPreRead(weekStart, { silent: true }); setPreReadBringForward(null); setPreReadAvailableJobs([]); }
        };

        const handlePDF = () => {
          const weekStart = pr?.week_start || (() => { const n = new Date(); const d = n.getDay(); const diff = d === 0 ? 1 : 8 - d; const m = new Date(n); m.setDate(n.getDate() + diff); return m.toISOString().split('T')[0]; })();
          window.open(`/api/issues/pre-read/pdf?week_start=${weekStart}`, '_blank');
        };

        const fmtD = (ds) => { if (!ds) return ''; const d = new Date(typeof ds === 'string' ? ds.split('T')[0] + 'T00:00:00' : ds); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }); };
        const fmtDFull = (ds) => { if (!ds) return ''; const d = new Date(typeof ds === 'string' ? ds.split('T')[0] + 'T00:00:00' : ds); return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }); };

        return (
          <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Weekly Pre-Read <HelpTip text="Review your team's planned work for next week. Change dates, fill spare capacity, add notes, then submit to lock the plan. Submitted pre-reads cannot be changed." /> {frozen && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: 'rgba(22,163,74,0.1)', color: '#16a34a', fontWeight: 600, marginLeft: 8 }}><FiLock size={10} style={{ marginRight: 3 }} />Submitted</span>}
                </h3>
                {pr?.week_start && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {fmtDFull(pr.week_start)} — {fmtDFull(pr.week_end)}
                    {pr.submitted_at && <span> &middot; Submitted {fmtDFull(pr.submitted_at)}</span>}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {/* View mode toggle */}
                <div style={{ display: 'flex', gap: 2, background: 'var(--bg-input)', borderRadius: 6, padding: 2, border: '1px solid var(--border)' }}>
                  {[{ v: 'list', label: 'List', icon: FiList }, { v: 'graph', label: 'Graph', icon: FiGrid }].map(opt => {
                    const Icon = opt.icon;
                    const active = preReadViewMode === opt.v;
                    return (
                      <button key={opt.v} onClick={() => setPreReadViewMode(opt.v)}
                        style={{ padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: active ? '#2563eb' : 'transparent', color: active ? '#fff' : 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon size={11} /> {opt.label}
                      </button>
                    );
                  })}
                </div>
                {/* Team member filter */}
                <select value={preReadFilter} onChange={e => setPreReadFilter(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}>
                  <option value="">All team members</option>
                  {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <button onClick={handlePDF} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiDownload size={12} /> PDF
                </button>
                <button onClick={() => window.print()} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiPrinter size={12} /> Print
                </button>
                <button onClick={() => setPreReadShowHistory(!preReadShowHistory)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: preReadShowHistory ? 'rgba(37,99,235,0.08)' : 'var(--bg-card)', color: preReadShowHistory ? '#2563eb' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FiClock size={12} /> History
                </button>
              </div>
            </div>

            {/* Summary cards */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 120px', padding: '12px 16px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#2563eb' }}>{ops.length}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>Operations</div>
              </div>
              <div style={{ flex: '1 1 120px', padding: '12px 16px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981' }}>{totalHours.toFixed(1)}h</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>Total Hours</div>
              </div>
              <div style={{ flex: '1 1 120px', padding: '12px 16px', borderRadius: 10, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#8b5cf6' }}>{members.length}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>Team Members</div>
              </div>
              {unassigned.length > 0 && (
                <div style={{ flex: '1 1 120px', padding: '12px 16px', borderRadius: 10, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#ef4444' }}>{unassigned.length}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>Unassigned</div>
                </div>
              )}
            </div>

            {/* History panel */}
            <AnimatePresence>
              {preReadShowHistory && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  style={{ overflow: 'hidden', marginBottom: 16 }}>
                  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}><FiClock size={14} /> Pre-Read History</div>
                    {preReadHistory.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>No past pre-reads</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                        {preReadHistory.map(h => (
                          <div key={h.id} onClick={() => { loadPreRead(h.week_start); setPreReadShowHistory(false); playClick(); }}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', background: pr?.week_start === h.week_start ? 'rgba(37,99,235,0.06)' : 'transparent' }}>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{fmtD(h.week_start)} — {fmtD(h.week_end)}</div>
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{h.item_count} operations {h.submitted_by_name ? `· by ${h.submitted_by_name}` : ''}</div>
                            </div>
                            <span style={{
                              padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                              background: h.status === 'submitted' ? 'rgba(22,163,74,0.1)' : 'rgba(245,158,11,0.1)',
                              color: h.status === 'submitted' ? '#16a34a' : '#d97706'
                            }}>{h.status === 'submitted' ? 'Submitted' : 'Draft'}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Notes */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 4 }}>Supervisor Notes</label>
              {frozen ? (
                pr?.notes ? <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--bg-input)', border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{pr.notes}</div> : <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>No notes</div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <textarea value={preReadNotes} onChange={e => setPreReadNotes(e.target.value)} placeholder="Add notes for the week (e.g. focus areas, reminders)..."
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, minHeight: 50, resize: 'vertical', fontFamily: 'inherit' }} />
                  <button onClick={handleSaveNotes} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, alignSelf: 'flex-start' }}>Save</button>
                </div>
              )}
            </div>

            {/* Loading */}
            {preReadLoading && (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading pre-read...</div>
              </div>
            )}

            {/* No ops */}
            {!preReadLoading && ops.length === 0 && (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
                <FiFileText size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
                <div style={{ fontSize: 15 }}>No operations planned for next week</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>Operations with planned dates within the week will appear here</div>
              </div>
            )}

            {/* Completion view — shown when submitted */}
            {!preReadLoading && frozen && !preReadShowFrozenDetail && (() => {
              // Calculate next Monday after the submitted week
              const nextMon = (() => {
                const weStr = safeDate(pr?.week_end);
                if (!weStr) return null;
                const we = new Date(weStr + 'T12:00:00');
                if (isNaN(we.getTime())) return null;
                we.setDate(we.getDate() + 1);
                return `${we.getFullYear()}-${String(we.getMonth()+1).padStart(2,'0')}-${String(we.getDate()).padStart(2,'0')}`;
              })();
              const nextSun = (() => {
                if (!nextMon) return null;
                const d = new Date(nextMon + 'T12:00:00');
                if (isNaN(d.getTime())) return null;
                d.setDate(d.getDate() + 6);
                return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              })();
              const uniqueAssignees = new Set(ops.map(o => o.assigned_to_name).filter(Boolean));
              const typeBreakdown = {};
              ops.forEach(o => { typeBreakdown[o.wo_type] = (typeBreakdown[o.wo_type] || 0) + 1; });
              return (
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  style={{ background: 'var(--bg-card)', borderRadius: 16, border: '1px solid rgba(22,163,74,0.2)', padding: 40, textAlign: 'center', marginBottom: 20, position: 'relative', overflow: 'hidden' }}>
                  {/* Background accent */}
                  <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(22,163,74,0.04)' }} />
                  <div style={{ position: 'absolute', bottom: -60, left: -60, width: 180, height: 180, borderRadius: '50%', background: 'rgba(37,99,235,0.04)' }} />

                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.1 }}
                    style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(22,163,74,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', position: 'relative' }}>
                    <FiCheckCircle size={36} color="#16a34a" />
                  </motion.div>
                  <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', position: 'relative' }}>Already Complete</h2>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 24, position: 'relative' }}>
                    The pre-read for next week has already been submitted — <strong>{fmtDFull(pr.week_start)}</strong> to <strong>{fmtDFull(pr.week_end)}</strong>
                  </div>

                  {/* Summary stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 24, maxWidth: 700, margin: '0 auto 24px', position: 'relative' }}>
                    <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.12)' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#2563eb' }}>{ops.length}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Operations Locked</div>
                    </div>
                    <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.12)' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{totalHours.toFixed(1)}h</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total Hours</div>
                    </div>
                    <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#8b5cf6' }}>{uniqueAssignees.size}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Team Members</div>
                    </div>
                    <div style={{ padding: '14px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.12)' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{Object.keys(typeBreakdown).length}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>WO Types</div>
                    </div>
                  </div>

                  {/* Type badges */}
                  {Object.keys(typeBreakdown).length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 24, position: 'relative' }}>
                      {Object.entries(typeBreakdown).map(([type, count]) => (
                        <span key={type} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[type] || '#6b7280' }}>
                          {type}: {count}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Submission details */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 24, flexWrap: 'wrap', padding: '16px 20px', borderRadius: 10, background: 'var(--bg-input)', border: '1px solid var(--border)', maxWidth: 700, margin: '0 auto 20px', position: 'relative' }}>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Week Planned</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>{fmtD(pr?.week_start)} — {fmtD(pr?.week_end)}</div>
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Submitted On</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>{pr.submitted_at ? fmtDFull(pr.submitted_at) : '—'}</div>
                    </div>
                    {nextMon && (
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>Complete — no action required</div>
                      </div>
                    )}
                  </div>

                  {pr.notes && (
                    <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)', maxWidth: 700, margin: '0 auto 20px', textAlign: 'left', position: 'relative' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Supervisor Notes</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5, fontStyle: 'italic' }}>"{pr.notes}"</div>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap', position: 'relative' }}>
                    <button onClick={() => setPreReadShowFrozenDetail(true)}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiFileText size={14} /> View Locked Plan
                    </button>
                    <button onClick={handlePDF}
                      style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiDownload size={14} /> Download PDF
                    </button>
                  </div>
                </motion.div>
              );
            })()}

            {/* Back to completion view button (only when drilling into frozen detail) */}
            {frozen && preReadShowFrozenDetail && (
              <button onClick={() => setPreReadShowFrozenDetail(false)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
                <FiArrowLeft size={13} /> Back to Summary
              </button>
            )}

            {/* Day-by-day breakdown (hidden when completion view is shown) */}
            {!preReadLoading && filtered.length > 0 && (!frozen || preReadShowFrozenDetail) && preReadViewMode === 'list' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {filteredOrder.map(dk => {
                  const dayOps = filteredGroups[dk];
                  const dayH = dayOps.reduce((s, o) => s + (parseFloat(o.estimated_hours) || 0), 0);
                  const dt = dk === 'unplanned' ? 'Unplanned' : (() => {
                    const d = new Date(dk + 'T00:00:00');
                    return `${dayNames[d.getDay()]} ${fmtD(dk)}`;
                  })();
                  return (
                    <div key={dk}>
                      {(() => {
                        const cap = dayCapacity[dk];
                        const spare = cap ? Math.max(0, cap.totalAvailable - cap.totalAllocated) : 0;
                        const pct = cap && cap.totalAvailable > 0 ? Math.min(100, Math.round((cap.totalAllocated / cap.totalAvailable) * 100)) : 0;
                        const barColor = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
                        return (
                      <div style={{ marginBottom: 10, padding: '10px 14px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{dt}</div>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: 'rgba(37,99,235,0.08)', color: '#2563eb' }}>{dayOps.length} ops</span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: 'rgba(16,185,129,0.08)', color: '#10b981' }}>{dayH.toFixed(1)}h allocated</span>
                          {cap && cap.totalAvailable > 0 && (
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: `${barColor}12`, color: barColor }}>{cap.totalAvailable.toFixed(1)}h capacity</span>
                          )}
                          {cap && cap.totalAvailable > 0 && spare > 0.1 && (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: 'rgba(37,99,235,0.1)', color: '#2563eb', marginLeft: 'auto' }}>
                              {spare.toFixed(1)}h unallocated
                            </span>
                          )}
                        </div>
                        {/* Capacity bar */}
                        {cap && cap.totalAvailable > 0 && (
                          <div style={{ marginTop: 6, height: 6, borderRadius: 3, background: 'var(--bg-input)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
                          </div>
                        )}
                        {/* Per-user capacity rows with Add Jobs button */}
                        {cap && Object.keys(cap.users).length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                            {Object.entries(cap.users).sort(([, a], [, b]) => (b.available - b.allocated) - (a.available - a.allocated)).map(([uid, u]) => {
                              const uSpare = Math.max(0, u.available - u.allocated);
                              const uPct = u.available > 0 ? Math.min(100, Math.round((u.allocated / u.available) * 100)) : 0;
                              const uColor = u.allocated >= u.available ? '#ef4444' : uSpare < 1 ? '#f59e0b' : '#10b981';
                              const hasSpare = uSpare > 0.1 && u.available > 0;
                              const uidNum = parseInt(uid);
                              return (
                                <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: 7, background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '0 0 140px', minWidth: 140 }}>
                                    <div style={{ width: 22, height: 22, borderRadius: '50%', background: uColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                                      {u.name.split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase()}
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                                  </div>
                                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--bg-card)', overflow: 'hidden', minWidth: 60 }}>
                                      <div style={{ height: '100%', width: `${uPct}%`, background: uColor, borderRadius: 3, transition: 'width 0.3s' }} />
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: uColor, whiteSpace: 'nowrap', minWidth: 75, textAlign: 'right' }}>
                                      {u.allocated.toFixed(1)}/{u.available.toFixed(1)}h
                                    </span>
                                    {uidNum > 0 && uidNum !== 0 && (
                                      hasSpare && !frozen ? (
                                        <button onClick={() => { setPreReadBringForward({ date: dk, userId: uidNum, userName: u.name, userSpare: uSpare }); setPreReadTypeFilter(''); setPreReadAvailSearch(''); loadAvailableJobs(''); }}
                                          style={{ padding: '4px 9px', borderRadius: 5, border: '1px solid #2563eb', background: 'rgba(37,99,235,0.06)', color: '#2563eb', cursor: 'pointer', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 3 }}>
                                          <FiPlus size={10} /> Add Jobs ({uSpare.toFixed(1)}h)
                                        </button>
                                      ) : !frozen && (
                                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', padding: '3px 8px', whiteSpace: 'nowrap' }}>
                                          {u.allocated >= u.available ? 'Full' : 'OK'}
                                        </span>
                                      )
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                        );
                      })()}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {dayOps.map((op, oi) => {
                          const isEditing = preReadDateEdit?.opId === (op.operation_id || op.id);
                          const priColor = priorityColors[op.wo_priority] || '#6b7280';
                          return (
                            <motion.div key={op.operation_id || op.id || oi} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: oi * 0.02 }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                                background: 'var(--bg-card)', borderRadius: 10, border: '1px solid var(--border)',
                                borderLeft: `4px solid ${WO_TYPE_COLORS[op.wo_type] || '#6b7280'}`,
                                flexWrap: 'wrap'
                              }}>
                              <div style={{ flex: 1, minWidth: 180 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{op.wo_number}</span>
                                  {op.operation_number && <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>/ {op.operation_number}</span>}
                                  <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#fff', background: WO_TYPE_COLORS[op.wo_type] || '#6b7280' }}>{op.wo_type}</span>
                                  <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: `${priColor}15`, color: priColor }}>{op.wo_priority}</span>
                                  {(() => {
                                    const group = getSliceGroup(op);
                                    if (group.length <= 1) return null;
                                    const idx = group.findIndex(o => (o.operation_id || o.id) === (op.operation_id || op.id));
                                    return (
                                      <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}>
                                        Split {idx + 1}/{group.length}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{op.wo_name}</div>
                                {op.operation_title && op.operation_title !== op.wo_name && (
                                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{op.operation_title}</div>
                                )}
                                {(op.asset_tag || op.asset_name) && (
                                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                    {op.asset_tag && <span style={{ fontWeight: 600 }}>{op.asset_tag}</span>}
                                    {op.asset_tag && op.asset_name && ' — '}{op.asset_name}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {/* Hours */}
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981', whiteSpace: 'nowrap' }}>{(parseFloat(op.estimated_hours) || 0).toFixed(1)}h</span>
                                {/* Assigned */}
                                <span style={{ fontSize: 11, fontWeight: 600, color: op.assigned_to_name ? 'var(--text-secondary)' : '#ef4444', padding: '2px 8px', borderRadius: 6, background: op.assigned_to_name ? 'var(--bg-input)' : 'rgba(239,68,68,0.08)', border: op.assigned_to_name ? 'none' : '1px solid rgba(239,68,68,0.2)' }}>
                                  {op.assigned_to_name || 'Unassigned'}
                                </span>
                                {/* Status */}
                                {(op.op_status || op.status) && (
                                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-input)', color: 'var(--text-muted)' }}>{op.op_status || op.status}</span>
                                )}
                                {/* Date change button (draft only) */}
                                {!frozen && (
                                  isEditing ? (
                                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                                      <input type="date" value={preReadDateEdit?.newDate || (dk !== 'unplanned' ? dk : '')}
                                        onChange={e => setPreReadDateEdit(prev => ({ ...prev, newDate: e.target.value }))}
                                        style={{ padding: '3px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11 }} />
                                      <button disabled={!preReadDateEdit?.newDate || preReadDateEdit.newDate === dk}
                                        onClick={() => { if (preReadDateEdit?.newDate && preReadDateEdit.newDate !== dk) handleReschedule(op.operation_id || op.id, preReadDateEdit.newDate); }}
                                        style={{ padding: '3px 8px', borderRadius: 5, border: 'none', background: preReadDateEdit?.newDate && preReadDateEdit.newDate !== dk ? '#16a34a' : '#94a3b8', color: '#fff', cursor: preReadDateEdit?.newDate && preReadDateEdit.newDate !== dk ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</button>
                                      <button onClick={() => setPreReadDateEdit(null)} style={{ padding: '3px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, lineHeight: 1 }}>✕</button>
                                    </div>
                                  ) : (
                                    <button onClick={() => setPreReadDateEdit({ opId: op.operation_id || op.id, currentDate: dk })}
                                      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d97706', background: 'rgba(217,119,6,0.06)', color: '#d97706', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                                      <FiCalendar size={10} /> Change Date
                                    </button>
                                  )
                                )}
                                {/* Split across days (draft only) */}
                                {!frozen && (
                                  <button onClick={() => openSplitFor(op)}
                                    style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #8b5cf6', background: 'rgba(139,92,246,0.06)', color: '#8b5cf6', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <FiGrid size={10} /> Split
                                  </button>
                                )}
                                {/* View WO */}
                                <button onClick={() => { playClick(); navigate(`/work-orders/${op.wo_number}`); }}
                                  style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                                  <FiMaximize2 size={10} /> View
                                </button>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Swim-lane graph view */}
            {!preReadLoading && filtered.length > 0 && (!frozen || preReadShowFrozenDetail) && preReadViewMode === 'graph' && (() => {
              const byUserByDay = {};
              const userTotals = {};
              filtered.forEach(op => {
                const uid = String(op.assigned_to || op.user_id || 'unassigned');
                const date = effectiveDate(op);
                if (!date) return;
                if (!byUserByDay[uid]) byUserByDay[uid] = {};
                if (!byUserByDay[uid][date]) byUserByDay[uid][date] = [];
                byUserByDay[uid][date].push(op);
                userTotals[uid] = (userTotals[uid] || 0) + (parseFloat(op.estimated_hours) || 0);
              });
              const memberList = preReadFilter ? members.filter(m => String(m.id) === preReadFilter) : members;
              const hasUnassigned = !!byUserByDay['unassigned'] && !preReadFilter;
              const dayHeader = allWeekDays.map(date => {
                const d = new Date(date + 'T12:00:00');
                const dayName = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
                const totalH = filtered.filter(op => effectiveDate(op) === date).reduce((s, op) => s + (parseFloat(op.estimated_hours) || 0), 0);
                const totalCap = memberList.reduce((s, m) => s + (getUserDayCapacity(String(m.id), date) || 0), 0);
                return { date, dayName, dayNum: d.getDate(), monthShort: d.toLocaleDateString('en-AU', { month: 'short' }), totalH, totalCap, over: totalCap > 0 && totalH > totalCap + 0.01 };
              });
              const renderTile = (op) => {
                const hours = parseFloat(op.estimated_hours) || 0;
                const tileBg = WO_TYPE_COLORS[op.wo_type] || '#6b7280';
                const grp = getSliceGroup(op);
                const isSlice = grp.length > 1;
                const idx = isSlice ? grp.findIndex(o => (o.operation_id || o.id) === (op.operation_id || op.id)) + 1 : 0;
                const opId = op.operation_id || op.id;
                const hasPending = preReadPendingMoves[opId] !== undefined;
                return (
                  <button key={opId}
                    draggable={!frozen}
                    onDragStart={e => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(opId));
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setPreReadTilePopover({ opId, op, x: rect.left, y: rect.bottom + 4 });
                    }}
                    title={`${op.wo_number} / ${op.operation_number || ''} — ${op.wo_name || ''} — ${fmtDuration(hours)}${op.assigned_to_name ? ' · ' + op.assigned_to_name : ''}${hasPending ? ' (unsaved move)' : ''}`}
                    style={{ padding: '4px 6px', borderRadius: 5, border: hasPending ? '2px dashed #fbbf24' : 'none', cursor: frozen ? 'pointer' : 'grab', background: tileBg, color: '#fff', fontSize: 10, fontWeight: 700, textAlign: 'left', minHeight: 22, boxShadow: '0 1px 2px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, overflow: 'hidden' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                      {op.wo_number}{op.operation_number ? `/${op.operation_number}` : ''}{isSlice && <sup style={{ marginLeft: 2, fontSize: 8, opacity: 0.85 }}>{idx}/{grp.length}</sup>}
                    </span>
                    <span style={{ whiteSpace: 'nowrap', fontSize: 10 }}>{fmtDuration(hours)}</span>
                  </button>
                );
              };

              // Build {opId -> op} so we can resolve the dragged operation on drop.
              const opIndex = {};
              filtered.forEach(op => { opIndex[op.operation_id || op.id] = op; });
              const handleDayDrop = (e, targetDate, targetUserId) => {
                e.preventDefault();
                const opId = parseInt(e.dataTransfer.getData('text/plain'));
                const op = opIndex[opId];
                if (!op) return;
                const currentDate = effectiveDate(op);
                const currentUid = String(op.assigned_to || op.user_id || 'unassigned');
                if (currentDate === targetDate && currentUid === String(targetUserId || currentUid)) return;
                // Compute projected hours on the target day for that user.
                const userDayOps = ((byUserByDay[String(targetUserId || 'unassigned')] || {})[targetDate] || []).filter(o => (o.operation_id || o.id) !== opId);
                const projected = userDayOps.reduce((s, o) => s + (parseFloat(o.estimated_hours) || 0), 0) + (parseFloat(op.estimated_hours) || 0);
                const cap = targetUserId ? (getUserDayCapacity(String(targetUserId), targetDate) || 0) : 0;
                const overBy = cap > 0 && projected > cap + 0.01 ? projected - cap : 0;
                const userName = (members.find(m => String(m.id) === String(targetUserId)) || {}).name || 'Unassigned';
                setPreReadDragConfirm({ opId, op, newDate: targetDate, userName, overBy, projected, cap });
              };
              return (
                <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
                  <div style={{ minWidth: 960, display: 'grid', gridTemplateColumns: '170px repeat(7, 1fr)', gap: 4 }}>
                    <div style={{ padding: '8px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Team</div>
                    {dayHeader.map(d => (
                      <div key={d.date} style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--bg-card)', border: `1px solid ${d.over ? 'rgba(239,68,68,0.35)' : 'var(--border)'}` }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{d.dayName}</div>
                        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{d.dayNum} {d.monthShort}</div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: d.over ? '#ef4444' : 'var(--text-muted)', marginTop: 2 }}>
                          {d.totalH.toFixed(1)}h {d.totalCap > 0 && <>/ {d.totalCap.toFixed(1)}h</>}
                        </div>
                      </div>
                    ))}
                    {memberList.map(m => {
                      const weekCap = allWeekDays.reduce((s, date) => s + (getUserDayCapacity(String(m.id), date) || 0), 0);
                      const userTotal = userTotals[String(m.id)] || 0;
                      const initials = (m.name || '').split(' ').map(p => p[0]).join('').substring(0, 2).toUpperCase();
                      return (
                        <React.Fragment key={m.id}>
                          {(() => {
                            const weekPct = weekCap > 0 ? Math.min(100, Math.round((userTotal / weekCap) * 100)) : (userTotal > 0 ? 100 : 0);
                            const weekBar = userTotal > weekCap + 0.01 ? '#ef4444' : weekPct >= 70 ? '#f59e0b' : '#10b981';
                            return (
                              <div style={{ padding: '10px 12px', borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: m.avatar_color || '#6b7280', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                                </div>
                                <div style={{ marginLeft: 36, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                  <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-input)', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.min(100, weekPct)}%`, background: weekBar, transition: 'width 0.2s' }} />
                                  </div>
                                  <div style={{ fontSize: 10, fontWeight: 600, color: userTotal > weekCap + 0.01 ? '#ef4444' : 'var(--text-muted)' }}>
                                    {userTotal.toFixed(1)}h / {weekCap.toFixed(1)}h{weekCap > 0 && <> · {weekPct}%</>}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {allWeekDays.map(date => {
                            const dayOps = (byUserByDay[String(m.id)] && byUserByDay[String(m.id)][date]) || [];
                            const avail = getUserDayCapacity(String(m.id), date);
                            const alloc = dayOps.reduce((s, op) => s + (parseFloat(op.estimated_hours) || 0), 0);
                            const isOff = avail === 0;
                            const spare = Math.max(0, avail - alloc);
                            const pct = avail > 0 ? Math.min(100, Math.round((alloc / avail) * 100)) : (alloc > 0 ? 100 : 0);
                            const barColor = alloc > avail + 0.01 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#10b981';
                            return (
                              <div key={date}
                                onDragOver={frozen ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                onDrop={frozen ? undefined : (e) => handleDayDrop(e, date, m.id)}
                                style={{
                                minHeight: 80, padding: 4, borderRadius: 8,
                                background: isOff ? 'repeating-linear-gradient(45deg, var(--bg-input), var(--bg-input) 6px, transparent 6px, transparent 12px)' : 'var(--bg-card)',
                                border: `1px solid ${alloc > avail + 0.01 ? 'rgba(239,68,68,0.35)' : 'var(--border)'}`,
                                display: 'flex', flexDirection: 'column', gap: 2
                              }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {dayOps.map(renderTile)}
                                  {!frozen && !isOff && (
                                    <button onClick={() => { setPreReadBringForward({ date, userId: m.id, userName: m.name, userSpare: spare }); setPreReadTypeFilter(''); setPreReadAvailSearch(''); loadAvailableJobs(''); }}
                                      style={{ padding: '3px 6px', borderRadius: 5, border: '1px dashed var(--border)', background: 'transparent', color: spare > 0.1 ? 'var(--text-muted)' : '#ef4444', cursor: 'pointer', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                                      <FiPlus size={9} /> Add {spare > 0.1 ? `(${spare.toFixed(1)}h)` : '(over)'}
                                    </button>
                                  )}
                                </div>
                                {avail > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
                                    <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-input)', overflow: 'hidden' }}>
                                      <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: barColor, transition: 'width 0.2s' }} />
                                    </div>
                                    <div style={{ fontSize: 9, fontWeight: 600, color: barColor, textAlign: 'right' }}>
                                      {alloc.toFixed(1)}/{avail.toFixed(1)}h
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                    {hasUnassigned && (
                      <React.Fragment>
                        <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FiUserX size={16} color="#ef4444" />
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444' }}>Unassigned</div>
                        </div>
                        {allWeekDays.map(date => {
                          const dayOps = (byUserByDay['unassigned'] && byUserByDay['unassigned'][date]) || [];
                          return (
                            <div key={date}
                              onDragOver={frozen ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                              onDrop={frozen ? undefined : (e) => handleDayDrop(e, date, null)}
                              style={{ minHeight: 60, padding: 4, borderRadius: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {dayOps.map(renderTile)}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Tile action popover (graph view) */}
            <AnimatePresence>
              {preReadTilePopover && (
                <>
                  <div onClick={() => setPreReadTilePopover(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 980 }} />
                  <motion.div initial={{ opacity: 0, scale: 0.95, y: -4 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                    onClick={e => e.stopPropagation()}
                    style={{
                      position: 'fixed',
                      left: Math.min(preReadTilePopover.x, window.innerWidth - 220),
                      top: Math.min(preReadTilePopover.y, window.innerHeight - 220),
                      zIndex: 985, width: 210,
                      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10,
                      boxShadow: '0 12px 32px rgba(0,0,0,0.25)', overflow: 'hidden'
                    }}>
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 11, fontFamily: 'monospace', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {preReadTilePopover.op.wo_number} / {preReadTilePopover.op.operation_number}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {preReadTilePopover.op.wo_name}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                        {(parseFloat(preReadTilePopover.op.estimated_hours) || 0).toFixed(1)}h · {preReadTilePopover.op.assigned_to_name || 'Unassigned'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {!frozen && (
                        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, alignItems: 'center' }}>
                          <FiCalendar size={13} color="#d97706" />
                          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Move to</span>
                          <input type="date" value={preReadTilePopover.draftDate ?? (safeDate(preReadTilePopover.op.planned_date) || '')}
                            onChange={e => setPreReadTilePopover(p => p ? { ...p, draftDate: e.target.value } : p)}
                            style={{ flex: 1, padding: '4px 6px', borderRadius: 5, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 11 }} />
                          {(() => {
                            const curr = safeDate(preReadTilePopover.op.planned_date) || '';
                            const draft = preReadTilePopover.draftDate ?? curr;
                            const canApply = !!draft && draft !== curr;
                            return (
                              <button disabled={!canApply}
                                onClick={async () => {
                                  if (!canApply) return;
                                  await handleReschedule(preReadTilePopover.opId, draft);
                                  if (draft < allWeekDays[0] || draft > allWeekDays[allWeekDays.length - 1]) {
                                    showToast(`Moved to ${draft} (out of current week)`);
                                  }
                                  setPreReadTilePopover(null);
                                }}
                                style={{ padding: '3px 8px', borderRadius: 5, border: 'none', background: canApply ? '#16a34a' : '#94a3b8', color: '#fff', cursor: canApply ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</button>
                            );
                          })()}
                        </div>
                      )}
                      {!frozen && (
                        <button onClick={() => { openSplitFor(preReadTilePopover.op); setPreReadTilePopover(null); }}
                          style={{ padding: '10px 12px', border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FiGrid size={13} color="#8b5cf6" /> Split Across Days
                        </button>
                      )}
                      <button onClick={() => { playClick(); navigate(`/work-orders/${preReadTilePopover.op.wo_number}`); setPreReadTilePopover(null); }}
                        style={{ padding: '10px 12px', border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FiMaximize2 size={13} color="#2563eb" /> View Work Order
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* Bring Forward Modal */}
            <AnimatePresence>
              {preReadBringForward && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={() => { setPreReadBringForward(null); setPreReadAvailableJobs([]); }}
                  style={{ position: 'fixed', inset: 0, zIndex: 990, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                  <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                    onClick={e => e.stopPropagation()}
                    style={{ background: 'var(--bg-card)', borderRadius: 14, width: '100%', maxWidth: 650, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 16px 48px rgba(0,0,0,0.2)' }}>
                    {/* Header */}
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FiPlus size={16} color="#2563eb" /> Add Jobs {preReadBringForward.userName ? `for ${preReadBringForward.userName}` : ''}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                          {preReadBringForward.userName && preReadBringForward.userSpare != null ? (
                            <>Moving into <strong>{(() => { const d = new Date(preReadBringForward.date + 'T00:00:00'); return d.toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'short' }); })()}</strong> &middot; <span style={{ color: '#2563eb', fontWeight: 700 }}>{preReadBringForward.userSpare.toFixed(1)}h free</span></>
                          ) : (
                            <>Select a job to move into <strong>{(() => { const d = new Date(preReadBringForward.date + 'T00:00:00'); return d.toLocaleDateString('en-AU', { weekday: 'long', day: '2-digit', month: 'short' }); })()}</strong></>
                          )}
                        </div>
                      </div>
                      <button onClick={() => { setPreReadBringForward(null); setPreReadAvailableJobs([]); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                    </div>
                    {/* Filters: type + search + sort */}
                    <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {[{ val: '', label: 'All Types' }, { val: 'PM01', label: 'PM01' }, { val: 'PM03', label: 'PM03' }].map(f => (
                          <button key={f.val} onClick={() => { setPreReadTypeFilter(f.val); loadAvailableJobs(f.val); }}
                            style={{
                              padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              border: `1px solid ${preReadTypeFilter === f.val ? '#2563eb' : 'var(--border)'}`,
                              background: preReadTypeFilter === f.val ? 'rgba(37,99,235,0.08)' : 'transparent',
                              color: preReadTypeFilter === f.val ? '#2563eb' : 'var(--text-secondary)'
                            }}>
                            {f.label}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <FiSearch size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                          <input type="text" value={preReadAvailSearch} onChange={e => setPreReadAvailSearch(e.target.value)}
                            placeholder="Search WO name or asset..."
                            style={{ width: '100%', padding: '6px 10px 6px 28px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box' }} />
                          {preReadAvailSearch && (
                            <button onClick={() => setPreReadAvailSearch('')}
                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                              <FiX size={12} />
                            </button>
                          )}
                        </div>
                        <select value={preReadAvailSort} onChange={e => setPreReadAvailSort(e.target.value)}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 12 }}>
                          <option value="due_asc">Oldest first</option>
                          <option value="due_desc">Newest first</option>
                          <option value="wo_asc">WO number ↑</option>
                          <option value="wo_desc">WO number ↓</option>
                          <option value="hours_desc">Most hours</option>
                          <option value="hours_asc">Least hours</option>
                          <option value="resched_desc">Most rescheduled</option>
                        </select>
                      </div>
                    </div>
                    {/* Job list */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
                      {preReadAvailLoading ? (
                        <div style={{ textAlign: 'center', padding: 30 }}>
                          <div style={{ width: 24, height: 24, border: '3px solid var(--border)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                        </div>
                      ) : (() => {
                        const q = preReadAvailSearch.trim().toLowerCase();
                        const filteredJobs = preReadAvailableJobs
                          .filter(j => j.wo_type !== 'PM02')
                          .filter(j => {
                            if (!q) return true;
                            const hay = `${j.wo_name || ''} ${j.asset_name || ''} ${j.asset_tag || ''} ${j.wo_number || ''}`.toLowerCase();
                            return hay.includes(q);
                          });
                        const sortKey = preReadAvailSort;
                        const dateVal = j => {
                          const v = j.due_date || j.planned_date;
                          if (!v) return sortKey === 'due_asc' ? Infinity : -Infinity;
                          return new Date(typeof v === 'string' ? v.slice(0, 10) : v).getTime();
                        };
                        const sortedJobs = [...filteredJobs].sort((a, b) => {
                          if (sortKey === 'due_asc') return dateVal(a) - dateVal(b);
                          if (sortKey === 'due_desc') return dateVal(b) - dateVal(a);
                          if (sortKey === 'wo_asc') return String(a.wo_number || '').localeCompare(String(b.wo_number || ''));
                          if (sortKey === 'wo_desc') return String(b.wo_number || '').localeCompare(String(a.wo_number || ''));
                          if (sortKey === 'hours_desc') return (parseFloat(b.estimated_hours) || 0) - (parseFloat(a.estimated_hours) || 0);
                          if (sortKey === 'hours_asc') return (parseFloat(a.estimated_hours) || 0) - (parseFloat(b.estimated_hours) || 0);
                          if (sortKey === 'resched_desc') return (parseInt(b.reschedule_count) || 0) - (parseInt(a.reschedule_count) || 0);
                          return 0;
                        });
                        return sortedJobs.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>
                            {q ? `No jobs match "${preReadAvailSearch}"` : 'No available jobs to bring forward'}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {sortedJobs.map(job => {
                            const jobHours = parseFloat(job.estimated_hours) || 0;
                            const fits = preReadBringForward.userSpare == null || jobHours <= preReadBringForward.userSpare + 0.01;
                            return (
                              <div key={job.operation_id} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                                borderRadius: 8, border: `1px solid ${fits ? 'var(--border)' : 'rgba(139,92,246,0.3)'}`, background: 'var(--bg-secondary)',
                                borderLeft: `3px solid ${WO_TYPE_COLORS[job.wo_type] || '#6b7280'}`
                              }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{job.wo_number}{job.operation_number ? ` / ${job.operation_number}` : ''}</span>
                                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: '#fff', background: WO_TYPE_COLORS[job.wo_type] || '#6b7280' }}>{job.wo_type}</span>
                                    <span style={{ fontSize: 10, fontWeight: 600, color: fits ? '#10b981' : '#8b5cf6' }}>{jobHours.toFixed(1)}h</span>
                                    {!fits && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}>Needs splitting</span>}
                                    {parseInt(job.reschedule_count) > 0 && <span title={`Rescheduled ${job.reschedule_count} time(s)`} style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'rgba(239,68,68,0.12)', color: '#dc2626' }}>↻{job.reschedule_count}</span>}
                                  </div>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.wo_name}</div>
                                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                                    {job.assigned_to_name || 'Unassigned'}
                                    {job.planned_date && <> &middot; Currently: {fmtD(job.planned_date)}</>}
                                    {!job.planned_date && <> &middot; Unplanned</>}
                                  </div>
                                </div>
                                {fits ? (
                                  <button onClick={() => handleBringForward(job.operation_id, preReadBringForward.date, preReadBringForward.userId)}
                                    style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FiArrowRight size={12} /> {preReadBringForward.userName ? 'Assign' : 'Move Here'}
                                  </button>
                                ) : (
                                  <button onClick={() => handleBringForwardAndSplit(job)}
                                    style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FiGrid size={12} /> Split
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          </div>
                        );
                      })()}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Split-across-days modal */}
            <AnimatePresence>
              {preReadSplit && (() => {
                const allocatedSum = allWeekDays.reduce((s, dt) => {
                  const slc = preReadSplit.slicesByDate[dt];
                  if (!slc) return s;
                  return s + (parseFloat(slc.h) || 0) + (parseFloat(slc.m) || 0) / 60;
                }, 0);
                const target = preReadSplit.totalHours;
                const diff = Math.round((allocatedSum - target) * 100) / 100;
                const canSave = Math.abs(diff) < 0.02 && allWeekDays.some(dt => {
                  const s = preReadSplit.slicesByDate[dt];
                  return s && ((parseFloat(s.h) || 0) + (parseFloat(s.m) || 0) / 60 > 0);
                });
                const updateSlice = (date, field, value) => {
                  setPreReadSplit(prev => {
                    const slices = { ...prev.slicesByDate };
                    const curr = slices[date] || { h: 0, m: 0 };
                    slices[date] = { ...curr, [field]: value };
                    return { ...prev, slicesByDate: slices };
                  });
                };
                const clearSlice = (date) => {
                  setPreReadSplit(prev => {
                    const slices = { ...prev.slicesByDate };
                    delete slices[date];
                    return { ...prev, slicesByDate: slices };
                  });
                };
                // Compute user's free capacity per day (excluding this op's own hours on that day)
                const userId = preReadSplit.assignedTo;
                const member = members.find(m => m.id === parseInt(userId));
                const dayOfWeekMap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                const capFor = (date) => {
                  if (!member) return { avail: 0, otherAlloc: 0 };
                  const d = new Date(date + 'T12:00:00');
                  if (isNaN(d.getTime())) return { avail: 0, otherAlloc: 0 };
                  let avail;
                  if (member.daily_available && Object.prototype.hasOwnProperty.call(member.daily_available, date)) {
                    const v = parseFloat(member.daily_available[date]);
                    avail = Number.isFinite(v) ? v : 0;
                  } else {
                    const col = `working_hours_${dayOfWeekMap[d.getDay()]}`;
                    avail = parseFloat(member[col]) || 0;
                  }
                  // Other allocations = sum of ops on that date for this user, minus any existing slice of this split group
                  const groupIds = new Set(getSliceGroup({ operation_id: preReadSplit.parentId, split_parent_id: null }).map(o => o.operation_id || o.id));
                  const otherAlloc = ops
                    .filter(o => safeDate(o.planned_date) === date && String(o.assigned_to || o.user_id) === String(userId) && !groupIds.has(o.operation_id || o.id))
                    .reduce((s, o) => s + (parseFloat(o.estimated_hours) || 0), 0);
                  return { avail, otherAlloc };
                };
                return (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={() => setPreReadSplit(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 995 }}>
                    <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
                      onClick={e => e.stopPropagation()}
                      style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
                      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FiGrid size={16} color="#8b5cf6" /> Split Operation Across Days
                          </h3>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                            {preReadSplit.woNumber} / {preReadSplit.opNumber} &middot; {preReadSplit.assignedToName || 'Unassigned'}
                          </div>
                        </div>
                        <button onClick={() => setPreReadSplit(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                      </div>
                      <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--border)', fontSize: 13, color: 'var(--text-secondary)' }}>
                        {preReadSplit.opTitle}
                      </div>
                      <div style={{ padding: '12px 22px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {allWeekDays.map(date => {
                          const d = new Date(date + 'T12:00:00');
                          const dayLabel = isNaN(d.getTime()) ? date : `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]} ${d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}`;
                          const slice = preReadSplit.slicesByDate[date];
                          const h = slice?.h ?? '';
                          const m = slice?.m ?? '';
                          const { avail, otherAlloc } = capFor(date);
                          const sliceHours = slice ? ((parseFloat(slice.h) || 0) + (parseFloat(slice.m) || 0) / 60) : 0;
                          const free = Math.max(0, avail - otherAlloc);
                          const over = avail > 0 && sliceHours > free + 0.01;
                          return (
                            <div key={date} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--bg-input)', border: `1px solid ${over ? '#ef4444' : 'var(--border)'}` }}>
                              <div style={{ minWidth: 110, fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{dayLabel}</div>
                              <div style={{ flex: 1, fontSize: 11, color: over ? '#ef4444' : 'var(--text-muted)' }}>
                                {avail > 0
                                  ? (over ? `Over capacity (${free.toFixed(1)}h free)` : `${free.toFixed(1)}h free of ${avail.toFixed(1)}h`)
                                  : 'Off day'}
                              </div>
                              <input type="number" min="0" max="24" step="1" value={h}
                                onChange={e => updateSlice(date, 'h', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0))}
                                style={{ width: 50, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, textAlign: 'right' }} />
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>h</span>
                              <select value={m === '' ? '' : String(m)}
                                onChange={e => updateSlice(date, 'm', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                                style={{ padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12 }}>
                                <option value="0">00</option>
                                <option value="15">15</option>
                                <option value="30">30</option>
                                <option value="45">45</option>
                              </select>
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>m</span>
                              <button onClick={() => clearSlice(date)} disabled={!slice}
                                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: slice ? 'var(--text-secondary)' : 'var(--text-muted)', cursor: slice ? 'pointer' : 'not-allowed', fontSize: 11 }}>
                                Clear
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                          Allocated <strong style={{ color: Math.abs(diff) < 0.02 ? '#16a34a' : '#d97706' }}>{allocatedSum.toFixed(2)}h</strong> of <strong>{target.toFixed(2)}h</strong>
                          {Math.abs(diff) >= 0.02 && <span style={{ color: '#d97706', marginLeft: 6 }}>({diff > 0 ? '+' : ''}{diff.toFixed(2)}h)</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button onClick={() => setPreReadSplit(null)}
                            style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                            Cancel
                          </button>
                          <motion.button whileTap={{ scale: 0.97 }} onClick={handleSplitSave} disabled={!canSave || preReadSplitSaving}
                            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: canSave ? '#8b5cf6' : '#94a3b8', color: '#fff', cursor: canSave && !preReadSplitSaving ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: preReadSplitSaving ? 0.6 : 1 }}>
                            <FiGrid size={13} /> {preReadSplitSaving ? 'Saving...' : 'Save Split'}
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })()}
            </AnimatePresence>

            {/* Submit confirmation modal */}
            <AnimatePresence>
              {preReadSubmitConfirm && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={() => setPreReadSubmitConfirm(false)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 990 }}>
                  <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
                    onClick={e => e.stopPropagation()}
                    style={{ background: 'var(--bg-card)', borderRadius: 16, width: '100%', maxWidth: 440, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
                    <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Submit Pre-Read?</h3>
                      <button onClick={() => setPreReadSubmitConfirm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}><FiX size={18} /></button>
                    </div>
                    <div style={{ padding: '18px 22px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>Week</span>
                          <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{fmtDFull(pr?.week_start || weekStartStr)} — {fmtDFull(pr?.week_end || allWeekDays[allWeekDays.length - 1])}</span>
                        </div>
                        <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>Operations</span>
                          <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word' }}>{ops.length} ({totalHours.toFixed(1)}h)</span>
                        </div>
                        {preReadNotes && (
                          <div style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                            <span style={{ fontWeight: 600, color: 'var(--text-secondary)', minWidth: 110, flexShrink: 0 }}>Notes</span>
                            <span style={{ color: 'var(--text-primary)', wordBreak: 'break-word', fontStyle: 'italic' }}>"{preReadNotes}"</span>
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
                          Once submitted, dates are locked and the team will be notified.
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                        <button onClick={() => setPreReadSubmitConfirm(false)}
                          style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                          Cancel
                        </button>
                        <motion.button whileTap={{ scale: 0.97 }} onClick={doSubmit} disabled={preReadSubmitting}
                          style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: preReadSubmitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, opacity: preReadSubmitting ? 0.6 : 1 }}>
                          <FiLock size={13} /> {preReadSubmitting ? 'Submitting...' : 'Confirm'}
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Drag-drop confirmation modal */}
            <AnimatePresence>
              {preReadDragConfirm && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={() => setPreReadDragConfirm(null)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 995 }}>
                  <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
                    onClick={e => e.stopPropagation()}
                    style={{ background: 'var(--bg-card)', borderRadius: 14, width: '100%', maxWidth: 420, border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
                      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Move work order?</h3>
                    </div>
                    <div style={{ padding: '16px 20px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      <div><strong style={{ color: 'var(--text-primary)' }}>{preReadDragConfirm.op.wo_number}</strong> — {preReadDragConfirm.op.wo_name || ''}</div>
                      <div style={{ marginTop: 8 }}>
                        Move to <strong style={{ color: 'var(--text-primary)' }}>{new Date(preReadDragConfirm.newDate + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' })}</strong> for <strong style={{ color: 'var(--text-primary)' }}>{preReadDragConfirm.userName}</strong>.
                      </div>
                      {preReadDragConfirm.overBy > 0 && (
                        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#dc2626', fontSize: 12, fontWeight: 600 }}>
                          ⚠ This would put {preReadDragConfirm.userName} at {preReadDragConfirm.projected.toFixed(1)}h — {preReadDragConfirm.overBy.toFixed(1)}h over their {preReadDragConfirm.cap.toFixed(1)}h day.
                        </div>
                      )}
                      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>Change is kept locally until you click <strong>Save as Draft</strong>.</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
                      <button onClick={() => setPreReadDragConfirm(null)}
                        style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Cancel</button>
                      <button onClick={() => { handleReschedule(preReadDragConfirm.opId, preReadDragConfirm.newDate); setPreReadDragConfirm(null); }}
                        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: preReadDragConfirm.overBy > 0 ? '#ef4444' : '#16a34a', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
                        {preReadDragConfirm.overBy > 0 ? 'Move anyway' : 'Confirm move'}
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit bar */}
            {!frozen && ops.length > 0 && !preReadLoading && (() => {
              // Plan is valid only when every day sits at-or-under each member's capacity.
              const allWithEffective = ops.map(op => ({ ...op, _effDate: effectiveDate(op), _uid: String(op.assigned_to || op.user_id || 'unassigned') }));
              const overCapDays = [];
              members.forEach(m => {
                allWeekDays.forEach(date => {
                  const cap = getUserDayCapacity(String(m.id), date) || 0;
                  if (cap <= 0) return;
                  const alloc = allWithEffective.filter(op => op._uid === String(m.id) && op._effDate === date).reduce((s, op) => s + (parseFloat(op.estimated_hours) || 0), 0);
                  if (alloc > cap + 0.01) overCapDays.push({ user: m.name, date, alloc, cap });
                });
              });
              const unassignedCount = allWithEffective.filter(op => op._uid === 'unassigned').length;
              const pendingCount = Object.keys(preReadPendingMoves).length;
              const canSubmitPlan = overCapDays.length === 0 && unassignedCount === 0 && pendingCount === 0;
              const submitReason = !canSubmitPlan
                ? (pendingCount > 0 ? `Save your ${pendingCount} draft change(s) first.`
                  : overCapDays.length > 0 ? `${overCapDays.length} day(s) over capacity — resolve before submitting.`
                  : unassignedCount > 0 ? `${unassignedCount} operation(s) still unassigned.`
                  : '')
                : '';
              return (
                <div style={{ marginTop: 20, padding: '16px 20px', borderRadius: 12, background: 'linear-gradient(135deg, rgba(37,99,235,0.04), rgba(139,92,246,0.04))', border: '1px solid rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                      {pendingCount > 0 ? `${pendingCount} unsaved change(s)` : canSubmitPlan ? 'Ready to submit' : 'Not ready to submit'}
                    </div>
                    <div style={{ fontSize: 11, color: canSubmitPlan ? 'var(--text-muted)' : '#b45309' }}>
                      {submitReason || 'Once submitted, dates are locked and the team will be notified.'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <motion.button whileTap={pendingCount > 0 ? { scale: 0.97 } : {}}
                      onClick={handleSaveDraft} disabled={pendingCount === 0 || preReadDraftSaving}
                      style={{ padding: '10px 18px', borderRadius: 8, border: '1px solid var(--border)', background: pendingCount > 0 ? '#fbbf24' : 'var(--bg-card)', color: pendingCount > 0 ? '#1f2937' : 'var(--text-muted)', cursor: pendingCount === 0 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 700 }}>
                      {preReadDraftSaving ? 'Saving…' : `Save as Draft${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
                    </motion.button>
                    <motion.button whileHover={canSubmitPlan ? { scale: 1.02 } : {}} whileTap={canSubmitPlan ? { scale: 0.97 } : {}}
                      onClick={handleSubmit} disabled={!canSubmitPlan || preReadSubmitting}
                      title={submitReason}
                      style={{ padding: '10px 24px', borderRadius: 8, border: 'none', background: canSubmitPlan ? '#2563eb' : '#94a3b8', color: '#fff', cursor: (!canSubmitPlan || preReadSubmitting) ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, opacity: (!canSubmitPlan || preReadSubmitting) ? 0.6 : 1, boxShadow: canSubmitPlan ? '0 2px 8px rgba(37,99,235,0.3)' : 'none' }}>
                      <FiLock size={14} /> {preReadSubmitting ? 'Submitting...' : 'Submit Pre-Read'}
                    </motion.button>
                  </div>
                </div>
              );
            })()}
          </div>
        );
        } catch (e) {
          console.error('Pre-read render error:', e);
          return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 15, marginBottom: 8 }}>Unable to load Pre-Read</div>
            <div style={{ fontSize: 12 }}>{e?.message || 'An error occurred'}</div>
            <button onClick={() => loadPreRead()} style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 }}>Retry</button>
          </div>;
        }
      })()}


    </div>
  );
}

